import { useEffect, useMemo, useState } from 'react';
import { getIdTokenResult, onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  documentId,
  getDoc,
  increment,
  limit as firestoreLimit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { ArrowDown, ArrowUp, Download, LogIn, LogOut, Mail, Pencil, Plus, Save, Trash2, Upload, X } from 'lucide-react';
import { readSheet } from 'read-excel-file/browser';
import { auth, cloudFunctions, db, googleProvider, storage } from './firebase';
import {
  getFriendlyAuthErrorMessage,
  isPasswordUser,
  sendPasswordReset,
  signInWithVerifiedEmailPassword,
} from './authHelpers';
import {
  defaultEmailTemplates,
  emailTemplatePlaceholders,
  emailTemplateSections,
  pickEmailTemplates,
} from './emailTemplates';
import {
  administrationContentSections,
  defaultSiteContent,
  pickSiteContent,
  siteContentFields,
} from './siteContent';
import {
  formatDateKeyInBudapest,
  formatDateTimeInBudapest,
  getBudapestDateAtTime,
  getBudapestTodayKey,
  getUpcomingBudapestSundays,
} from './dateUtils';
import {
  exerciseUnitOptions,
  formatExerciseTask,
  formatResultValue,
  getExerciseName,
  getPublicPlayerName,
  getResultSortValue,
  normalizeExercise,
  normalizeFitnessResult,
  normalizePersonalRecord,
  resultDirectionOptions,
  resultUnitOptions,
  sortRankingRows,
} from './resultsUtils';
import {
  calculateClubCourtCost,
  isFullFeeBillingMode,
  isTrainerOnlyBillingMode,
  resolveBillingSettlement,
} from './billingUtils';
import {
  getSquashSetLoserScore,
  getSquashSetPlayerName,
  isValidSquashSetLoserScore,
  normalizeSquashSet,
} from './squashSetUtils';
import SquashScoreEditor from './SquashScoreEditor';
import './App.css';

const formatTrainingDate = (dateKey) => {
  if (typeof dateKey !== 'string') {
    return 'Ismeretlen dátum';
  }

  const [year, month, day] = dateKey.split('-').map(Number);
  if (!year || !month || !day) {
    return dateKey;
  }

  return formatDateKeyInBudapest(dateKey, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long',
  });
};

const formatRegistrationTimestamp = (timestampValue) => {
  if (!timestampValue?.toDate) {
    return 'Nincs adat';
  }
  return formatDateTimeInBudapest(timestampValue.toDate(), {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getUpcomingSundays = () => getUpcomingBudapestSundays(5);
const DEFAULT_TRAINING_CAPACITY = 12;
const DEFAULT_TRAINING_START_TIME = '09:00';
const TRAINING_DURATION_MINUTES = 120;
const INITIAL_TRAININGS_PAGE_SIZE = 15;
const TRAININGS_PAGE_INCREMENT = 30;

const billingModeOptions = [
  { value: 'full', label: 'Teljes összeg' },
  { value: 'trainer_only', label: 'Csak edzői díj' },
  { value: 'balance_full', label: 'Egyenlegből teljes díj' },
  { value: 'balance_trainer_only', label: 'Egyenlegből edzői díj' },
  { value: 'none', label: 'Nem számlázandó' },
];

const paymentMethodOptions = [
  { value: 'bank_transfer', label: 'Átutalás' },
  { value: 'cash', label: 'Készpénz' },
];

const centeredAdminViews = [
  'monitor',
  'trainings',
  'tasks',
  'billing',
  'results',
  'content',
  'administration',
  'emailTemplates',
  'users',
];

const isAuthQuotaExceededError = (error) => {
  const code = String(error?.code || '');
  const message = String(error?.message || '');
  return code === 'auth/quota-exceeded' || message.includes('auth/quota-exceeded');
};

const balanceTransactionTypeOptions = [
  { value: 'admin_credit', label: 'Admin jóváírás' },
  { value: 'payment', label: 'Befizetés készpénzzel/utalással' },
  { value: 'manual_invoice', label: 'Manuálisan számlázva' },
  { value: 'admin_correction', label: 'Admin korrekció' },
];

const defaultBillingSettings = {
  courtFeeHuf: 4500,
  trainingDurationHours: 2,
  lateCancellationPercent: 70,
  categories: [
    { id: '2-3', minParticipants: 2, maxParticipants: 3, totalPriceHuf: 12000, courtCount: 1 },
    { id: '4-6', minParticipants: 4, maxParticipants: 6, totalPriceHuf: 11000, courtCount: 2 },
    { id: '7-9', minParticipants: 7, maxParticipants: 9, totalPriceHuf: 10000, courtCount: 3 },
    { id: '10-12', minParticipants: 10, maxParticipants: 12, totalPriceHuf: 9000, courtCount: 4 },
  ],
};

const getTrainingStartDateTime = (dateKey, timeValue = DEFAULT_TRAINING_START_TIME) => {
  const [hour = 9, minute = 0] = String(timeValue || DEFAULT_TRAINING_START_TIME).split(':').map(Number);
  return getBudapestDateAtTime(dateKey, hour || 0, minute || 0);
};

const getTrainingStartForWeek = (trainingDate, training = {}) => (
  training.trainingStartAt?.toDate?.()
  || getTrainingStartDateTime(trainingDate, training.startTime || DEFAULT_TRAINING_START_TIME)
);

const getTrainingEndForWeek = (trainingDate, training = {}) => {
  const trainingStart = getTrainingStartForWeek(trainingDate, training);
  return trainingStart
    ? new Date(trainingStart.getTime() + TRAINING_DURATION_MINUTES * 60 * 1000)
    : null;
};

const formatTrainingTime = (training) => {
  if (training?.startTime) {
    return training.startTime;
  }
  if (training?.trainingStartAt?.toDate) {
    return formatDateTimeInBudapest(training.trainingStartAt.toDate(), {
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  return DEFAULT_TRAINING_START_TIME;
};

const normalizeTrainingWeek = (weekDoc) => {
  const data = weekDoc.data();
  const key = typeof data.training_date === 'string' ? data.training_date : weekDoc.id;

  return {
    id: weekDoc.id,
    trainingDate: key,
    cancelledByAdmin: data.cancelled_by_admin === true,
    cancelledAt: data.cancelled_at,
    capacity: typeof data.capacity === 'number' && data.capacity > 0 ? data.capacity : DEFAULT_TRAINING_CAPACITY,
    count: typeof data.count === 'number' ? data.count : 0,
    waitlistCount: typeof data.waitlist_count === 'number' ? data.waitlist_count : 0,
    waitlistReservedCount: typeof data.waitlist_reserved_count === 'number' ? data.waitlist_reserved_count : 0,
    startTime: typeof data.start_time === 'string' ? data.start_time : DEFAULT_TRAINING_START_TIME,
    trainingStartAt: data.training_start_at,
    createdAt: data.createdAt,
  };
};

const getTrainingStatusLabel = (training, now = new Date()) => {
  if (training.cancelledByAdmin) {
    return 'Lemondva';
  }

  const trainingStart = training.trainingStartAt?.toDate?.()
    || getTrainingStartDateTime(training.trainingDate, training.startTime);

  if (trainingStart && trainingStart.getTime() <= now.getTime()) {
    return 'Megtartva';
  }

  return 'Aktív';
};

const getTrainingDeleteBlockedMessage = ({ regularCount = 0, lateCancelledCount = 0, waitlistCount = 0, loading = false } = {}) => {
  if (loading) {
    return 'A törlés előtt várd meg, amíg a jelentkezések és a várólista betöltődnek.';
  }

  const reasons = [];
  if (regularCount > 0) {
    reasons.push(`${regularCount} aktív jelentkező`);
  }
  if (lateCancelledCount > 0) {
    reasons.push(`${lateCancelledCount} késői lemondó`);
  }
  if (waitlistCount > 0) {
    reasons.push(`${waitlistCount} várólistás`);
  }

  return reasons.length > 0
    ? `Ez az edzés még nem törölhető, mert van rajta ${reasons.join(', ')}.`
    : '';
};

const getLateCancellationCutoff = () => {
  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - 4);
  return cutoff;
};

const getTextField = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
};

const formatNickname = (value) =>
  String(value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .split(/([\s-]+)/u)
    .map((part) => {
      if (!part || /^[\s-]+$/u.test(part)) {
        return part;
      }
      const letters = Array.from(part.toLocaleLowerCase('hu-HU'));
      if (letters.length === 0) {
        return '';
      }
      return `${letters[0].toLocaleUpperCase('hu-HU')}${letters.slice(1).join('')}`;
    })
    .join('');

const getNicknameKey = (value) =>
  formatNickname(value)
    .toLocaleLowerCase('hu-HU')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const getUserAddress = (userData = {}) => {
  const nestedAddress = userData.address && typeof userData.address === 'object' ? userData.address : {};

  const country = getTextField(
    userData.billing_country,
    userData.country,
    userData.address_country,
    nestedAddress.country,
  );
  const zip = getTextField(
    userData.billing_zip,
    userData.postal_code,
    userData.zip,
    userData.zipcode,
    userData.address_zip,
    nestedAddress.zip,
    nestedAddress.postal_code,
  );
  const city = getTextField(
    userData.billing_city,
    userData.city,
    userData.address_city,
    nestedAddress.city,
  );
  const street = getTextField(
    userData.billing_address,
    userData.street,
    userData.address_line1,
    userData.address_street,
    nestedAddress.street,
    nestedAddress.line1,
  );

  return { country, zip, city, street };
};

const getUserBillingName = (userData = {}) =>
  getTextField(userData.billing_name, userData.billingName, userData.invoice_name);

const hasCompleteBillingData = (data = {}) =>
  Boolean(data.name && data.country && data.zip && data.city && data.street);

const formatCurrency = (value) =>
  `${Math.round(Number(value) || 0).toLocaleString('hu-HU')} Ft`;

const normalizeBillingMode = (value) =>
  billingModeOptions.some((option) => option.value === value) ? value : 'full';

const normalizePaymentMethod = (value) =>
  paymentMethodOptions.some((option) => option.value === value) ? value : 'bank_transfer';

const normalizeBillingSettings = (data = {}) => {
  const remoteCategories = Array.isArray(data.categories) ? data.categories : [];
  const categories = defaultBillingSettings.categories.map((fallbackCategory, index) => {
    const remoteCategory = remoteCategories[index] && typeof remoteCategories[index] === 'object'
      ? remoteCategories[index]
      : {};
    const minParticipants = Number.parseInt(remoteCategory.minParticipants, 10);
    const maxParticipants = Number.parseInt(remoteCategory.maxParticipants, 10);
    const totalPriceHuf = Number.parseInt(remoteCategory.totalPriceHuf, 10);
    const courtCount = Number.parseFloat(remoteCategory.courtCount);

    return {
      ...fallbackCategory,
      ...remoteCategory,
      id: fallbackCategory.id,
      minParticipants: Number.isInteger(minParticipants) && minParticipants > 0
        ? minParticipants
        : fallbackCategory.minParticipants,
      maxParticipants: Number.isInteger(maxParticipants) && maxParticipants > 0
        ? maxParticipants
        : fallbackCategory.maxParticipants,
      totalPriceHuf: Number.isInteger(totalPriceHuf) && totalPriceHuf >= 0
        ? totalPriceHuf
        : fallbackCategory.totalPriceHuf,
      courtCount: Number.isFinite(courtCount) && courtCount > 0
        ? courtCount
        : fallbackCategory.courtCount,
    };
  });
  const courtFeeHuf = Number.parseInt(data.courtFeeHuf, 10);
  const trainingDurationHours = Number.parseFloat(data.trainingDurationHours);
  const lateCancellationPercent = Number.parseFloat(data.lateCancellationPercent);

  return {
    courtFeeHuf: Number.isInteger(courtFeeHuf) && courtFeeHuf >= 0
      ? courtFeeHuf
      : defaultBillingSettings.courtFeeHuf,
    trainingDurationHours: Number.isFinite(trainingDurationHours) && trainingDurationHours > 0
      ? trainingDurationHours
      : defaultBillingSettings.trainingDurationHours,
    lateCancellationPercent: Number.isFinite(lateCancellationPercent) && lateCancellationPercent >= 0
      ? lateCancellationPercent
      : defaultBillingSettings.lateCancellationPercent,
    categories,
  };
};

const getBillingCategoryForCount = (settings, participantCount) => {
  const categories = [...(settings.categories || defaultBillingSettings.categories)]
    .sort((left, right) => left.minParticipants - right.minParticipants);
  return categories.find((category) =>
    participantCount >= category.minParticipants && participantCount <= category.maxParticipants
  ) || categories[categories.length - 1] || defaultBillingSettings.categories[defaultBillingSettings.categories.length - 1];
};

const calculateBillingPrice = (settings, participantCount, billingMode, lateCancelled) => {
  if (participantCount <= 0 || billingMode === 'none') {
    return {
      amountHuf: 0,
      baseAmountHuf: 0,
      category: null,
      courtFeePerPersonHuf: 0,
      trainerFeePerPersonHuf: 0,
    };
  }

  const category = getBillingCategoryForCount(settings, participantCount);
  const courtFeePerPersonHuf =
    (Number(settings.courtFeeHuf || 0) * Number(settings.trainingDurationHours || 0) * Number(category.courtCount || 0))
    / participantCount;
  const trainerFeePerPersonHuf = Math.max(Number(category.totalPriceHuf || 0) - courtFeePerPersonHuf, 0);
  const baseAmountHuf = isTrainerOnlyBillingMode(billingMode)
    ? trainerFeePerPersonHuf
    : Number(category.totalPriceHuf || 0);
  const multiplier = lateCancelled ? Number(settings.lateCancellationPercent || 0) / 100 : 1;

  return {
    amountHuf: Math.ceil(baseAmountHuf * multiplier),
    baseAmountHuf,
    category,
    courtFeePerPersonHuf,
    trainerFeePerPersonHuf,
  };
};

const hasBillingPartnerData = (userItem = {}) =>
  Boolean(
    getTextField(userItem.email)
    && getTextField(userItem.billingName)
    && getTextField(userItem.country)
    && getTextField(userItem.zip)
    && getTextField(userItem.city)
    && getTextField(userItem.street),
  );

const backgroundImageFields = [
  {
    key: 'aboutProfileImageUrl',
    title: 'Rólam portrékép',
    helper: 'Félalakos, bizalomépítő portré a "Rólam" szekcióhoz.',
    previewClassName: 'admin-background-preview admin-portrait-preview',
    emptyText: 'Nincs beállított portrékép.',
    clearLabel: 'Kép törlése',
  },
  {
    key: 'aboutBackgroundUrl',
    title: 'Rólam háttere',
    helper: 'A "Rólam" szekció háttérképe.',
  },
  {
    key: 'trainingBackgroundUrl',
    title: 'Csapatedzés háttere',
    helper: 'A "Csapatedzés" szekció háttérképe.',
  },
];

const sanitizeFilename = (name) => {
  const normalized = (name || '').toLowerCase().trim();
  if (!normalized) {
    return 'image';
  }
  return normalized.replace(/[^a-z0-9._-]+/g, '-').replace(/-+/g, '-');
};

const getImageUrls = (...values) => {
  const urls = values.flatMap((value) => (Array.isArray(value) ? value : [value]));
  return [...new Set(urls.map((url) => (typeof url === 'string' ? url.trim() : '')).filter(Boolean))];
};

const normalizeImagePosition = (value) => {
  if (typeof value !== 'string') {
    return '50% 50%';
  }

  const [rawX = '50%', rawY = '50%'] = value.trim().split(/\s+/);
  const toPercent = (part) => {
    const number = Number.parseInt(String(part).replace('%', ''), 10);
    if (!Number.isFinite(number)) {
      return 50;
    }
    return Math.min(Math.max(number, 0), 100);
  };

  return `${toPercent(rawX)}% ${toPercent(rawY)}%`;
};

const getPositionAxisValue = (position, axis) => {
  const normalized = normalizeImagePosition(position);
  const [x, y] = normalized.split(' ');
  return Number.parseInt((axis === 'x' ? x : y).replace('%', ''), 10);
};

const updateImagePositionAxis = (position, axis, nextValue) => {
  const normalized = normalizeImagePosition(position);
  const [x, y] = normalized.split(' ');
  const value = `${Math.min(Math.max(Number(nextValue) || 0, 0), 100)}%`;
  return axis === 'x' ? `${value} ${y}` : `${x} ${value}`;
};

const normalizeImportHeader = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const getTaskKey = (value) => {
  const normalizedName = normalizeImportHeader(value);
  return normalizedName || encodeURIComponent(String(value || '').trim().toLocaleLowerCase('hu-HU')).slice(0, 500);
};

const exerciseImportHeaderAliases = {
  nev: 'name',
  name: 'name',
  gyakorlat: 'name',
  leiras: 'description',
  description: 'description',
  ismetlesszam: 'repetitionCount',
  ismetles_szam: 'repetitionCount',
  ism_szam: 'repetitionCount',
  mennyiseg: 'repetitionCount',
  quantity: 'repetitionCount',
  repetition_count: 'repetitionCount',
  repetitions: 'repetitionCount',
  mertekegyseg: 'unit',
  mertek_egyseg: 'unit',
  unit: 'unit',
  idotartam_perc: 'durationMinutes',
  duration_minutes: 'durationMinutes',
  perc: 'durationMinutes',
  idotartam_mp: 'durationSeconds',
  idotartam_masodperc: 'durationSeconds',
  duration_seconds: 'durationSeconds',
  mp: 'durationSeconds',
  eredmeny_mertekegyseg: 'resultUnit',
  eredmeny_mertekegysege: 'resultUnit',
  result_unit: 'resultUnit',
  eredmeny_unit: 'resultUnit',
  rangsorolas: 'resultDirection',
  rangsor: 'resultDirection',
  result_direction: 'resultDirection',
};

const taskImportHeaderAliases = {
  nev: 'name',
  name: 'name',
  feladat: 'name',
  kategoria: 'category',
  category: 'category',
  mennyiseg: 'quantity',
  quantity: 'quantity',
  mertekegyseg: 'unit',
  mertek_egyseg: 'unit',
  unit: 'unit',
  leiras: 'description',
  description: 'description',
};

const parseDelimitedRows = (text) => {
  const normalizedText = String(text || '').replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const firstLine = normalizedText.split('\n').find((line) => line.trim()) || '';
  const delimiter = firstLine.includes('\t') ? '\t' : firstLine.includes(';') ? ';' : ',';
  const rows = [];
  let row = [];
  let cell = '';
  let inQuotes = false;

  for (let index = 0; index < normalizedText.length; index += 1) {
    const char = normalizedText[index];
    const nextChar = normalizedText[index + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        cell += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (!inQuotes && char === delimiter) {
      row.push(cell.trim());
      cell = '';
      continue;
    }

    if (!inQuotes && char === '\n') {
      row.push(cell.trim());
      if (row.some(Boolean)) {
        rows.push(row);
      }
      row = [];
      cell = '';
      continue;
    }

    cell += char;
  }

  row.push(cell.trim());
  if (row.some(Boolean)) {
    rows.push(row);
  }

  return rows;
};

const parseImportedResultDirection = (value) => {
  const normalizedValue = normalizeImportHeader(value);
  if (['lower', 'kisebb', 'kisebb_a_jobb', 'min', 'minimum', 'ido', 'idore', 'gyorsabb'].includes(normalizedValue)) {
    return 'lower';
  }
  return 'higher';
};

const parseExerciseImportFile = (text) => {
  const rows = parseDelimitedRows(text);
  if (rows.length < 2) {
    return { exercises: [], errors: ['A fájlban legyen fejléc és legalább egy gyakorlat sor.'] };
  }

  const headerKeys = rows[0].map((header) => exerciseImportHeaderAliases[normalizeImportHeader(header)] || '');
  const errors = [];
  const exercisesToImport = [];

  rows.slice(1).forEach((row, index) => {
    const rowNumber = index + 2;
    const item = {};
    row.forEach((value, cellIndex) => {
      const key = headerKeys[cellIndex];
      if (key) {
        item[key] = value;
      }
    });

    const name = getTextField(item.name);
    const repetitionCount = item.repetitionCount === undefined || item.repetitionCount === ''
      ? 0
      : Number.parseFloat(String(item.repetitionCount).replace(',', '.'));
    const durationMinutes = Number.parseInt(item.durationMinutes || '0', 10);
    const durationSeconds = Number.parseInt(item.durationSeconds || '0', 10);

    if (!name) {
      errors.push(`${rowNumber}. sor: a név kötelező.`);
      return;
    }
    if (!Number.isFinite(repetitionCount) || repetitionCount < 0) {
      errors.push(`${rowNumber}. sor: az ismétlésszám legyen 0 vagy nagyobb szám.`);
      return;
    }
    if (!Number.isInteger(durationMinutes) || durationMinutes < 0) {
      errors.push(`${rowNumber}. sor: az időtartam perc legyen 0 vagy nagyobb egész szám.`);
      return;
    }
    if (!Number.isInteger(durationSeconds) || durationSeconds < 0 || durationSeconds > 59) {
      errors.push(`${rowNumber}. sor: az időtartam mp legyen 0 és 59 közötti egész szám.`);
      return;
    }

    exercisesToImport.push({
      name,
      description: getTextField(item.description),
      repetitionCount,
      unit: getTextField(item.unit) || 'mp',
      durationMinutes,
      durationSeconds,
      resultUnit: getTextField(item.resultUnit) || 'db',
      resultDirection: parseImportedResultDirection(item.resultDirection),
    });
  });

  return { exercises: exercisesToImport, errors };
};

const parseTaskImportRows = (rawRows) => {
  const rows = rawRows.filter((row) => Array.isArray(row) && row.some((value) => value !== null && value !== undefined && value !== ''));
  if (rows.length < 2) {
    return { tasks: [], errors: ['A fájlban legyen fejléc és legalább egy feladat sor.'] };
  }

  const headerKeys = rows[0].map((header) => taskImportHeaderAliases[normalizeImportHeader(header)] || '');
  const errors = [];
  const tasksToImport = [];

  rows.slice(1).forEach((row, index) => {
    const rowNumber = index + 2;
    const item = {};
    row.forEach((value, cellIndex) => {
      const key = headerKeys[cellIndex];
      if (key) {
        item[key] = typeof value === 'string' ? value.trim() : value;
      }
    });

    const name = getTextField(item.name);
    const category = getTextField(item.category);
    const quantity = Number.parseFloat(String(item.quantity || '').replace(',', '.'));
    const unit = getTextField(item.unit);

    if (!name) {
      errors.push(`${rowNumber}. sor: a név kötelező.`);
      return;
    }
    if (!category) {
      errors.push(`${rowNumber}. sor: a kategória kötelező.`);
      return;
    }
    if (!Number.isFinite(quantity) || quantity <= 0) {
      errors.push(`${rowNumber}. sor: a mennyiség legyen nullánál nagyobb szám.`);
      return;
    }
    if (!unit) {
      errors.push(`${rowNumber}. sor: a mértékegység kötelező.`);
      return;
    }

    tasksToImport.push({
      name,
      category,
      quantity,
      unit,
      description: getTextField(item.description),
    });
  });

  return { tasks: tasksToImport, errors };
};

const parseTaskImportFile = (text) => parseTaskImportRows(parseDelimitedRows(text));

const normalizeTask = (taskDoc) => {
  const data = taskDoc.data() || {};
  return {
    id: taskDoc.id,
    name: getTextField(data.name),
    category: getTextField(data.category),
    quantity: Number(data.quantity) || 0,
    unit: getTextField(data.unit),
    description: getTextField(data.description),
    taskKey: getTextField(data.task_key) || getTaskKey(data.name),
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
};

const normalizeTrainingTaskAssignment = (assignmentDoc) => {
  const data = assignmentDoc.data() || {};
  return {
    id: assignmentDoc.id,
    taskId: getTextField(data.task_id),
    taskName: getTextField(data.task_name),
    taskCategory: getTextField(data.task_category),
    quantity: Number(data.quantity) || 0,
    unit: getTextField(data.unit),
    description: getTextField(data.description),
    position: Number.isFinite(data.position) ? data.position : Number.MAX_SAFE_INTEGER,
    createdAt: data.createdAt,
  };
};

function AdminPage() {
  const [activeView, setActiveView] = useState('monitor');
  const [registrationPeriod, setRegistrationPeriod] = useState('future');
  const upcomingTrainings = useMemo(getUpcomingSundays, []);
  const [currentUser, setCurrentUser] = useState(null);
  const [hasOwnerAccess, setHasOwnerAccess] = useState(false);
  const [claimsLoading, setClaimsLoading] = useState(true);
  const [authBusy, setAuthBusy] = useState(false);
  const [authNotice, setAuthNotice] = useState('');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');

  const [registrations, setRegistrations] = useState([]);
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [registrationsLoading, setRegistrationsLoading] = useState(false);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [trainingWeeks, setTrainingWeeks] = useState({});
  const [trainingCancelDate, setTrainingCancelDate] = useState('');
  const [trainingCancelBusy, setTrainingCancelBusy] = useState('');
  const [trainingCancelNotice, setTrainingCancelNotice] = useState('');
  const [trainingRestoreBusy, setTrainingRestoreBusy] = useState('');
  const [trainingDeleteDate, setTrainingDeleteDate] = useState('');
  const [trainingDeleteBusy, setTrainingDeleteBusy] = useState('');
  const [registrationRemoveBusyId, setRegistrationRemoveBusyId] = useState('');
  const [registrationAddUserDrafts, setRegistrationAddUserDrafts] = useState({});
  const [registrationAddBusyDate, setRegistrationAddBusyDate] = useState('');
  const [registrationActionNotice, setRegistrationActionNotice] = useState('');
  const [capacityDrafts, setCapacityDrafts] = useState({});
  const [capacityBusyDate, setCapacityBusyDate] = useState('');
  const [trainingCreateOpen, setTrainingCreateOpen] = useState(false);
  const [trainingCreateBusy, setTrainingCreateBusy] = useState(false);
  const [trainingCreateNotice, setTrainingCreateNotice] = useState('');
  const [trainingCreateForm, setTrainingCreateForm] = useState({
    date: '',
    time: DEFAULT_TRAINING_START_TIME,
    capacity: String(DEFAULT_TRAINING_CAPACITY),
  });
  const [trainingListRows, setTrainingListRows] = useState([]);
  const [trainingListLimit, setTrainingListLimit] = useState(INITIAL_TRAININGS_PAGE_SIZE);
  const [trainingListLoading, setTrainingListLoading] = useState(false);
  const [trainingListNotice, setTrainingListNotice] = useState('');
  const [deletedTrainingDates, setDeletedTrainingDates] = useState([]);
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userActionNotice, setUserActionNotice] = useState('');
  const [userBlockBusyId, setUserBlockBusyId] = useState('');
  const [userRoleBusyId, setUserRoleBusyId] = useState('');
  const [userDeleteBusyId, setUserDeleteBusyId] = useState('');
  const [userDeleteConfirm, setUserDeleteConfirm] = useState(null);
  const [userUidPreview, setUserUidPreview] = useState(null);
  const [userBillingEdit, setUserBillingEdit] = useState(null);
  const [userBillingForm, setUserBillingForm] = useState({
    name: '',
    country: 'Magyarország',
    zip: '',
    city: '',
    street: '',
  });
  const [userBillingSaveBusy, setUserBillingSaveBusy] = useState(false);
  const [userBillingSaveNotice, setUserBillingSaveNotice] = useState('');
  const [lateCancellationCounts, setLateCancellationCounts] = useState({});
  const [billingSettings, setBillingSettings] = useState(defaultBillingSettings);
  const [billingSettingsDraft, setBillingSettingsDraft] = useState(defaultBillingSettings);
  const [billingSettingsSaveBusy, setBillingSettingsSaveBusy] = useState(false);
  const [billingNotice, setBillingNotice] = useState('');
  const [billingUserBusyId, setBillingUserBusyId] = useState('');
  const [billingRuns, setBillingRuns] = useState({});
  const [billingItems, setBillingItems] = useState([]);
  const [billingLedgerEntries, setBillingLedgerEntries] = useState([]);
  const [billingStartBusyDate, setBillingStartBusyDate] = useState('');
  const [paymentMethodBusyId, setPaymentMethodBusyId] = useState('');
  const [balanceForm, setBalanceForm] = useState({
    userId: '',
    type: 'admin_credit',
    amount: '',
    note: '',
  });
  const [balanceSaveBusy, setBalanceSaveBusy] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [taskForm, setTaskForm] = useState({
    name: '',
    category: '',
    quantity: '',
    unit: 'db',
    description: '',
  });
  const [taskNotice, setTaskNotice] = useState('');
  const [taskSaveBusy, setTaskSaveBusy] = useState(false);
  const [taskImportFile, setTaskImportFile] = useState(null);
  const [taskImportBusy, setTaskImportBusy] = useState(false);
  const [taskImportNotice, setTaskImportNotice] = useState('');
  const [taskImportErrors, setTaskImportErrors] = useState([]);
  const [selectedTaskTrainingDate, setSelectedTaskTrainingDate] = useState('');
  const [selectedTrainingTaskId, setSelectedTrainingTaskId] = useState('');
  const [trainingTaskAssignments, setTrainingTaskAssignments] = useState([]);
  const [trainingTaskAssignmentsLoading, setTrainingTaskAssignmentsLoading] = useState(false);
  const [trainingTaskAssignmentNotice, setTrainingTaskAssignmentNotice] = useState('');
  const [trainingTaskAssignmentBusyId, setTrainingTaskAssignmentBusyId] = useState('');
  const [exercises, setExercises] = useState([]);
  const [exercisesLoading, setExercisesLoading] = useState(false);
  const [exerciseForm, setExerciseForm] = useState({
    name: '',
    description: '',
    repetitionCount: '',
    unit: 'mp',
    durationMinutes: '0',
    durationSeconds: '0',
    resultUnit: 'db',
    resultDirection: 'higher',
  });
  const [exerciseNotice, setExerciseNotice] = useState('');
  const [exerciseSaveBusy, setExerciseSaveBusy] = useState(false);
  const [exerciseImportFile, setExerciseImportFile] = useState(null);
  const [exerciseImportBusy, setExerciseImportBusy] = useState(false);
  const [exerciseImportNotice, setExerciseImportNotice] = useState('');
  const [exerciseImportErrors, setExerciseImportErrors] = useState([]);
  const [fitnessResults, setFitnessResults] = useState([]);
  const [fitnessResultsLoading, setFitnessResultsLoading] = useState(false);
  const [adminResultsMode, setAdminResultsMode] = useState('exercises');
  const [adminSquashSets, setAdminSquashSets] = useState([]);
  const [adminSquashSetsLoading, setAdminSquashSetsLoading] = useState(false);
  const [adminSquashSetDrafts, setAdminSquashSetDrafts] = useState({});
  const [adminSquashSetBusyId, setAdminSquashSetBusyId] = useState('');
  const [adminSquashSetNotice, setAdminSquashSetNotice] = useState('');
  const [fitnessResultForm, setFitnessResultForm] = useState({
    userId: '',
    exerciseId: '',
    trainingDate: getBudapestTodayKey(),
    value: '',
  });
  const [fitnessResultNotice, setFitnessResultNotice] = useState('');
  const [fitnessResultSaveBusy, setFitnessResultSaveBusy] = useState(false);
  const [resultDrafts, setResultDrafts] = useState({});
  const [resultUpdateBusyId, setResultUpdateBusyId] = useState('');
  const [selectedResultExerciseId, setSelectedResultExerciseId] = useState('');
  const [selectedResultUserId, setSelectedResultUserId] = useState('');
  const [exerciseRecords, setExerciseRecords] = useState([]);

  const [siteContentForm, setSiteContentForm] = useState(defaultSiteContent);
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveNotice, setSaveNotice] = useState('');
  const [emailTemplateForm, setEmailTemplateForm] = useState(defaultEmailTemplates);
  const [emailTemplateSaveBusy, setEmailTemplateSaveBusy] = useState(false);
  const [emailTemplateNotice, setEmailTemplateNotice] = useState('');
  const [backgroundFiles, setBackgroundFiles] = useState({});
  const [uploadBusyKey, setUploadBusyKey] = useState('');
  const [uploadNotice, setUploadNotice] = useState('');
  const heroSlideshowUrls = useMemo(
    () => getImageUrls(siteContentForm.heroBackgroundUrls, siteContentForm.heroBackgroundUrl),
    [siteContentForm.heroBackgroundUrls, siteContentForm.heroBackgroundUrl],
  );

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && isPasswordUser(user) && !user.emailVerified) {
        setCurrentUser(null);
        setAuthNotice('A belépéshez előbb erősítsd meg az e-mail címed.');
        void signOut(auth);
        return;
      }

      setCurrentUser(user);
      setAuthNotice('');
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const checkClaims = async () => {
      if (!currentUser) {
        setHasOwnerAccess(false);
        setClaimsLoading(false);
        return;
      }

      setClaimsLoading(true);
      try {
        const tokenResult = await getIdTokenResult(currentUser, false);
        if (!cancelled) {
          setHasOwnerAccess(tokenResult.claims.admin === true);
        }
      } catch (error) {
        if (!cancelled) {
          setHasOwnerAccess(false);
          setAuthNotice(
            isAuthQuotaExceededError(error)
              ? 'Jogosultság ellenőrzési hiba: a Firebase Auth kvóta átmenetileg betelt. Próbáld újra később.'
              : `Jogosultság ellenőrzési hiba: ${error?.message || 'ismeretlen hiba'}`,
          );
        }
      } finally {
        if (!cancelled) {
          setClaimsLoading(false);
        }
      }
    };

    checkClaims();
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  useEffect(() => {
    const contentRef = doc(db, 'site_content', 'main');
    const unsubscribe = onSnapshot(
      contentRef,
      (snapshot) => {
        const remoteContent = snapshot.exists() ? snapshot.data() : {};
        setSiteContentForm({
          ...defaultSiteContent,
          ...pickSiteContent(remoteContent),
        });
      },
      (error) => {
        setSaveNotice(`Szövegek betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
      },
    );
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!currentUser || !hasOwnerAccess) {
      setEmailTemplateForm(defaultEmailTemplates);
      return undefined;
    }

    const templatesRef = doc(db, 'email_templates', 'main');
    const unsubscribe = onSnapshot(
      templatesRef,
      (snapshot) => {
        const remoteTemplates = snapshot.exists() ? snapshot.data() : {};
        setEmailTemplateForm({
          ...defaultEmailTemplates,
          ...pickEmailTemplates(remoteTemplates),
        });
      },
      (error) => {
        setEmailTemplateNotice(`E-mail sablonok betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
      },
    );

    return () => unsubscribe();
  }, [currentUser, hasOwnerAccess]);

  useEffect(() => {
    if (!currentUser || !hasOwnerAccess) {
      setRegistrations([]);
      setWaitlistEntries([]);
      setRegistrationsLoading(false);
      setWaitlistLoading(false);
      return undefined;
    }

    setRegistrationsLoading(true);
    setWaitlistLoading(true);

    const registrationsRef = collection(db, 'training_registrations');
    const waitlistRef = collection(db, 'training_waitlist');
    const unsubscribeRegistrations = onSnapshot(
      registrationsRef,
      (snapshot) => {
        const items = [];
        snapshot.forEach((registrationDoc) => {
          const data = registrationDoc.data();
          items.push({
            id: registrationDoc.id,
            trainingDate: typeof data.training_date === 'string' ? data.training_date : '',
            createdAt: data.createdAt,
            userId: typeof data.user_id === 'string' ? data.user_id : '',
            userEmail: typeof data.user_email === 'string' ? data.user_email : '',
            userDisplayName: typeof data.user_display_name === 'string' ? data.user_display_name : '',
            lateCancelled: data.late_cancelled === true,
            billingPaymentMethod: normalizePaymentMethod(data.billing_payment_method),
            waitlisted: false,
          });
        });

        setRegistrations(items);
        setRegistrationsLoading(false);
      },
      (error) => {
        setAuthNotice(`Jelentkezések betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
        setRegistrationsLoading(false);
      },
    );
    const unsubscribeWaitlist = onSnapshot(
      waitlistRef,
      (snapshot) => {
        const items = [];
        snapshot.forEach((waitlistDoc) => {
          const data = waitlistDoc.data();
          items.push({
            id: waitlistDoc.id,
            trainingDate: typeof data.training_date === 'string' ? data.training_date : '',
            createdAt: data.createdAt,
            userId: typeof data.user_id === 'string' ? data.user_id : '',
            userEmail: typeof data.user_email === 'string' ? data.user_email : '',
            userDisplayName: typeof data.user_display_name === 'string' ? data.user_display_name : '',
            lateCancelled: false,
            billingPaymentMethod: 'bank_transfer',
            waitlisted: true,
          });
        });

        setWaitlistEntries(items);
        setWaitlistLoading(false);
      },
      (error) => {
        setAuthNotice(`Várólista betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
        setWaitlistLoading(false);
      },
    );

    return () => {
      unsubscribeRegistrations();
      unsubscribeWaitlist();
    };
  }, [currentUser, hasOwnerAccess]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!currentUser || !hasOwnerAccess) {
      setDeletedTrainingDates([]);
      return undefined;
    }

    const unsubscribe = onSnapshot(
      collection(db, 'deleted_training_weeks'),
      (snapshot) => {
        const dates = [];
        snapshot.forEach((deletedDoc) => {
          dates.push(deletedDoc.id);
        });
        setDeletedTrainingDates(dates);
      },
      (error) => {
        setAuthNotice(`Törölt edzések betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
        setDeletedTrainingDates([]);
      },
    );

    return () => unsubscribe();
  }, [currentUser, hasOwnerAccess]);

  useEffect(() => {
    if (!currentUser || !hasOwnerAccess) {
      setTrainingWeeks({});
      return undefined;
    }

    const unsubscribe = onSnapshot(
      collection(db, 'training_weeks'),
      (snapshot) => {
        const weeks = {};
        snapshot.forEach((weekDoc) => {
          const week = normalizeTrainingWeek(weekDoc);
          weeks[week.trainingDate] = week;
        });
        setTrainingWeeks(weeks);
      },
      (error) => {
        setAuthNotice(`Edzés állapotok betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
        setTrainingWeeks({});
      },
    );

    return () => unsubscribe();
  }, [currentUser, hasOwnerAccess]);

  useEffect(() => {
    if (!currentUser || !hasOwnerAccess) {
      setTrainingListRows([]);
      setTrainingListLoading(false);
      setTrainingListNotice('');
      return undefined;
    }

    setTrainingListLoading(true);
    setTrainingListNotice('');

    const trainingsQuery = query(
      collection(db, 'training_weeks'),
      orderBy(documentId(), 'desc'),
      firestoreLimit(trainingListLimit),
    );

    const unsubscribe = onSnapshot(
      trainingsQuery,
      (snapshot) => {
        const rows = [];
        snapshot.forEach((weekDoc) => {
          rows.push(normalizeTrainingWeek(weekDoc));
        });
        setTrainingListRows(rows);
        setTrainingListLoading(false);
      },
      (error) => {
        setTrainingListNotice(`Edzések betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
        setTrainingListRows([]);
        setTrainingListLoading(false);
      },
    );

    return () => unsubscribe();
  }, [currentUser, hasOwnerAccess, trainingListLimit]);

  useEffect(() => {
    if (!currentUser || !hasOwnerAccess) {
      setLateCancellationCounts({});
      return undefined;
    }

    const lateCancellationsQuery = query(
      collection(db, 'late_cancellations'),
      where('cancelled_at', '>=', Timestamp.fromDate(getLateCancellationCutoff())),
    );

    const unsubscribe = onSnapshot(
      lateCancellationsQuery,
      (snapshot) => {
        const counts = {};
        snapshot.forEach((cancellationDoc) => {
          const data = cancellationDoc.data();
          const userId = getTextField(data.user_id);
          if (!userId) {
            return;
          }
          counts[userId] = (counts[userId] || 0) + 1;
        });

        setLateCancellationCounts(counts);
      },
      (error) => {
        setAuthNotice(`Késői lemondások betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
        setLateCancellationCounts({});
      },
    );

    return () => unsubscribe();
  }, [currentUser, hasOwnerAccess]);

  useEffect(() => {
    if (!currentUser || !hasOwnerAccess) {
      setUsers([]);
      setUsersLoading(false);
      return undefined;
    }

    setUsersLoading(true);
    const usersRef = collection(db, 'users');
    const unsubscribe = onSnapshot(
      usersRef,
      (snapshot) => {
        const items = [];
        snapshot.forEach((userDoc) => {
          const data = userDoc.data();
          const address = getUserAddress(data);

          items.push({
            id: userDoc.id,
            uid: getTextField(data.uid, userDoc.id),
            displayName: getTextField(data.displayName, data.full_name, data.name),
            billingName: getUserBillingName(data),
            nickname: formatNickname(getTextField(data.nickname, data.nickName, data.becenev)),
            email: getTextField(data.email),
            role: getTextField(data.role).toLowerCase() === 'admin' || (userDoc.id === currentUser.uid && hasOwnerAccess)
              ? 'admin'
              : 'user',
            trainingRegistrationDisabledByAdmin:
              data.training_registration_disabled_by_admin === true
              || data.trainingRegistrationDisabledByAdmin === true,
            billingMode: normalizeBillingMode(data.billing_mode),
            billingBalanceHuf: typeof data.billing_balance_huf === 'number' ? data.billing_balance_huf : 0,
            billingAllowNegativeBalance: data.billing_allow_negative_balance === true,
            ...address,
          });
        });

        items.sort((left, right) => {
          const leftName = left.displayName || left.email || left.uid;
          const rightName = right.displayName || right.email || right.uid;
          return leftName.localeCompare(rightName, 'hu');
        });

        setUsers(items);
        setUsersLoading(false);
      },
      (error) => {
        setAuthNotice(`Felhasználók betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
        setUsersLoading(false);
      },
    );

    return () => unsubscribe();
  }, [currentUser, hasOwnerAccess]);

  useEffect(() => {
    if (!currentUser || !hasOwnerAccess) {
      setBillingSettings(defaultBillingSettings);
      setBillingSettingsDraft(defaultBillingSettings);
      setBillingRuns({});
      setBillingItems([]);
      setBillingLedgerEntries([]);
      return undefined;
    }

    const settingsRef = doc(db, 'billing_settings', 'main');
    const runsQuery = query(collection(db, 'billing_runs'), orderBy('training_date', 'desc'), firestoreLimit(80));
    const itemsQuery = query(collection(db, 'billing_invoice_items'), orderBy('createdAt', 'desc'), firestoreLimit(200));
    const ledgerQuery = query(collection(db, 'billing_balance_ledger'), orderBy('createdAt', 'desc'), firestoreLimit(120));

    const unsubscribeSettings = onSnapshot(
      settingsRef,
      (snapshot) => {
        const normalizedSettings = normalizeBillingSettings(snapshot.exists() ? snapshot.data() : {});
        setBillingSettings(normalizedSettings);
        setBillingSettingsDraft(normalizedSettings);
      },
      (error) => {
        setBillingNotice(`Számlázási beállítások betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
      },
    );

    const unsubscribeRuns = onSnapshot(
      runsQuery,
      (snapshot) => {
        const runs = {};
        snapshot.forEach((runDoc) => {
          const data = runDoc.data();
          const trainingDate = getTextField(data.training_date, runDoc.id);
          runs[trainingDate] = {
            id: runDoc.id,
            trainingDate,
            createdAt: data.createdAt,
            createdByEmail: getTextField(data.created_by_user_email),
            participantCount: typeof data.participant_count === 'number' ? data.participant_count : 0,
            invoiceCount: typeof data.invoice_count === 'number' ? data.invoice_count : 0,
            balanceCoveredCount: typeof data.balance_covered_count === 'number' ? data.balance_covered_count : 0,
            clubCourtFeePerPersonHuf: typeof data.club_court_fee_per_person_huf === 'number'
              ? data.club_court_fee_per_person_huf
              : null,
            clubCourtFeeParticipantCount: typeof data.club_court_fee_participant_count === 'number'
              ? data.club_court_fee_participant_count
              : null,
            clubCourtCostHuf: typeof data.club_court_cost_huf === 'number' ? data.club_court_cost_huf : null,
            skippedCount: typeof data.skipped_count === 'number' ? data.skipped_count : 0,
            failedCount: typeof data.failed_count === 'number' ? data.failed_count : 0,
            billingoSentCount: typeof data.billingo_sent_count === 'number' ? data.billingo_sent_count : 0,
            billingoFailedCount: typeof data.billingo_failed_count === 'number' ? data.billingo_failed_count : 0,
            billingoSkippedCount: typeof data.billingo_skipped_count === 'number' ? data.billingo_skipped_count : 0,
            status: getTextField(data.status) || 'prepared',
          };
        });
        setBillingRuns(runs);
      },
      (error) => {
        setBillingNotice(`Számlázási futások betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
      },
    );

    const unsubscribeItems = onSnapshot(
      itemsQuery,
      (snapshot) => {
        const items = [];
        snapshot.forEach((itemDoc) => {
          const data = itemDoc.data();
          items.push({
            id: itemDoc.id,
            trainingDate: getTextField(data.training_date),
            userId: getTextField(data.user_id),
            userEmail: getTextField(data.user_email),
            userDisplayName: getTextField(data.user_display_name),
            amountHuf: typeof data.amount_huf === 'number' ? data.amount_huf : 0,
            invoiceAmountHuf: typeof data.invoice_amount_huf === 'number' ? data.invoice_amount_huf : 0,
            balanceDeductionHuf: typeof data.balance_deduction_huf === 'number' ? data.balance_deduction_huf : 0,
            billingMode: normalizeBillingMode(data.billing_mode),
            paymentMethod: normalizePaymentMethod(data.payment_method),
            status: getTextField(data.status) || 'prepared',
            statusMessage: getTextField(data.status_message),
            billingoDocumentId: typeof data.billingo_document_id === 'number' ? data.billingo_document_id : 0,
            billingoInvoiceNumber: getTextField(data.billingo_invoice_number),
            createdAt: data.createdAt,
          });
        });
        setBillingItems(items);
      },
      (error) => {
        setBillingNotice(`Számlázási tételek betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
      },
    );

    const unsubscribeLedger = onSnapshot(
      ledgerQuery,
      (snapshot) => {
        const entries = [];
        snapshot.forEach((entryDoc) => {
          const data = entryDoc.data();
          entries.push({
            id: entryDoc.id,
            userId: getTextField(data.user_id),
            userEmail: getTextField(data.user_email),
            userDisplayName: getTextField(data.user_display_name),
            amountHuf: typeof data.amount_huf === 'number' ? data.amount_huf : 0,
            type: getTextField(data.type),
            note: getTextField(data.note),
            trainingDate: getTextField(data.training_date),
            createdAt: data.createdAt,
          });
        });
        setBillingLedgerEntries(entries);
      },
      (error) => {
        setBillingNotice(`Egyenlegtörténet betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
      },
    );

    return () => {
      unsubscribeSettings();
      unsubscribeRuns();
      unsubscribeItems();
      unsubscribeLedger();
    };
  }, [currentUser, hasOwnerAccess]);

  useEffect(() => {
    if (!currentUser || !hasOwnerAccess || users.length === 0) {
      return undefined;
    }

    const syncExistingNicknames = async () => {
      const seenNicknameKeys = new Set();
      const batch = writeBatch(db);
      let writeCount = 0;

      users.forEach((userItem) => {
        const nickname = formatNickname(userItem.nickname);
        const nicknameKey = getNicknameKey(nickname);
        const userId = userItem.uid || userItem.id;

        if (!nickname || !nicknameKey || !userId || seenNicknameKeys.has(nicknameKey)) {
          return;
        }

        seenNicknameKeys.add(nicknameKey);
        batch.set(
          doc(db, 'nicknames', nicknameKey),
          {
            nickname,
            user_id: userId,
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
        writeCount += 1;
      });

      if (writeCount > 0) {
        await batch.commit();
      }
    };

    void syncExistingNicknames();
    return undefined;
  }, [currentUser, hasOwnerAccess, users]);

  useEffect(() => {
    if (!currentUser || !hasOwnerAccess) {
      setTasks([]);
      setTasksLoading(false);
      return undefined;
    }

    setTasksLoading(true);
    const tasksQuery = query(collection(db, 'tasks'), orderBy('name', 'asc'));
    const unsubscribe = onSnapshot(
      tasksQuery,
      (snapshot) => {
        const items = [];
        snapshot.forEach((taskDoc) => {
          items.push(normalizeTask(taskDoc));
        });
        setTasks(items);
        setTasksLoading(false);
      },
      (error) => {
        setTaskNotice(`Feladatok betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
        setTasksLoading(false);
      },
    );

    return () => unsubscribe();
  }, [currentUser, hasOwnerAccess]);

  useEffect(() => {
    if (!currentUser || !hasOwnerAccess || !selectedTaskTrainingDate) {
      setTrainingTaskAssignments([]);
      setTrainingTaskAssignmentsLoading(false);
      return undefined;
    }

    setTrainingTaskAssignmentsLoading(true);
    setTrainingTaskAssignmentNotice('');
    const assignmentsRef = collection(
      db,
      'training_weeks',
      selectedTaskTrainingDate,
      'task_assignments',
    );
    const unsubscribe = onSnapshot(
      assignmentsRef,
      (snapshot) => {
        const items = [];
        snapshot.forEach((assignmentDoc) => {
          items.push(normalizeTrainingTaskAssignment(assignmentDoc));
        });
        items.sort((left, right) => {
          if (left.position !== right.position) {
            return left.position - right.position;
          }
          const createdAtDifference = (left.createdAt?.toMillis?.() || 0) - (right.createdAt?.toMillis?.() || 0);
          return createdAtDifference || left.id.localeCompare(right.id);
        });
        setTrainingTaskAssignments(items);
        setTrainingTaskAssignmentsLoading(false);
      },
      (error) => {
        setTrainingTaskAssignmentNotice(`Edzésfeladatok betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
        setTrainingTaskAssignments([]);
        setTrainingTaskAssignmentsLoading(false);
      },
    );

    return () => unsubscribe();
  }, [currentUser, hasOwnerAccess, selectedTaskTrainingDate]);

  useEffect(() => {
    if (!currentUser || !hasOwnerAccess) {
      setExercises([]);
      setExercisesLoading(false);
      return undefined;
    }

    setExercisesLoading(true);
    const exercisesQuery = query(collection(db, 'fitness_exercises'), orderBy('name', 'asc'));
    const unsubscribe = onSnapshot(
      exercisesQuery,
      (snapshot) => {
        const items = [];
        snapshot.forEach((exerciseDoc) => {
          items.push(normalizeExercise(exerciseDoc));
        });
        setExercises(items);
        setExercisesLoading(false);
      },
      (error) => {
        setAuthNotice(`Gyakorlatok betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
        setExercisesLoading(false);
      },
    );

    return () => unsubscribe();
  }, [currentUser, hasOwnerAccess]);

  useEffect(() => {
    if (!currentUser || !hasOwnerAccess) {
      setFitnessResults([]);
      setFitnessResultsLoading(false);
      return undefined;
    }

    setFitnessResultsLoading(true);
    const unsubscribe = onSnapshot(
      collection(db, 'fitness_results'),
      (snapshot) => {
        const items = [];
        snapshot.forEach((resultDoc) => {
          items.push(normalizeFitnessResult(resultDoc));
        });
        setFitnessResults(items);
        setFitnessResultsLoading(false);
      },
      (error) => {
        setAuthNotice(`Eredmények betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
        setFitnessResultsLoading(false);
      },
    );

    return () => unsubscribe();
  }, [currentUser, hasOwnerAccess]);

  useEffect(() => {
    if (!currentUser || !hasOwnerAccess) {
      setAdminSquashSets([]);
      setAdminSquashSetsLoading(false);
      return undefined;
    }

    setAdminSquashSetsLoading(true);
    const setsQuery = query(
      collection(db, 'squash_sets'),
      orderBy('played_at', 'desc'),
      firestoreLimit(300),
    );
    const unsubscribe = onSnapshot(
      setsQuery,
      (snapshot) => {
        const items = [];
        snapshot.forEach((setDoc) => {
          items.push(normalizeSquashSet(setDoc));
        });
        setAdminSquashSets(items);
        setAdminSquashSetsLoading(false);
      },
      (error) => {
        setAdminSquashSetNotice(`Szett eredmények betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
        setAdminSquashSets([]);
        setAdminSquashSetsLoading(false);
      },
    );

    return () => unsubscribe();
  }, [currentUser, hasOwnerAccess]);

  useEffect(() => {
    if (!currentUser || !hasOwnerAccess || !selectedResultExerciseId) {
      setExerciseRecords([]);
      return undefined;
    }

    const recordsQuery = query(
      collection(db, 'personal_records'),
      where('exercise_id', '==', selectedResultExerciseId),
      orderBy('sort_value', 'asc'),
      orderBy('achieved_at', 'asc'),
      firestoreLimit(50),
    );
    const unsubscribe = onSnapshot(
      recordsQuery,
      (snapshot) => {
        const items = [];
        snapshot.forEach((recordDoc) => {
          items.push(normalizePersonalRecord(recordDoc));
        });
        setExerciseRecords(items);
      },
      (error) => {
        setAuthNotice(`Ranglista betöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
      },
    );

    return () => unsubscribe();
  }, [currentUser, hasOwnerAccess, selectedResultExerciseId]);

  const groupedRegistrations = useMemo(() => {
    const todayKey = getBudapestTodayKey();
    const deletedDates = new Set(deletedTrainingDates);
    const byDate = new Map();

    if (registrationPeriod === 'future') {
      upcomingTrainings.forEach((training) => {
        if (!deletedDates.has(training.key)) {
          byDate.set(training.key, []);
        }
      });
    }

    const allEntries = [...registrations, ...waitlistEntries];

    const filteredRegistrations = allEntries.filter((registration) => {
      if (deletedDates.has(registration.trainingDate)) {
        return false;
      }
      if (!registration.trainingDate) {
        return registrationPeriod === 'future';
      }
      if (registrationPeriod === 'past') {
        return registration.trainingDate < todayKey;
      }
      return registration.trainingDate >= todayKey;
    });

    filteredRegistrations.forEach((registration) => {
      const dateKey = registration.trainingDate || 'ismeretlen';
      if (!byDate.has(dateKey)) {
        byDate.set(dateKey, []);
      }
      byDate.get(dateKey).push(registration);
    });

    return Array.from(byDate.entries())
      .map(([trainingDate, items]) => {
        const sortedItems = [...items].sort((left, right) => {
          const leftTime = left.createdAt?.toMillis?.() || 0;
          const rightTime = right.createdAt?.toMillis?.() || 0;
          return leftTime - rightTime;
        });
        return {
          trainingDate,
          items: sortedItems,
          regularCount: sortedItems.filter((item) => !item.waitlisted && !item.lateCancelled).length,
          lateCancelledCount: sortedItems.filter((item) => !item.waitlisted && item.lateCancelled).length,
          waitlistCount: sortedItems.filter((item) => item.waitlisted).length,
          capacity: trainingWeeks[trainingDate]?.capacity || DEFAULT_TRAINING_CAPACITY,
          cancelledByAdmin: trainingWeeks[trainingDate]?.cancelledByAdmin === true,
        };
      })
      .sort((left, right) =>
        registrationPeriod === 'past'
          ? right.trainingDate.localeCompare(left.trainingDate)
          : left.trainingDate.localeCompare(right.trainingDate),
      );
  }, [deletedTrainingDates, registrations, registrationPeriod, trainingWeeks, upcomingTrainings, waitlistEntries]);

  const trainingRows = useMemo(() => {
    const deletedDates = new Set(deletedTrainingDates);
    const rowsByDate = new Map();

    trainingListRows.forEach((training) => {
      if (!training.trainingDate || deletedDates.has(training.trainingDate)) {
        return;
      }
      rowsByDate.set(training.trainingDate, training);
    });

    upcomingTrainings.forEach((training) => {
      if (deletedDates.has(training.key) || rowsByDate.has(training.key)) {
        return;
      }
      rowsByDate.set(training.key, {
        id: training.key,
        trainingDate: training.key,
        cancelledByAdmin: false,
        capacity: DEFAULT_TRAINING_CAPACITY,
        count: 0,
        waitlistCount: 0,
        waitlistReservedCount: 0,
        startTime: DEFAULT_TRAINING_START_TIME,
        trainingStartAt: null,
        createdAt: null,
      });
    });

    return Array.from(rowsByDate.values())
      .map((training) => ({
        ...training,
        timeLabel: formatTrainingTime(training),
        statusLabel: getTrainingStatusLabel(training, currentTime),
      }))
      .sort((left, right) => {
        const dateDiff = (right.trainingDate || '').localeCompare(left.trainingDate || '');
        if (dateDiff !== 0) {
          return dateDiff;
        }
        return (right.timeLabel || '').localeCompare(left.timeLabel || '');
      })
      .slice(0, trainingListLimit);
  }, [currentTime, deletedTrainingDates, trainingListLimit, trainingListRows, upcomingTrainings]);

  const hasMoreTrainingRows = trainingListRows.length >= trainingListLimit;

  const taskTrainingTodayKey = getBudapestTodayKey();
  const upcomingTaskTrainings = useMemo(() => {
    const deletedDates = new Set(deletedTrainingDates);
    return Object.values(trainingWeeks)
      .filter((training) => (
        training.trainingDate >= taskTrainingTodayKey
        && !training.cancelledByAdmin
        && !deletedDates.has(training.trainingDate)
      ))
      .sort((left, right) => {
        const dateDifference = left.trainingDate.localeCompare(right.trainingDate);
        return dateDifference || formatTrainingTime(left).localeCompare(formatTrainingTime(right));
      });
  }, [deletedTrainingDates, taskTrainingTodayKey, trainingWeeks]);

  const taskById = useMemo(
    () => Object.fromEntries(tasks.map((task) => [task.id, task])),
    [tasks],
  );

  useEffect(() => {
    if (upcomingTaskTrainings.some((training) => training.trainingDate === selectedTaskTrainingDate)) {
      return;
    }
    setSelectedTaskTrainingDate(upcomingTaskTrainings[0]?.trainingDate || '');
  }, [selectedTaskTrainingDate, upcomingTaskTrainings]);

  useEffect(() => {
    if (tasks.some((task) => task.id === selectedTrainingTaskId)) {
      return;
    }
    setSelectedTrainingTaskId(tasks[0]?.id || '');
  }, [selectedTrainingTaskId, tasks]);

  const exerciseById = useMemo(
    () => Object.fromEntries(exercises.map((exercise) => [exercise.id, exercise])),
    [exercises],
  );

  useEffect(() => {
    if (fitnessResultForm.exerciseId || exercises.length === 0) {
      return;
    }
    setFitnessResultForm((current) => ({ ...current, exerciseId: exercises[0].id }));
  }, [exercises, fitnessResultForm.exerciseId]);

  useEffect(() => {
    if (selectedResultExerciseId || exercises.length === 0) {
      return;
    }
    setSelectedResultExerciseId(exercises[0].id);
  }, [exercises, selectedResultExerciseId]);

  useEffect(() => {
    if (fitnessResultForm.userId || users.length === 0) {
      return;
    }
    setFitnessResultForm((current) => ({ ...current, userId: users[0].uid || users[0].id }));
  }, [fitnessResultForm.userId, users]);

  useEffect(() => {
    if (balanceForm.userId || users.length === 0) {
      return;
    }
    setBalanceForm((current) => ({ ...current, userId: users[0].uid || users[0].id }));
  }, [balanceForm.userId, users]);

  const userById = useMemo(() => {
    const byId = {};
    users.forEach((userItem) => {
      const userId = userItem.uid || userItem.id;
      if (userId) {
        byId[userId] = userItem;
      }
    });
    return byId;
  }, [users]);

  const billingItemsByTrainingDate = useMemo(() => {
    const byDate = {};
    billingItems.forEach((item) => {
      if (!item.trainingDate) {
        return;
      }
      if (!byDate[item.trainingDate]) {
        byDate[item.trainingDate] = [];
      }
      byDate[item.trainingDate].push(item);
    });
    return byDate;
  }, [billingItems]);

  const buildTrainingBillingPreview = (trainingDate, items) => {
    const participants = items.filter((item) => !item.waitlisted);
    const participantCount = participants.length;
    const rows = participants.map((item) => {
      const userItem = userById[item.userId] || {};
      const billingMode = normalizeBillingMode(userItem.billingMode);
      const paymentMethod = normalizePaymentMethod(item.billingPaymentMethod);
      const calculation = calculateBillingPrice(
        billingSettings,
        participantCount,
        billingMode,
        item.lateCancelled,
      );
      const billingBalanceHuf = Number(userItem.billingBalanceHuf || 0);
      const {
        balanceBillingMode,
        balanceDeductionHuf,
        balanceAfterHuf,
        needsInvoice,
      } = resolveBillingSettlement({
        amountHuf: calculation.amountHuf,
        billingMode,
        balanceHuf: billingBalanceHuf,
        allowNegativeBalance: userItem.billingAllowNegativeBalance,
      });
      const missingPartnerData = needsInvoice && !hasBillingPartnerData(userItem);
      const status = billingMode === 'none' || calculation.amountHuf <= 0
        ? 'skipped_no_invoice'
        : balanceDeductionHuf > 0
          ? balanceBillingMode ? 'charged_to_balance' : 'paid_from_balance'
          : missingPartnerData
            ? 'needs_billing_data'
            : 'ready_for_billingo';

      return {
        ...item,
        userItem,
        billingMode,
        paymentMethod,
        amountHuf: calculation.amountHuf,
        invoiceAmountHuf: needsInvoice ? calculation.amountHuf : 0,
        balanceDeductionHuf,
        balanceBeforeHuf: billingBalanceHuf,
        balanceAfterHuf,
        balanceBillingMode,
        billingAllowNegativeBalance: userItem.billingAllowNegativeBalance === true,
        category: calculation.category,
        courtFeePerPersonHuf: calculation.courtFeePerPersonHuf,
        trainerFeePerPersonHuf: calculation.trainerFeePerPersonHuf,
        status,
        statusMessage: missingPartnerData
          ? 'Hiányzó számlázási adat'
          : status === 'charged_to_balance'
            ? 'Egyenlegre terhelve'
            : status === 'paid_from_balance'
              ? 'Egyenleg fedezi'
              : status === 'skipped_no_invoice'
                ? 'Nem számlázandó'
                : 'Billingo számlára előkészítve',
      };
    });

    const category = getBillingCategoryForCount(billingSettings, participantCount);
    const clubCourtFeeParticipantCount = rows.filter((row) => isFullFeeBillingMode(row.billingMode)).length;
    const clubCourtCost = calculateClubCourtCost({
      courtFeeHuf: billingSettings.courtFeeHuf,
      trainingDurationHours: billingSettings.trainingDurationHours,
      courtCount: category?.courtCount,
      participantCount,
      fullFeeParticipantCount: clubCourtFeeParticipantCount,
    });

    return {
      trainingDate,
      participantCount,
      category,
      rows,
      clubCourtFeePerPersonHuf: clubCourtCost.courtFeePerPersonHuf,
      clubCourtFeeParticipantCount,
      clubCourtCostHuf: clubCourtCost.clubCourtCostHuf,
      invoiceCount: rows.filter((row) => row.status === 'ready_for_billingo').length,
      balanceCoveredCount: rows.filter((row) => ['paid_from_balance', 'charged_to_balance'].includes(row.status)).length,
      skippedCount: rows.filter((row) => row.status === 'skipped_no_invoice').length,
      failedCount: rows.filter((row) => row.status === 'needs_billing_data').length,
      invoiceTotalHuf: rows.reduce((sum, row) => sum + row.invoiceAmountHuf, 0),
      balanceDeductionTotalHuf: rows.reduce((sum, row) => sum + row.balanceDeductionHuf, 0),
    };
  };

  const filteredFitnessResults = useMemo(() => {
    return fitnessResults
      .filter((item) => !selectedResultExerciseId || item.exerciseId === selectedResultExerciseId)
      .filter((item) => !selectedResultUserId || item.userId === selectedResultUserId)
      .sort((left, right) => {
        const leftTime = left.recordedAt?.toMillis?.() || left.recordedAt?.toDate?.()?.getTime?.() || 0;
        const rightTime = right.recordedAt?.toMillis?.() || right.recordedAt?.toDate?.()?.getTime?.() || 0;
        return rightTime - leftTime;
      });
  }, [fitnessResults, selectedResultExerciseId, selectedResultUserId]);

  const selectedExerciseLeaderboard = useMemo(() => {
    const exercise = exerciseById[selectedResultExerciseId];
    return sortRankingRows(exerciseRecords, exercise?.resultDirection || 'higher');
  }, [exerciseById, exerciseRecords, selectedResultExerciseId]);

  const selectedUserProgress = useMemo(() => {
    if (!selectedResultUserId) {
      return [];
    }
    return fitnessResults
      .filter((item) => item.userId === selectedResultUserId)
      .sort((left, right) => {
        const leftExercise = getExerciseName(exerciseById, left.exerciseId, left.exerciseName);
        const rightExercise = getExerciseName(exerciseById, right.exerciseId, right.exerciseName);
        const nameDiff = leftExercise.localeCompare(rightExercise, 'hu');
        if (nameDiff !== 0) {
          return nameDiff;
        }
        return (left.recordedAt?.toMillis?.() || 0) - (right.recordedAt?.toMillis?.() || 0);
      });
  }, [exerciseById, fitnessResults, selectedResultUserId]);

  const handleGoogleLogin = async () => {
    setAuthBusy(true);
    setAuthNotice('');
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      setAuthNotice(getFriendlyAuthErrorMessage(error));
    } finally {
      setAuthBusy(false);
    }
  };

  const handleEmailLogin = async (event) => {
    event.preventDefault();
    setAuthBusy(true);
    setAuthNotice('');

    try {
      await signInWithVerifiedEmailPassword({
        auth,
        cloudFunctions,
        db,
        email: authEmail,
        password: authPassword,
      });
    } catch (error) {
      setAuthNotice(getFriendlyAuthErrorMessage(error));
    } finally {
      setAuthBusy(false);
    }
  };

  const handlePasswordReset = async () => {
    setAuthBusy(true);
    setAuthNotice('');

    try {
      const resetResult = await sendPasswordReset({ cloudFunctions, email: authEmail });
      if (
        resetResult.status === 'google_provider'
        || resetResult.status === 'provider_without_password'
      ) {
        setAuthNotice('Ehhez az e-mail címhez Google-belépés tartozik. A jelszavadat a Google fiókodban tudod megváltoztatni.');
        return;
      }

      setAuthNotice('Ha ehhez az e-mail címhez tartozik fiók, elküldtük a jelszó-visszaállító levelet.');
    } catch (error) {
      if (error?.code === 'auth/user-not-found') {
        setAuthNotice('Ha ehhez az e-mail címhez tartozik fiók, elküldtük a jelszó-visszaállító levelet.');
      } else {
        setAuthNotice(getFriendlyAuthErrorMessage(error));
      }
    } finally {
      setAuthBusy(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setAuthNotice('');
    } catch (error) {
      setAuthNotice(`Kijelentkezési hiba: ${error?.message || 'ismeretlen hiba'}`);
    }
  };

  const closeTrainingCancelConfirm = () => {
    if (trainingCancelBusy) {
      return;
    }

    setTrainingCancelDate('');
  };

  const closeTrainingDeleteConfirm = () => {
    if (trainingDeleteBusy) {
      return;
    }

    setTrainingDeleteDate('');
  };

  const handleCancelTraining = async () => {
    if (!currentUser || !hasOwnerAccess || !trainingCancelDate) {
      return;
    }

    setTrainingCancelBusy(trainingCancelDate);
    setTrainingCancelNotice('');

    try {
      await setDoc(
        doc(db, 'training_weeks', trainingCancelDate),
        {
          training_date: trainingCancelDate,
          cancelled_by_admin: true,
          cancelled_at: serverTimestamp(),
          cancelled_by_user_id: currentUser.uid,
          cancelled_by_user_email: currentUser.email || '',
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      setTrainingCancelNotice(`Sikeres edzés lemondás: ${formatTrainingDate(trainingCancelDate)}`);
      setTrainingCancelDate('');
    } catch (error) {
      setTrainingCancelNotice(`Edzés lemondási hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setTrainingCancelBusy('');
    }
  };

  const handleRestoreTraining = async (trainingDate) => {
    if (!currentUser || !hasOwnerAccess || !trainingDate) {
      return;
    }

    setTrainingRestoreBusy(trainingDate);
    setTrainingCancelNotice('');

    try {
      await setDoc(
        doc(db, 'training_weeks', trainingDate),
        {
          training_date: trainingDate,
          cancelled_by_admin: false,
          restored_at: serverTimestamp(),
          restored_by_user_id: currentUser.uid,
          restored_by_user_email: currentUser.email || '',
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      setTrainingCancelNotice(`Sikeres lemondás visszavonás: ${formatTrainingDate(trainingDate)}`);
    } catch (error) {
      setTrainingCancelNotice(`Lemondás visszavonási hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setTrainingRestoreBusy('');
    }
  };

  const handleDeleteTraining = async () => {
    if (!currentUser || !hasOwnerAccess || !trainingDeleteDate) {
      return;
    }

    const training = trainingListRows.find((item) => item.trainingDate === trainingDeleteDate)
      || trainingWeeks[trainingDeleteDate];
    const matchingRegistrations = registrations.filter((item) => item.trainingDate === trainingDeleteDate);
    const regularCount = registrationsLoading
      ? (typeof training?.count === 'number' ? training.count : 0)
      : matchingRegistrations.filter((item) => !item.lateCancelled).length;
    const lateCancelledCount = registrationsLoading
      ? 0
      : matchingRegistrations.filter((item) => item.lateCancelled).length;
    const waitlistCount = waitlistLoading
      ? (typeof training?.waitlistCount === 'number' ? training.waitlistCount : 0)
      : waitlistEntries.filter((item) => item.trainingDate === trainingDeleteDate).length;
    const blockedMessage = getTrainingDeleteBlockedMessage({
      regularCount,
      lateCancelledCount,
      waitlistCount,
      loading: registrationsLoading || waitlistLoading,
    });

    if (blockedMessage) {
      setTrainingCancelNotice(blockedMessage);
      setTrainingDeleteDate('');
      return;
    }

    setTrainingDeleteBusy(trainingDeleteDate);
    setTrainingCancelNotice('');

    try {
      await setDoc(
        doc(db, 'deleted_training_weeks', trainingDeleteDate),
        {
          training_date: trainingDeleteDate,
          deleted_at: serverTimestamp(),
          deleted_by_user_id: currentUser.uid,
          deleted_by_user_email: currentUser.email || '',
        },
        { merge: true },
      );
      await deleteDoc(doc(db, 'training_weeks', trainingDeleteDate));
      setTrainingCancelNotice(`Sikeres edzés törlés: ${formatTrainingDate(trainingDeleteDate)}`);
      setTrainingDeleteDate('');
    } catch (error) {
      setTrainingCancelNotice(`Edzés törlési hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setTrainingDeleteBusy('');
    }
  };

  const handleRemoveRegistration = async (item) => {
    if (!currentUser || !hasOwnerAccess || !item?.id) {
      return;
    }

    setRegistrationRemoveBusyId(item.id);
    setRegistrationActionNotice('');

    try {
      const collectionName = item.waitlisted ? 'training_waitlist' : 'training_registrations';
      const registrationRef = doc(db, collectionName, item.id);
      await setDoc(
        registrationRef,
        {
          admin_removed: true,
          admin_removed_at: serverTimestamp(),
          admin_removed_by_user_id: currentUser.uid,
          admin_removed_by_user_email: currentUser.email || '',
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      await deleteDoc(registrationRef);

      if (!item.waitlisted && item.userId && item.trainingDate) {
        await deleteDoc(doc(db, 'late_cancellations', `${item.userId}_${item.trainingDate}`));
      }

      setRegistrationActionNotice(
        `Sikeres ${item.waitlisted ? 'várólistás jelentkezés' : 'jelentkezés'} törlés: ${item.userDisplayName || item.userEmail || item.id}`,
      );
    } catch (error) {
      setRegistrationActionNotice(`Jelentkezés törlési hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setRegistrationRemoveBusyId('');
    }
  };

  const handleAddRegistration = async (trainingDate) => {
    if (!currentUser || !hasOwnerAccess || !trainingDate) {
      return;
    }

    const selectedUserId = registrationAddUserDrafts[trainingDate] || '';
    const selectedUser = users.find((user) => user.uid === selectedUserId || user.id === selectedUserId);
    const training = trainingWeeks[trainingDate] || {};
    const trainingStartAt = getTrainingStartForWeek(trainingDate, training);
    const trainingEndAt = getTrainingEndForWeek(trainingDate, training);
    const matchingRegistrations = registrations.filter((item) => item.trainingDate === trainingDate);
    const activeRegistrationCount = matchingRegistrations.filter((item) => !item.lateCancelled).length;
    const occupiedCapacity = activeRegistrationCount + (training.waitlistReservedCount || 0);
    const capacity = training.capacity || DEFAULT_TRAINING_CAPACITY;

    setRegistrationActionNotice('');

    if (!selectedUser) {
      setRegistrationActionNotice('Válassz ki egy felhasználót a hozzáadáshoz.');
      return;
    }
    if (selectedUser.trainingRegistrationDisabledByAdmin) {
      setRegistrationActionNotice('A kiválasztott felhasználó jelentkezése adminisztrátor által le van tiltva.');
      return;
    }
    if (training.cancelledByAdmin) {
      setRegistrationActionNotice('Lemondott edzéshez nem adhatsz hozzá felhasználót.');
      return;
    }
    if (!trainingStartAt || !trainingEndAt || new Date().getTime() >= trainingEndAt.getTime()) {
      setRegistrationActionNotice('Az edzés már befejeződött, ezért új felhasználó nem adható hozzá.');
      return;
    }
    if (occupiedCapacity >= capacity) {
      setRegistrationActionNotice('Az edzés elérte a maximális férőhelyet. Előbb emeld meg a létszámkorlátot.');
      return;
    }

    const registrationId = `${selectedUser.uid}_${trainingDate}`;
    const waitlistId = `${selectedUser.uid}_${trainingDate}`;
    setRegistrationAddBusyDate(trainingDate);

    try {
      const registrationRef = doc(db, 'training_registrations', registrationId);
      const waitlistRef = doc(db, 'training_waitlist', waitlistId);
      await runTransaction(db, async (transaction) => {
        const [registrationSnapshot, waitlistSnapshot] = await Promise.all([
          transaction.get(registrationRef),
          transaction.get(waitlistRef),
        ]);
        if (registrationSnapshot.exists()) {
          throw new Error('A felhasználó már szerepel az edzés jelentkezői között.');
        }
        if (waitlistSnapshot.exists()) {
          throw new Error('A felhasználó már szerepel az edzés várólistáján.');
        }

        transaction.set(registrationRef, {
          user_id: selectedUser.uid,
          user_email: selectedUser.email || '',
          user_display_name: selectedUser.displayName || selectedUser.billingName || selectedUser.nickname || selectedUser.email || '',
          training_date: trainingDate,
          training_start_at: Timestamp.fromDate(trainingStartAt),
          billing_payment_method: 'bank_transfer',
          admin_added: true,
          admin_added_at: serverTimestamp(),
          admin_added_by_user_id: currentUser.uid,
          admin_added_by_user_email: currentUser.email || '',
          updatedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
        });
      });

      setRegistrationAddUserDrafts((current) => ({ ...current, [trainingDate]: '' }));
      setRegistrationActionNotice(
        `Sikeres felhasználó-hozzáadás: ${selectedUser.displayName || selectedUser.email || selectedUser.uid}`,
      );
    } catch (error) {
      setRegistrationActionNotice(`Felhasználó hozzáadási hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setRegistrationAddBusyDate('');
    }
  };

  const handleRegistrationPaymentMethodChange = async (item, nextPaymentMethod) => {
    if (!currentUser || !hasOwnerAccess || !item?.id || item.waitlisted) {
      return;
    }

    const normalizedPaymentMethod = normalizePaymentMethod(nextPaymentMethod);
    setPaymentMethodBusyId(item.id);
    setRegistrationActionNotice('');

    try {
      await setDoc(
        doc(db, 'training_registrations', item.id),
        {
          billing_payment_method: normalizedPaymentMethod,
          billing_payment_method_updated_at: serverTimestamp(),
          billing_payment_method_updated_by_user_id: currentUser.uid,
          billing_payment_method_updated_by_user_email: currentUser.email || '',
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      setRegistrationActionNotice('Sikeres fizetési mód mentés.');
    } catch (error) {
      setRegistrationActionNotice(`Fizetési mód mentési hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setPaymentMethodBusyId('');
    }
  };

  const handleBillingSettingFieldChange = (field, value) => {
    setBillingSettingsDraft((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleBillingCategoryDraftChange = (index, field, value) => {
    setBillingSettingsDraft((current) => ({
      ...current,
      categories: current.categories.map((category, categoryIndex) =>
        categoryIndex === index ? { ...category, [field]: value } : category
      ),
    }));
  };

  const handleSaveBillingSettings = async () => {
    if (!currentUser || !hasOwnerAccess) {
      setBillingNotice('A mentéshez admin jogosultság szükséges.');
      return;
    }

    const normalizedSettings = normalizeBillingSettings(billingSettingsDraft);
    const orderedCategories = [...normalizedSettings.categories].sort((left, right) => left.minParticipants - right.minParticipants);
    const hasOverlap = orderedCategories.some((category, index) => {
      if (category.maxParticipants < category.minParticipants) {
        return true;
      }
      const previousCategory = orderedCategories[index - 1];
      return previousCategory && category.minParticipants <= previousCategory.maxParticipants;
    });

    if (hasOverlap) {
      setBillingNotice('A létszám kategóriák nem fedhetik egymást, és a tól érték nem lehet nagyobb az ig értéknél.');
      return;
    }

    setBillingSettingsSaveBusy(true);
    setBillingNotice('');

    try {
      await setDoc(
        doc(db, 'billing_settings', 'main'),
        {
          ...normalizedSettings,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser.uid,
          updatedByEmail: currentUser.email || '',
        },
        { merge: true },
      );
      setBillingNotice('Sikeres számlázási beállítás mentés.');
    } catch (error) {
      setBillingNotice(`Számlázási beállítás mentési hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setBillingSettingsSaveBusy(false);
    }
  };

  const handleChangeUserBillingMode = async (userItem, nextBillingMode) => {
    if (!currentUser || !hasOwnerAccess || !userItem?.id) {
      return;
    }

    const normalizedBillingMode = normalizeBillingMode(nextBillingMode);
    setBillingUserBusyId(userItem.id);
    setBillingNotice('');

    try {
      await setDoc(
        doc(db, 'users', userItem.id),
        {
          billing_mode: normalizedBillingMode,
          billing_mode_updated_at: serverTimestamp(),
          billing_mode_updated_by_user_id: currentUser.uid,
          billing_mode_updated_by_user_email: currentUser.email || '',
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      setBillingNotice(`Sikeres számlázási mód mentés: ${userItem.displayName || userItem.email || userItem.id}`);
    } catch (error) {
      setBillingNotice(`Számlázási mód mentési hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setBillingUserBusyId('');
    }
  };

  const handleToggleNegativeBalance = async (userItem, allowNegativeBalance) => {
    if (!currentUser || !hasOwnerAccess || !userItem?.id) {
      return;
    }

    setBillingUserBusyId(userItem.id);
    setBillingNotice('');

    try {
      await setDoc(
        doc(db, 'users', userItem.id),
        {
          billing_allow_negative_balance: allowNegativeBalance === true,
          billing_allow_negative_balance_updated_at: serverTimestamp(),
          billing_allow_negative_balance_updated_by_user_id: currentUser.uid,
          billing_allow_negative_balance_updated_by_user_email: currentUser.email || '',
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      setBillingNotice(
        `Negatív egyenleg ${allowNegativeBalance ? 'engedélyezve' : 'kikapcsolva'}: ${userItem.displayName || userItem.email || userItem.id}`,
      );
    } catch (error) {
      setBillingNotice(`Negatív egyenleg beállítási hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setBillingUserBusyId('');
    }
  };

  const handleBalanceFormChange = (field, value) => {
    setBalanceForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleAddBalanceTransaction = async () => {
    if (!currentUser || !hasOwnerAccess) {
      setBillingNotice('Az egyenleg módosításához admin jogosultság szükséges.');
      return;
    }

    const selectedUser = users.find((userItem) => (userItem.uid || userItem.id) === balanceForm.userId);
    const amountHuf = Number.parseInt(String(balanceForm.amount).replace(/\s+/g, ''), 10);
    const type = balanceTransactionTypeOptions.some((option) => option.value === balanceForm.type)
      ? balanceForm.type
      : 'admin_credit';

    if (!selectedUser) {
      setBillingNotice('Válassz felhasználót az egyenleg módosításához.');
      return;
    }
    if (!Number.isInteger(amountHuf) || amountHuf === 0) {
      setBillingNotice('Az egyenleg módosítás összege nem lehet nulla.');
      return;
    }
    if (type !== 'admin_correction' && amountHuf < 0) {
      setBillingNotice('Negatív összeget csak Admin korrekció típusnál lehet rögzíteni.');
      return;
    }

    setBalanceSaveBusy(true);
    setBillingNotice('');

    try {
      const batch = writeBatch(db);
      const userRef = doc(db, 'users', selectedUser.id);
      const ledgerRef = doc(collection(db, 'billing_balance_ledger'));
      batch.set(
        userRef,
        {
          billing_balance_huf: increment(amountHuf),
          billing_balance_updated_at: serverTimestamp(),
          billing_balance_updated_by_user_id: currentUser.uid,
          billing_balance_updated_by_user_email: currentUser.email || '',
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      batch.set(ledgerRef, {
        user_id: selectedUser.uid || selectedUser.id,
        user_email: selectedUser.email || '',
        user_display_name: selectedUser.displayName || selectedUser.nickname || '',
        amount_huf: amountHuf,
        type,
        note: balanceForm.note.trim(),
        createdAt: serverTimestamp(),
        created_by_user_id: currentUser.uid,
        created_by_user_email: currentUser.email || '',
      });
      await batch.commit();
      setBillingNotice(`Sikeres egyenleg módosítás: ${formatCurrency(amountHuf)}`);
      setBalanceForm((current) => ({
        ...current,
        amount: '',
        note: '',
      }));
    } catch (error) {
      setBillingNotice(`Egyenleg módosítási hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setBalanceSaveBusy(false);
    }
  };

  const handleStartTrainingBilling = async (trainingDate, items) => {
    if (!currentUser || !hasOwnerAccess || !trainingDate) {
      return;
    }

    if (trainingWeeks[trainingDate]?.cancelledByAdmin === true) {
      setRegistrationActionNotice('Lemondott edzésre nem indítható számlázás.');
      return;
    }

    const preview = buildTrainingBillingPreview(trainingDate, items);
    if (preview.participantCount < 2) {
      setRegistrationActionNotice('Legalább 2 jelentkező szükséges a számlázás indításához.');
      return;
    }

    setBillingStartBusyDate(trainingDate);
    setRegistrationActionNotice('');

    try {
      const sendPreparedRunToBillingo = async () => {
        const sendBillingRunToBillingo = httpsCallable(cloudFunctions, 'sendBillingRunToBillingo');
        const result = await sendBillingRunToBillingo({ trainingDate });
        const resultData = result.data && typeof result.data === 'object' ? result.data : {};
        const sentCount = typeof resultData.sentCount === 'number' ? resultData.sentCount : 0;
        const failedCount = typeof resultData.failedCount === 'number' ? resultData.failedCount : 0;
        const skippedCount = typeof resultData.skippedCount === 'number' ? resultData.skippedCount : 0;

        setRegistrationActionNotice(
          failedCount > 0
            ? `Billingo küldés részben kész: ${sentCount} kiküldve, ${failedCount} hibás, ${skippedCount} kihagyva.`
            : `Billingo számlázás kész: ${sentCount} számla kiküldve, ${skippedCount} tétel kihagyva.`,
        );
      };

      const runRef = doc(db, 'billing_runs', trainingDate);
      const existingRun = await getDoc(runRef);
      if (existingRun.exists()) {
        await sendPreparedRunToBillingo();
        return;
      }

      const batch = writeBatch(db);
      batch.set(runRef, {
        training_date: trainingDate,
        status: preview.failedCount > 0 ? 'prepared_with_errors' : 'prepared',
        participant_count: preview.participantCount,
        category_id: preview.category?.id || '',
        category_min_participants: preview.category?.minParticipants || 0,
        category_max_participants: preview.category?.maxParticipants || 0,
        category_total_price_huf: preview.category?.totalPriceHuf || 0,
        court_fee_huf: Number(billingSettings.courtFeeHuf || 0),
        training_duration_hours: Number(billingSettings.trainingDurationHours || 0),
        late_cancellation_percent: Number(billingSettings.lateCancellationPercent || 0),
        invoice_count: preview.invoiceCount,
        balance_covered_count: preview.balanceCoveredCount,
        club_court_fee_per_person_huf: preview.clubCourtFeePerPersonHuf,
        club_court_fee_participant_count: preview.clubCourtFeeParticipantCount,
        club_court_cost_huf: preview.clubCourtCostHuf,
        skipped_count: preview.skippedCount,
        failed_count: preview.failedCount,
        invoice_total_huf: preview.invoiceTotalHuf,
        balance_deduction_total_huf: preview.balanceDeductionTotalHuf,
        createdAt: serverTimestamp(),
        created_by_user_id: currentUser.uid,
        created_by_user_email: currentUser.email || '',
      });

      preview.rows.forEach((row) => {
        const itemRef = doc(db, 'billing_invoice_items', `${trainingDate}_${row.userId || row.id}`);
        batch.set(itemRef, {
          training_date: trainingDate,
          registration_id: row.id,
          user_id: row.userId || '',
          user_email: row.userItem.email || row.userEmail || '',
          user_display_name: row.userItem.displayName || row.userDisplayName || '',
          billing_name: row.userItem.billingName || '',
          billing_mode: row.billingMode,
          payment_method: row.paymentMethod,
          payment_due_days: row.paymentMethod === 'bank_transfer' ? 5 : 0,
          late_cancelled: row.lateCancelled === true,
          participant_count: preview.participantCount,
          category_id: row.category?.id || '',
          category_total_price_huf: row.category?.totalPriceHuf || 0,
          court_fee_per_person_huf: row.courtFeePerPersonHuf,
          trainer_fee_per_person_huf: row.trainerFeePerPersonHuf,
          amount_huf: row.amountHuf,
          invoice_amount_huf: row.invoiceAmountHuf,
          balance_before_huf: row.balanceBeforeHuf,
          balance_after_huf: row.balanceAfterHuf,
          balance_deduction_huf: row.balanceDeductionHuf,
          balance_billing_mode: row.balanceBillingMode,
          billing_allow_negative_balance: row.billingAllowNegativeBalance,
          status: row.status,
          status_message: row.statusMessage,
          createdAt: serverTimestamp(),
          created_by_user_id: currentUser.uid,
          created_by_user_email: currentUser.email || '',
        });

        if (row.balanceDeductionHuf > 0 && row.userItem.id) {
          const chargedToBalance = row.status === 'charged_to_balance';
          const userRef = doc(db, 'users', row.userItem.id);
          const ledgerRef = doc(collection(db, 'billing_balance_ledger'));
          batch.set(
            userRef,
            {
              billing_balance_huf: increment(-row.balanceDeductionHuf),
              billing_balance_updated_at: serverTimestamp(),
              billing_balance_updated_by_user_id: currentUser.uid,
              billing_balance_updated_by_user_email: currentUser.email || '',
              updatedAt: serverTimestamp(),
            },
            { merge: true },
          );
          batch.set(ledgerRef, {
            user_id: row.userId || '',
            user_email: row.userItem.email || row.userEmail || '',
            user_display_name: row.userItem.displayName || row.userDisplayName || '',
            amount_huf: -row.balanceDeductionHuf,
            balance_after_huf: row.balanceAfterHuf,
            type: chargedToBalance ? 'training_balance_charge' : 'training_deduction',
            note: chargedToBalance
              ? `Csapatedzés egyenlegre terhelve (${trainingDate})`
              : `Csapatedzés egyenlegből rendezve (${trainingDate})`,
            training_date: trainingDate,
            billing_run_id: trainingDate,
            createdAt: serverTimestamp(),
            created_by_user_id: currentUser.uid,
            created_by_user_email: currentUser.email || '',
          });
        }
      });

      await batch.commit();
      await sendPreparedRunToBillingo();
    } catch (error) {
      setRegistrationActionNotice(`Számlázás indítási hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setBillingStartBusyDate('');
    }
  };

  const handleToggleUserTrainingRegistration = async (userItem) => {
    if (!currentUser || !hasOwnerAccess || !userItem?.id) {
      return;
    }

    const nextDisabled = !userItem.trainingRegistrationDisabledByAdmin;
    setUserBlockBusyId(userItem.id);
    setUserActionNotice('');

    try {
      await setDoc(
        doc(db, 'users', userItem.id),
        {
          training_registration_disabled_by_admin: nextDisabled,
          training_registration_disabled_at: nextDisabled ? serverTimestamp() : null,
          training_registration_disabled_by_user_id: nextDisabled ? currentUser.uid : '',
          training_registration_disabled_by_user_email: nextDisabled ? currentUser.email || '' : '',
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      setUserActionNotice(
        nextDisabled
          ? `Jelentkezés letiltva: ${userItem.displayName || userItem.email || userItem.id}`
          : `Jelentkezési tiltás feloldva: ${userItem.displayName || userItem.email || userItem.id}`,
      );
    } catch (error) {
      setUserActionNotice(`Felhasználó tiltási hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setUserBlockBusyId('');
    }
  };

  const handleChangeUserRole = async (userItem, nextRole) => {
    if (!currentUser || !hasOwnerAccess || !userItem?.id) {
      return;
    }

    const normalizedRole = nextRole === 'admin' ? 'admin' : 'user';
    const targetUserId = userItem.uid || userItem.id;

    if (normalizedRole === userItem.role) {
      return;
    }

    if (targetUserId === currentUser.uid && normalizedRole !== 'admin') {
      setUserActionNotice('A saját admin jogosultságodat nem tudod levenni.');
      return;
    }

    setUserRoleBusyId(userItem.id);
    setUserActionNotice('');

    try {
      const setUserRole = httpsCallable(cloudFunctions, 'setUserRole');
      await setUserRole({
        userId: targetUserId,
        role: normalizedRole,
      });
      setUserActionNotice(
        `Jogosultság frissítve: ${userItem.displayName || userItem.email || userItem.id} - ${normalizedRole === 'admin' ? 'Admin' : 'Felhasználó'}`,
      );
    } catch (error) {
      setUserActionNotice(`Jogosultság mentési hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setUserRoleBusyId('');
    }
  };

  const handleOpenUserBillingEdit = (userItem) => {
    if (!userItem?.id) {
      return;
    }

    setUserBillingEdit(userItem);
    setUserBillingForm({
      name: userItem.billingName || userItem.displayName || '',
      country: userItem.country || 'Magyarország',
      zip: userItem.zip || '',
      city: userItem.city || '',
      street: userItem.street || '',
    });
    setUserBillingSaveNotice('');
  };

  const handleUserBillingFormChange = (field, value) => {
    setUserBillingForm((current) => ({
      ...current,
      [field]: value,
    }));
    setUserBillingSaveNotice('');
  };

  const handleSaveUserBillingData = async () => {
    if (!currentUser || !hasOwnerAccess || !userBillingEdit?.id) {
      return;
    }

    const normalizedForm = {
      name: userBillingForm.name.trim(),
      country: userBillingForm.country.trim(),
      zip: userBillingForm.zip.trim(),
      city: userBillingForm.city.trim(),
      street: userBillingForm.street.trim(),
    };

    if (!hasCompleteBillingData(normalizedForm)) {
      setUserBillingSaveNotice('A név és a teljes számlázási cím megadása kötelező.');
      return;
    }

    setUserBillingSaveBusy(true);
    setUserBillingSaveNotice('');

    try {
      await setDoc(
        doc(db, 'users', userBillingEdit.id),
        {
          billing_name: normalizedForm.name,
          billing_country: normalizedForm.country,
          billing_zip: normalizedForm.zip,
          billing_city: normalizedForm.city,
          billing_address: normalizedForm.street,
          billing_updated_at: serverTimestamp(),
          billing_updated_by_user_id: currentUser.uid,
          billing_updated_by_user_email: currentUser.email || '',
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      setUserActionNotice(
        `Számlázási adatok frissítve: ${userBillingEdit.displayName || userBillingEdit.email || userBillingEdit.id}`,
      );
      setUserBillingEdit(null);
    } catch (error) {
      setUserBillingSaveNotice(`Számlázási adatok mentési hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setUserBillingSaveBusy(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!currentUser || !hasOwnerAccess || !userDeleteConfirm?.id) {
      return;
    }

    const userItem = userDeleteConfirm;
    const targetUserId = userItem.uid || userItem.id;

    if (userItem.role === 'admin' && targetUserId !== currentUser.uid) {
      setUserActionNotice('Admin jogosultságú felhasználót nem törölhetsz.');
      setUserDeleteConfirm(null);
      return;
    }

    setUserDeleteBusyId(userItem.id);
    setUserActionNotice('');

    try {
      const deleteUserAccount = httpsCallable(cloudFunctions, 'deleteUserAccount');
      const result = await deleteUserAccount({ userId: targetUserId });
      const data = result?.data || {};
      if (targetUserId === currentUser.uid) {
        setUserDeleteConfirm(null);
        await signOut(auth);
        window.location.href = '/';
        return;
      }
      setUserActionNotice(
        `Felhasználó törölve: ${userItem.displayName || userItem.email || userItem.id}. Jövőbeni jelentkezések: ${data.registrationsDeleted || 0}, várólista: ${data.waitlistDeleted || 0}.`,
      );
      setUserDeleteConfirm(null);
    } catch (error) {
      setUserActionNotice(`Felhasználó törlési hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setUserDeleteBusyId('');
    }
  };

  const handleCapacityDraftChange = (trainingDate, value) => {
    setCapacityDrafts((current) => ({
      ...current,
      [trainingDate]: value,
    }));
  };

  const handleSaveTrainingCapacity = async (trainingDate) => {
    if (!currentUser || !hasOwnerAccess || !trainingDate) {
      return;
    }

    const currentCapacity = trainingWeeks[trainingDate]?.capacity || DEFAULT_TRAINING_CAPACITY;
    const draftValue = capacityDrafts[trainingDate] ?? String(currentCapacity);
    const nextCapacity = Number.parseInt(draftValue, 10);

    if (!Number.isInteger(nextCapacity) || nextCapacity < 1) {
      setTrainingCancelNotice('A maximális férőhely legalább 1 fő kell legyen.');
      return;
    }

    setCapacityBusyDate(trainingDate);
    setTrainingCancelNotice('');

    try {
      await setDoc(
        doc(db, 'training_weeks', trainingDate),
        {
          training_date: trainingDate,
          capacity: nextCapacity,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      setCapacityDrafts((current) => {
        const next = { ...current };
        delete next[trainingDate];
        return next;
      });
      setTrainingCancelNotice(`Sikeres férőhely frissítés: ${formatTrainingDate(trainingDate)} - ${nextCapacity} fő`);
    } catch (error) {
      setTrainingCancelNotice(`Férőhely mentési hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setCapacityBusyDate('');
    }
  };

  const handleTrainingCreateFieldChange = (key, value) => {
    setTrainingCreateForm((current) => ({
      ...current,
      [key]: value,
    }));
    setTrainingCreateNotice('');
  };

  const handleOpenTrainingCreate = () => {
    setTrainingCreateForm({
      date: '',
      time: DEFAULT_TRAINING_START_TIME,
      capacity: String(DEFAULT_TRAINING_CAPACITY),
    });
    setTrainingCreateNotice('');
    setTrainingCreateOpen(true);
  };

  const handleLoadMoreTrainings = () => {
    setTrainingListLimit((currentLimit) => currentLimit + TRAININGS_PAGE_INCREMENT);
  };

  const handleCreateTraining = async () => {
    if (!currentUser || !hasOwnerAccess) {
      return;
    }

    const trainingDate = trainingCreateForm.date;
    const startTime = trainingCreateForm.time || DEFAULT_TRAINING_START_TIME;
    const capacity = Number.parseInt(trainingCreateForm.capacity, 10);
    const trainingStartAt = getTrainingStartDateTime(trainingDate, startTime);

    if (!trainingDate || !trainingStartAt) {
      setTrainingCreateNotice('Add meg az edzés dátumát.');
      return;
    }

    if (!Number.isInteger(capacity) || capacity < 1) {
      setTrainingCreateNotice('A maximális létszám legalább 1 fő kell legyen.');
      return;
    }

    setTrainingCreateBusy(true);
    setTrainingCreateNotice('');

    try {
      const trainingRef = doc(db, 'training_weeks', trainingDate);
      const existingTraining = await getDoc(trainingRef);
      if (existingTraining.exists()) {
        setTrainingCreateNotice('Erre a dátumra már létezik edzés. A táblázatban tudod szerkeszteni.');
        return;
      }

      await setDoc(
        trainingRef,
        {
          training_date: trainingDate,
          start_time: startTime,
          training_start_at: Timestamp.fromDate(trainingStartAt),
          capacity,
          count: 0,
          registered_emails: [],
          waitlist_count: 0,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
      );
      await deleteDoc(doc(db, 'deleted_training_weeks', trainingDate));
      setTrainingCreateNotice('Sikeres edzés létrehozás.');
      setTrainingCreateOpen(false);
    } catch (error) {
      setTrainingCreateNotice(`Edzés létrehozási hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setTrainingCreateBusy(false);
    }
  };

  const handleFieldChange = (key, value) => {
    setSiteContentForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const handleEmailTemplateFieldChange = (key, value) => {
    setEmailTemplateForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const handleTaskFormChange = (key, value) => {
    setTaskForm((current) => ({
      ...current,
      [key]: value,
    }));
    setTaskNotice('');
  };

  const handleCreateTask = async () => {
    if (!currentUser || !hasOwnerAccess) {
      return;
    }

    const name = taskForm.name.trim();
    const category = taskForm.category.trim();
    const quantity = Number.parseFloat(String(taskForm.quantity).replace(',', '.'));
    const unit = taskForm.unit.trim();
    const taskKey = getTaskKey(name);

    if (!name) {
      setTaskNotice('A feladat neve kötelező.');
      return;
    }
    if (!category) {
      setTaskNotice('A kategória kötelező.');
      return;
    }
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setTaskNotice('A mennyiség legyen nullánál nagyobb szám.');
      return;
    }
    if (!unit) {
      setTaskNotice('A mértékegység kötelező.');
      return;
    }

    const existingTask = tasks.find((task) => task.taskKey === taskKey);
    const taskRef = doc(db, 'tasks', existingTask?.id || taskKey);
    setTaskSaveBusy(true);
    setTaskNotice('');

    try {
      await setDoc(
        taskRef,
        {
          name,
          category,
          quantity,
          unit,
          description: taskForm.description.trim(),
          task_key: taskKey,
          active: true,
          updatedAt: serverTimestamp(),
          updated_by_user_id: currentUser.uid,
          ...(!existingTask ? {
            createdAt: serverTimestamp(),
            created_by_user_id: currentUser.uid,
          } : {}),
        },
        { merge: true },
      );
      setTaskForm({
        name: '',
        category: '',
        quantity: '',
        unit: 'db',
        description: '',
      });
      setTaskNotice(existingTask ? 'Feladat frissítve.' : 'Feladat létrehozva.');
    } catch (error) {
      setTaskNotice(`Feladat mentési hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setTaskSaveBusy(false);
    }
  };

  const handleTaskImportFileChange = (file) => {
    setTaskImportFile(file || null);
    setTaskImportNotice('');
    setTaskImportErrors([]);
  };

  const handleImportTasks = async () => {
    if (!currentUser || !hasOwnerAccess) {
      return;
    }
    if (!taskImportFile) {
      setTaskImportNotice('Válassz feltöltendő CSV vagy TSV fájlt.');
      setTaskImportErrors([]);
      return;
    }

    setTaskImportBusy(true);
    setTaskImportNotice('');
    setTaskImportErrors([]);

    try {
      const isXlsxFile = taskImportFile.name?.toLowerCase().endsWith('.xlsx')
        || taskImportFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const parsedImport = isXlsxFile
        ? parseTaskImportRows(await readSheet(taskImportFile))
        : parseTaskImportFile(await taskImportFile.text());
      const { tasks: importedTasks, errors } = parsedImport;
      if (errors.length > 0) {
        setTaskImportErrors(errors);
      }
      if (importedTasks.length === 0) {
        setTaskImportNotice('Nem találtam importálható feladatot a fájlban.');
        return;
      }

      const tasksByKey = new Map();
      importedTasks.forEach((task) => {
        tasksByKey.set(getTaskKey(task.name), task);
      });
      const uniqueTasks = [...tasksByKey.entries()];
      const existingTasksByKey = new Map(tasks.map((task) => [task.taskKey, task]));
      let createdCount = 0;
      let updatedCount = 0;

      for (let start = 0; start < uniqueTasks.length; start += 450) {
        const batch = writeBatch(db);
        const taskBatch = uniqueTasks.slice(start, start + 450);
        for (const [taskKey, task] of taskBatch) {
          const existingTask = existingTasksByKey.get(taskKey);
          const taskRef = doc(db, 'tasks', existingTask?.id || taskKey);
          batch.set(
            taskRef,
            {
              name: task.name,
              category: task.category,
              quantity: task.quantity,
              unit: task.unit,
              description: task.description,
              task_key: taskKey,
              active: true,
              updatedAt: serverTimestamp(),
              updated_by_user_id: currentUser.uid,
              import_source_filename: taskImportFile.name || '',
              ...(!existingTask ? {
                createdAt: serverTimestamp(),
                created_by_user_id: currentUser.uid,
              } : {}),
            },
            { merge: true },
          );
          if (existingTask) {
            updatedCount += 1;
          } else {
            createdCount += 1;
          }
        }
        await batch.commit();
      }

      setTaskImportFile(null);
      const importParts = [`${createdCount} új feladat létrehozva`, `${updatedCount} feladat frissítve`];
      const repeatedRows = importedTasks.length - uniqueTasks.length;
      if (repeatedRows > 0) {
        importParts.push(`${repeatedRows} fájlon belüli ismétlődés összevonva`);
      }
      if (errors.length > 0) {
        importParts.push(`${errors.length} sor hibás volt`);
      }
      setTaskImportNotice(`${importParts.join(', ')}.`);
    } catch (error) {
      setTaskImportNotice(`Importálási hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setTaskImportBusy(false);
    }
  };

  const handleAddTrainingTask = async () => {
    if (!currentUser || !hasOwnerAccess) {
      return;
    }

    const task = taskById[selectedTrainingTaskId];
    const training = upcomingTaskTrainings.find((item) => item.trainingDate === selectedTaskTrainingDate);
    if (!training) {
      setTrainingTaskAssignmentNotice('Válassz aktuális vagy jövőbeli edzést.');
      return;
    }
    if (!task) {
      setTrainingTaskAssignmentNotice('Válassz hozzáadandó feladatot.');
      return;
    }

    const lastPosition = trainingTaskAssignments.reduce(
      (highestPosition, assignment) => Math.max(highestPosition, assignment.position),
      -1,
    );
    setTrainingTaskAssignmentBusyId('add');
    setTrainingTaskAssignmentNotice('');

    try {
      await addDoc(
        collection(db, 'training_weeks', selectedTaskTrainingDate, 'task_assignments'),
        {
          training_date: selectedTaskTrainingDate,
          task_id: task.id,
          task_name: task.name,
          task_category: task.category,
          quantity: task.quantity,
          unit: task.unit,
          description: task.description,
          position: lastPosition + 1,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          created_by_user_id: currentUser.uid,
        },
      );
      setTrainingTaskAssignmentNotice('Feladat hozzáadva az edzéshez.');
    } catch (error) {
      setTrainingTaskAssignmentNotice(`Feladat hozzárendelési hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setTrainingTaskAssignmentBusyId('');
    }
  };

  const handleMoveTrainingTask = async (assignmentIndex, direction) => {
    const targetIndex = assignmentIndex + direction;
    if (
      !currentUser
      || !hasOwnerAccess
      || !selectedTaskTrainingDate
      || targetIndex < 0
      || targetIndex >= trainingTaskAssignments.length
    ) {
      return;
    }

    const assignment = trainingTaskAssignments[assignmentIndex];
    const targetAssignment = trainingTaskAssignments[targetIndex];
    setTrainingTaskAssignmentBusyId(assignment.id);
    setTrainingTaskAssignmentNotice('');

    try {
      const batch = writeBatch(db);
      const assignmentsPath = ['training_weeks', selectedTaskTrainingDate, 'task_assignments'];
      batch.update(doc(db, ...assignmentsPath, assignment.id), {
        position: targetAssignment.position,
        updatedAt: serverTimestamp(),
        updated_by_user_id: currentUser.uid,
      });
      batch.update(doc(db, ...assignmentsPath, targetAssignment.id), {
        position: assignment.position,
        updatedAt: serverTimestamp(),
        updated_by_user_id: currentUser.uid,
      });
      await batch.commit();
    } catch (error) {
      setTrainingTaskAssignmentNotice(`Sorrend módosítási hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setTrainingTaskAssignmentBusyId('');
    }
  };

  const handleDeleteTrainingTask = async (assignment) => {
    if (!currentUser || !hasOwnerAccess || !selectedTaskTrainingDate || !assignment?.id) {
      return;
    }

    setTrainingTaskAssignmentBusyId(assignment.id);
    setTrainingTaskAssignmentNotice('');
    try {
      await deleteDoc(doc(
        db,
        'training_weeks',
        selectedTaskTrainingDate,
        'task_assignments',
        assignment.id,
      ));
      setTrainingTaskAssignmentNotice('Feladat eltávolítva az edzésből.');
    } catch (error) {
      setTrainingTaskAssignmentNotice(`Feladat eltávolítási hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setTrainingTaskAssignmentBusyId('');
    }
  };

  const handleExerciseFormChange = (key, value) => {
    setExerciseForm((current) => ({
      ...current,
      [key]: value,
    }));
    setExerciseNotice('');
  };

  const handleFitnessResultFormChange = (key, value) => {
    setFitnessResultForm((current) => ({
      ...current,
      [key]: value,
    }));
    setFitnessResultNotice('');
  };

  const handleCreateExercise = async () => {
    if (!currentUser || !hasOwnerAccess) {
      return;
    }

    const name = exerciseForm.name.trim();
    const repetitionCount = exerciseForm.repetitionCount === ''
      ? 0
      : Number.parseFloat(String(exerciseForm.repetitionCount).replace(',', '.'));
    const durationMinutes = Number.parseInt(exerciseForm.durationMinutes, 10);
    const durationSeconds = Number.parseInt(exerciseForm.durationSeconds, 10);

    if (!name) {
      setExerciseNotice('A gyakorlat neve kötelező.');
      return;
    }
    if (!Number.isFinite(repetitionCount) || repetitionCount < 0) {
      setExerciseNotice('Az ismétlésszám legyen 0 vagy nagyobb szám.');
      return;
    }
    if (!Number.isInteger(durationMinutes) || durationMinutes < 0 || !Number.isInteger(durationSeconds) || durationSeconds < 0 || durationSeconds > 59) {
      setExerciseNotice('Az időtartam perc és másodperc mezője legyen érvényes.');
      return;
    }

    setExerciseSaveBusy(true);
    setExerciseNotice('');

    try {
      await addDoc(collection(db, 'fitness_exercises'), {
        name,
        description: exerciseForm.description.trim(),
        repetition_count: repetitionCount,
        unit: exerciseForm.unit,
        duration_minutes: durationMinutes,
        duration_seconds: durationSeconds,
        result_unit: exerciseForm.resultUnit,
        result_direction: exerciseForm.resultDirection,
        active: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        created_by_user_id: currentUser.uid,
      });
      setExerciseForm({
        name: '',
        description: '',
        repetitionCount: '',
        unit: 'mp',
        durationMinutes: '0',
        durationSeconds: '0',
        resultUnit: 'db',
        resultDirection: 'higher',
      });
      setExerciseNotice('Gyakorlat létrehozva.');
    } catch (error) {
      setExerciseNotice(`Gyakorlat mentési hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setExerciseSaveBusy(false);
    }
  };

  const handleExerciseImportFileChange = (file) => {
    setExerciseImportFile(file || null);
    setExerciseImportNotice('');
    setExerciseImportErrors([]);
  };

  const handleImportExercises = async () => {
    if (!currentUser || !hasOwnerAccess) {
      return;
    }

    if (!exerciseImportFile) {
      setExerciseImportNotice('Válassz feltöltendő CSV vagy TSV fájlt.');
      setExerciseImportErrors([]);
      return;
    }

    setExerciseImportBusy(true);
    setExerciseImportNotice('');
    setExerciseImportErrors([]);

    try {
      const text = await exerciseImportFile.text();
      const { exercises: importedExercises, errors } = parseExerciseImportFile(text);

      if (errors.length > 0) {
        setExerciseImportErrors(errors);
      }
      if (importedExercises.length === 0) {
        setExerciseImportNotice('Nem találtam importálható gyakorlatot a fájlban.');
        return;
      }

      const existingExerciseNames = new Set(exercises.map((exercise) => normalizeImportHeader(exercise.name)));
      const importedExerciseNames = new Set();
      const uniqueExercises = importedExercises.filter((exercise) => {
        const normalizedName = normalizeImportHeader(exercise.name);
        if (existingExerciseNames.has(normalizedName) || importedExerciseNames.has(normalizedName)) {
          return false;
        }
        importedExerciseNames.add(normalizedName);
        return true;
      });
      const skippedDuplicateCount = importedExercises.length - uniqueExercises.length;

      if (uniqueExercises.length === 0) {
        setExerciseImportNotice('Nem importáltam új gyakorlatot, mert minden sor duplikált név volt.');
        return;
      }

      await Promise.all(
        uniqueExercises.map((exercise) =>
          addDoc(collection(db, 'fitness_exercises'), {
            name: exercise.name,
            description: exercise.description,
            repetition_count: exercise.repetitionCount,
            unit: exercise.unit,
            duration_minutes: exercise.durationMinutes,
            duration_seconds: exercise.durationSeconds,
            result_unit: exercise.resultUnit,
            result_direction: exercise.resultDirection,
            active: true,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            created_by_user_id: currentUser.uid,
            import_source_filename: exerciseImportFile.name || '',
          }),
        ),
      );

      setExerciseImportFile(null);
      const importParts = [`${uniqueExercises.length} gyakorlat importálva`];
      if (skippedDuplicateCount > 0) {
        importParts.push(`${skippedDuplicateCount} duplikált név kihagyva`);
      }
      if (errors.length > 0) {
        importParts.push(`${errors.length} sor hibás volt`);
      }
      setExerciseImportNotice(`${importParts.join(', ')}.`);
    } catch (error) {
      setExerciseImportNotice(`Importálási hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setExerciseImportBusy(false);
    }
  };

  const handleCreateFitnessResult = async () => {
    if (!currentUser || !hasOwnerAccess) {
      return;
    }

    const exercise = exerciseById[fitnessResultForm.exerciseId];
    const selectedUser = users.find((userItem) => (userItem.uid || userItem.id) === fitnessResultForm.userId);
    const resultValue = Number.parseFloat(String(fitnessResultForm.value).replace(',', '.'));

    if (!selectedUser) {
      setFitnessResultNotice('Válassz játékost.');
      return;
    }
    if (!exercise) {
      setFitnessResultNotice('Válassz gyakorlatot.');
      return;
    }
    if (!fitnessResultForm.trainingDate) {
      setFitnessResultNotice('Add meg az edzés dátumát.');
      return;
    }
    if (!Number.isFinite(resultValue)) {
      setFitnessResultNotice('Adj meg érvényes eredményt.');
      return;
    }

    setFitnessResultSaveBusy(true);
    setFitnessResultNotice('');

    try {
      await addDoc(collection(db, 'fitness_results'), {
        user_id: selectedUser.uid || selectedUser.id,
        user_email: selectedUser.email || '',
        user_display_name: selectedUser.displayName || selectedUser.email || '',
        exercise_id: exercise.id,
        exercise_name: exercise.name,
        result_unit: exercise.resultUnit,
        result_direction: exercise.resultDirection,
        training_date: fitnessResultForm.trainingDate,
        training_id: fitnessResultForm.trainingDate,
        result_value: resultValue,
        sort_value: getResultSortValue(resultValue, exercise.resultDirection),
        recorded_at: serverTimestamp(),
        recorded_by_user_id: currentUser.uid,
        source: 'admin',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setFitnessResultForm((current) => ({ ...current, trainingDate: getBudapestTodayKey(), value: '' }));
      setFitnessResultNotice('Eredmény rögzítve.');
    } catch (error) {
      setFitnessResultNotice(`Eredmény mentési hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setFitnessResultSaveBusy(false);
    }
  };

  const handleResultDraftChange = (resultId, value) => {
    setResultDrafts((current) => ({
      ...current,
      [resultId]: value,
    }));
  };

  const handleUpdateFitnessResult = async (item) => {
    if (!currentUser || !hasOwnerAccess || !item?.id) {
      return;
    }

    const draftValue = resultDrafts[item.id] ?? String(item.resultValue);
    const nextValue = Number.parseFloat(String(draftValue).replace(',', '.'));
    if (!Number.isFinite(nextValue)) {
      setFitnessResultNotice('Adj meg érvényes eredményt a javításhoz.');
      return;
    }

    setResultUpdateBusyId(item.id);
    setFitnessResultNotice('');

    try {
      const exercise = exerciseById[item.exerciseId];
      const resultDirection = exercise?.resultDirection || item.resultDirection || 'higher';
      await updateDoc(doc(db, 'fitness_results', item.id), {
        result_value: nextValue,
        sort_value: getResultSortValue(nextValue, resultDirection),
        result_unit: exercise?.resultUnit || item.resultUnit || '',
        result_direction: resultDirection,
        exercise_name: exercise?.name || item.exerciseName || '',
        updatedAt: serverTimestamp(),
        updated_by_user_id: currentUser.uid,
      });
      setResultDrafts((current) => {
        const next = { ...current };
        delete next[item.id];
        return next;
      });
      setFitnessResultNotice('Eredmény frissítve.');
    } catch (error) {
      setFitnessResultNotice(`Eredmény frissítési hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setResultUpdateBusyId('');
    }
  };

  const handleAdminResultsModeChange = (mode) => {
    setAdminResultsMode(mode);
    setFitnessResultNotice('');
    setAdminSquashSetNotice('');
  };

  const handleStartAdminSquashSetEdit = (set) => {
    setAdminSquashSetDrafts((current) => ({
      ...current,
      [set.id]: {
        winnerId: set.winnerId,
        loserScore: String(getSquashSetLoserScore(set)),
      },
    }));
    setAdminSquashSetNotice('');
  };

  const handleAdminSquashSetDraftChange = (setId, field, value) => {
    setAdminSquashSetDrafts((current) => ({
      ...current,
      [setId]: {
        ...(current[setId] || {}),
        [field]: value,
      },
    }));
    setAdminSquashSetNotice('');
  };

  const handleCancelAdminSquashSetEdit = (setId) => {
    setAdminSquashSetDrafts((current) => {
      const next = { ...current };
      delete next[setId];
      return next;
    });
  };

  const handleSaveAdminSquashSet = async (set) => {
    const draft = adminSquashSetDrafts[set.id];
    if (
      !draft
      || (draft.winnerId !== set.playerAId && draft.winnerId !== set.playerBId)
      || !isValidSquashSetLoserScore(draft.loserScore)
    ) {
      setAdminSquashSetNotice('Válassz győztest és adj meg érvényes vesztes pontszámot.');
      return;
    }

    setAdminSquashSetBusyId(set.id);
    setAdminSquashSetNotice('');
    try {
      const updateSet = httpsCallable(cloudFunctions, 'updateSquashSet');
      await updateSet({
        setId: set.id,
        winnerId: draft.winnerId,
        loserScore: Number(draft.loserScore),
      });
      handleCancelAdminSquashSetEdit(set.id);
      setAdminSquashSetNotice('Szett eredménye frissítve.');
    } catch (error) {
      setAdminSquashSetNotice(error?.message || 'A szett eredményének módosítása nem sikerült.');
    } finally {
      setAdminSquashSetBusyId('');
    }
  };

  const handleDeleteAdminSquashSet = async (set) => {
    if (!window.confirm('Biztosan törlöd ezt a szett eredményt?')) {
      return;
    }

    setAdminSquashSetBusyId(set.id);
    setAdminSquashSetNotice('');
    try {
      const deleteSet = httpsCallable(cloudFunctions, 'deleteSquashSet');
      await deleteSet({ setId: set.id });
      setAdminSquashSetNotice('Szett eredménye törölve.');
    } catch (error) {
      setAdminSquashSetNotice(error?.message || 'A szett eredményének törlése nem sikerült.');
    } finally {
      setAdminSquashSetBusyId('');
    }
  };

  const handleBackgroundFileSelect = (fieldKey, file) => {
    setBackgroundFiles((current) => ({
      ...current,
      [fieldKey]: file || null,
    }));
    setUploadNotice('');
  };

  const handleImagePositionChange = (fieldKey, axis, value, index = null) => {
    setSiteContentForm((current) => {
      if (fieldKey === 'heroBackgroundPositions') {
        const positions = [...(Array.isArray(current.heroBackgroundPositions) ? current.heroBackgroundPositions : [])];
        positions[index] = updateImagePositionAxis(positions[index], axis, value);
        return {
          ...current,
          heroBackgroundPositions: positions,
        };
      }

      return {
        ...current,
        [fieldKey]: updateImagePositionAxis(current[fieldKey], axis, value),
      };
    });
    setUploadNotice('');
  };

  const handleImagePositionSave = async (fieldKey) => {
    if (!currentUser || !hasOwnerAccess) {
      setUploadNotice('A mentéshez admin jogosultság szükséges.');
      return;
    }

    setUploadBusyKey(fieldKey);
    setUploadNotice('');

    try {
      const value = fieldKey === 'heroBackgroundPositions'
        ? heroSlideshowUrls.map((_, index) => normalizeImagePosition(siteContentForm.heroBackgroundPositions?.[index]))
        : normalizeImagePosition(siteContentForm[fieldKey]);
      const contentRef = doc(db, 'site_content', 'main');
      await setDoc(
        contentRef,
        {
          [fieldKey]: value,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser.uid,
        },
        { merge: true },
      );
      setSiteContentForm((current) => ({
        ...current,
        [fieldKey]: value,
      }));
      setUploadNotice('Kép pozíciója elmentve.');
    } catch (error) {
      setUploadNotice(`Pozíció mentési hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setUploadBusyKey('');
    }
  };

  const handleBackgroundUpload = async (fieldKey) => {
    if (!currentUser || !hasOwnerAccess) {
      setUploadNotice('A feltöltéshez admin jogosultság szükséges.');
      return;
    }

    const selectedFile = backgroundFiles[fieldKey];
    if (!selectedFile) {
      setUploadNotice('Előbb válassz képfájlt a feltöltéshez.');
      return;
    }

    if (!selectedFile.type || !selectedFile.type.startsWith('image/')) {
      setUploadNotice('Csak képfájl tölthető fel.');
      return;
    }

    setUploadBusyKey(fieldKey);
    setUploadNotice('');

    try {
      const safeFilename = sanitizeFilename(selectedFile.name);
      const storagePath = `site_backgrounds/${fieldKey}/${Date.now()}-${safeFilename}`;
      const imageRef = ref(storage, storagePath);

      await uploadBytes(imageRef, selectedFile, {
        contentType: selectedFile.type,
      });

      const downloadUrl = await getDownloadURL(imageRef);
      const contentRef = doc(db, 'site_content', 'main');
      await setDoc(
        contentRef,
        {
          [fieldKey]: downloadUrl,
          [`${fieldKey.replace(/Url$/, '')}Position`]: siteContentForm[`${fieldKey.replace(/Url$/, '')}Position`] || '50% 50%',
          updatedAt: serverTimestamp(),
          updatedBy: currentUser.uid,
        },
        { merge: true },
      );

      setSiteContentForm((current) => ({
        ...current,
        [fieldKey]: downloadUrl,
      }));
      setBackgroundFiles((current) => ({
        ...current,
        [fieldKey]: null,
      }));
      setUploadNotice('Háttérkép sikeresen feltöltve és elmentve.');
    } catch (error) {
      setUploadNotice(`Feltöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setUploadBusyKey('');
    }
  };

  const handleHeroSlideUpload = async () => {
    const fieldKey = 'heroBackgroundUrls';
    if (!currentUser || !hasOwnerAccess) {
      setUploadNotice('A feltöltéshez admin jogosultság szükséges.');
      return;
    }

    const selectedFile = backgroundFiles[fieldKey];
    if (!selectedFile) {
      setUploadNotice('Előbb válassz képfájlt a feltöltéshez.');
      return;
    }

    if (!selectedFile.type || !selectedFile.type.startsWith('image/')) {
      setUploadNotice('Csak képfájl tölthető fel.');
      return;
    }

    setUploadBusyKey(fieldKey);
    setUploadNotice('');

    try {
      const safeFilename = sanitizeFilename(selectedFile.name);
      const storagePath = `site_backgrounds/${fieldKey}/${Date.now()}-${safeFilename}`;
      const imageRef = ref(storage, storagePath);

      await uploadBytes(imageRef, selectedFile, {
        contentType: selectedFile.type,
      });

      const downloadUrl = await getDownloadURL(imageRef);
      const nextUrls = getImageUrls(heroSlideshowUrls, downloadUrl);
      const nextPositions = [
        ...(Array.isArray(siteContentForm.heroBackgroundPositions) ? siteContentForm.heroBackgroundPositions : []),
        '50% 50%',
      ].slice(0, nextUrls.length);
      const contentRef = doc(db, 'site_content', 'main');
      await setDoc(
        contentRef,
        {
          heroBackgroundUrls: nextUrls,
          heroBackgroundPositions: nextPositions,
          heroBackgroundUrl: nextUrls[0] || '',
          updatedAt: serverTimestamp(),
          updatedBy: currentUser.uid,
        },
        { merge: true },
      );

      setSiteContentForm((current) => ({
        ...current,
        heroBackgroundUrls: nextUrls,
        heroBackgroundPositions: nextPositions,
        heroBackgroundUrl: nextUrls[0] || '',
      }));
      setBackgroundFiles((current) => ({
        ...current,
        [fieldKey]: null,
      }));
      setUploadNotice('Háttérkép sikeresen feltöltve és elmentve.');
    } catch (error) {
      setUploadNotice(`Feltöltési hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setUploadBusyKey('');
    }
  };

  const handleHeroSlideRemove = async (urlToRemove) => {
    if (!currentUser || !hasOwnerAccess) {
      setUploadNotice('A törléshez admin jogosultság szükséges.');
      return;
    }

    setUploadBusyKey(urlToRemove);
    setUploadNotice('');

    try {
      const nextUrls = heroSlideshowUrls.filter((url) => url !== urlToRemove);
      const removeIndex = heroSlideshowUrls.findIndex((url) => url === urlToRemove);
      const currentPositions = Array.isArray(siteContentForm.heroBackgroundPositions)
        ? siteContentForm.heroBackgroundPositions
        : [];
      const nextPositions = currentPositions.filter((_, index) => index !== removeIndex).slice(0, nextUrls.length);
      const contentRef = doc(db, 'site_content', 'main');
      await setDoc(
        contentRef,
        {
          heroBackgroundUrls: nextUrls,
          heroBackgroundPositions: nextPositions,
          heroBackgroundUrl: nextUrls[0] || '',
          updatedAt: serverTimestamp(),
          updatedBy: currentUser.uid,
        },
        { merge: true },
      );
      setSiteContentForm((current) => ({
        ...current,
        heroBackgroundUrls: nextUrls,
        heroBackgroundPositions: nextPositions,
        heroBackgroundUrl: nextUrls[0] || '',
      }));
      setUploadNotice('Háttérkép URL törölve.');
    } catch (error) {
      setUploadNotice(`Törlési hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setUploadBusyKey('');
    }
  };

  const handleBackgroundClear = async (fieldKey) => {
    if (!currentUser || !hasOwnerAccess) {
      setUploadNotice('A törléshez admin jogosultság szükséges.');
      return;
    }

    setUploadBusyKey(fieldKey);
    setUploadNotice('');

    try {
      const contentRef = doc(db, 'site_content', 'main');
      await setDoc(
        contentRef,
        {
          [fieldKey]: '',
          [`${fieldKey.replace(/Url$/, '')}Position`]: '50% 50%',
          updatedAt: serverTimestamp(),
          updatedBy: currentUser.uid,
        },
        { merge: true },
      );
      setSiteContentForm((current) => ({
        ...current,
        [fieldKey]: '',
        [`${fieldKey.replace(/Url$/, '')}Position`]: '50% 50%',
      }));
      setBackgroundFiles((current) => ({
        ...current,
        [fieldKey]: null,
      }));
      setUploadNotice('Háttérkép URL törölve.');
    } catch (error) {
      setUploadNotice(`Törlési hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setUploadBusyKey('');
    }
  };

  const handleSaveContent = async () => {
    if (!currentUser || !hasOwnerAccess) {
      setSaveNotice('A mentéshez admin jogosultság szükséges.');
      return;
    }

    setSaveBusy(true);
    setSaveNotice('');

    try {
      const contentRef = doc(db, 'site_content', 'main');
      await setDoc(
        contentRef,
        {
          ...siteContentForm,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser.uid,
        },
        { merge: true },
      );
      setSaveNotice('Sikeres mentés. A főoldal azonnal frissül.');
    } catch (error) {
      setSaveNotice(`Mentési hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setSaveBusy(false);
    }
  };

  const handleSaveEmailTemplates = async () => {
    if (!currentUser || !hasOwnerAccess) {
      setEmailTemplateNotice('A mentéshez admin jogosultság szükséges.');
      return;
    }

    setEmailTemplateSaveBusy(true);
    setEmailTemplateNotice('');

    try {
      const templatesRef = doc(db, 'email_templates', 'main');
      await setDoc(
        templatesRef,
        {
          ...emailTemplateForm,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser.uid,
        },
        { merge: true },
      );
      setEmailTemplateNotice('Sikeres mentés. A következő e-mailek már ezeket a sablonokat használják.');
    } catch (error) {
      setEmailTemplateNotice(`E-mail sablon mentési hiba: ${error?.message || 'ismeretlen hiba'}`);
    } finally {
      setEmailTemplateSaveBusy(false);
    }
  };

  const renderImagePositionControls = ({ position, onChange, onSave, busyKey, disabled = false }) => (
    <div className="admin-image-position-editor">
      <div className="admin-image-position-row">
        <label>
          <span>Vízszintes fókusz</span>
          <input
            type="range"
            min="0"
            max="100"
            value={getPositionAxisValue(position, 'x')}
            onChange={(event) => onChange('x', event.target.value)}
            disabled={disabled}
          />
        </label>
        <strong>{getPositionAxisValue(position, 'x')}%</strong>
      </div>
      <div className="admin-image-position-row">
        <label>
          <span>Függőleges fókusz</span>
          <input
            type="range"
            min="0"
            max="100"
            value={getPositionAxisValue(position, 'y')}
            onChange={(event) => onChange('y', event.target.value)}
            disabled={disabled}
          />
        </label>
        <strong>{getPositionAxisValue(position, 'y')}%</strong>
      </div>
      <button
        type="button"
        className="button-soft"
        onClick={onSave}
        disabled={disabled || uploadBusyKey === busyKey}
      >
        <Save size={16} />
        {uploadBusyKey === busyKey ? 'Mentés...' : 'Pozíció mentése'}
      </button>
    </div>
  );

  const useCenteredAdminLayout = currentUser && !claimsLoading && hasOwnerAccess && centeredAdminViews.includes(activeView);

  return (
    <div className="admin-shell">
      <header className="admin-topbar">
        <button className="brand" onClick={() => (window.location.href = '/?home=1')}>
          PNG<span>SQUASH</span>
        </button>

        <div className="admin-topbar-center">
          <div className="admin-view-switch">
            <button
              type="button"
              className={`admin-view-tab ${activeView === 'monitor' ? 'active' : ''}`}
              onClick={() => setActiveView('monitor')}
              disabled={!currentUser || claimsLoading || !hasOwnerAccess}
            >
              Jelentkezés monitor
            </button>
            <button
              type="button"
              className={`admin-view-tab ${activeView === 'trainings' ? 'active' : ''}`}
              onClick={() => setActiveView('trainings')}
              disabled={!currentUser || claimsLoading || !hasOwnerAccess}
            >
              Edzések
            </button>
            <button
              type="button"
              className={`admin-view-tab ${activeView === 'tasks' ? 'active' : ''}`}
              onClick={() => setActiveView('tasks')}
              disabled={!currentUser || claimsLoading || !hasOwnerAccess}
            >
              Feladatok
            </button>
            <button
              type="button"
              className={`admin-view-tab ${activeView === 'billing' ? 'active' : ''}`}
              onClick={() => setActiveView('billing')}
              disabled={!currentUser || claimsLoading || !hasOwnerAccess}
            >
              Számlázás
            </button>
            <button
              type="button"
              className={`admin-view-tab ${activeView === 'results' ? 'active' : ''}`}
              onClick={() => setActiveView('results')}
              disabled={!currentUser || claimsLoading || !hasOwnerAccess}
            >
              Eredmények
            </button>
            <button
              type="button"
              className={`admin-view-tab ${activeView === 'content' ? 'active' : ''}`}
              onClick={() => setActiveView('content')}
              disabled={!currentUser || claimsLoading || !hasOwnerAccess}
            >
              Weboldal szövegek
            </button>
            <button
              type="button"
              className={`admin-view-tab ${activeView === 'administration' ? 'active' : ''}`}
              onClick={() => setActiveView('administration')}
              disabled={!currentUser || claimsLoading || !hasOwnerAccess}
            >
              Adminisztráció
            </button>
            <button
              type="button"
              className={`admin-view-tab ${activeView === 'emailTemplates' ? 'active' : ''}`}
              onClick={() => setActiveView('emailTemplates')}
              disabled={!currentUser || claimsLoading || !hasOwnerAccess}
            >
              E-mail sablonok
            </button>
            <button
              type="button"
              className={`admin-view-tab ${activeView === 'users' ? 'active' : ''}`}
              onClick={() => setActiveView('users')}
              disabled={!currentUser || claimsLoading || !hasOwnerAccess}
            >
              Felhasználók
            </button>
          </div>
        </div>

        <div className="admin-topbar-actions">
          {currentUser ? (
            <>
              <span className="admin-user">{currentUser.displayName || currentUser.email}</span>
              <button className="button-soft" onClick={handleLogout}>
                <LogOut size={16} />
                Kijelentkezés
              </button>
            </>
          ) : (
            <button className="button-primary" onClick={handleGoogleLogin} disabled={authBusy}>
              <LogIn size={16} />
              {authBusy ? 'Belépés...' : 'Belépés Google-lel'}
            </button>
          )}
        </div>
      </header>

      <main className={`admin-main container ${useCenteredAdminLayout ? 'admin-main-wide' : ''}`}>
        <section className="card card-large admin-intro">
            <h1>Admin felület</h1>
          <p>Itt monitorozhatod a jelentkezéseket, szerkesztheted a honlap szövegeit és az adminisztratív oldalakat.</p>
          {authNotice && <p className="registration-notice error">{authNotice}</p>}
        </section>

        {!currentUser && (
          <section className="card card-large admin-warning">
            <h2>Belépés szükséges</h2>
            <p>Az admin oldal használatához jelentkezz be Google fiókkal vagy e-mail címmel.</p>
            <div className="profile-auth-actions">
              <button className="button-primary" onClick={handleGoogleLogin} disabled={authBusy}>
                <LogIn size={16} />
                {authBusy ? 'Belépés...' : 'Belépés Google-lel'}
              </button>
              <form className="auth-form profile-auth-form" onSubmit={handleEmailLogin}>
                <label className="auth-field">
                  <span>E-mail cím</span>
                  <input
                    className="auth-input"
                    type="email"
                    value={authEmail}
                    onChange={(event) => setAuthEmail(event.target.value)}
                    autoComplete="email"
                    required
                  />
                </label>
                <label className="auth-field">
                  <span>Jelszó</span>
                  <input
                    className="auth-input"
                    type="password"
                    value={authPassword}
                    onChange={(event) => setAuthPassword(event.target.value)}
                    autoComplete="current-password"
                    required
                  />
                </label>
                <button className="button-primary" type="submit" disabled={authBusy}>
                  <LogIn size={16} />
                  Belépés e-maillel
                </button>
                <button className="button-soft" type="button" onClick={handlePasswordReset} disabled={authBusy}>
                  Elfelejtett jelszó
                </button>
              </form>
            </div>
          </section>
        )}

        {currentUser && claimsLoading && (
          <section className="card card-large admin-warning">
            <h2>Jogosultság ellenőrzése...</h2>
          </section>
        )}

        {currentUser && !claimsLoading && !hasOwnerAccess && (
          <section className="card card-large admin-warning">
            <h2>Nincs admin jogosultságod</h2>
            <p>
              Az admin felülethez a felhasználódon `admin: true` custom claim szükséges.
            </p>
          </section>
        )}

        {currentUser && !claimsLoading && hasOwnerAccess && activeView === 'monitor' && (
          <section className="card card-large admin-panel admin-monitor-panel">
            <div className="admin-monitor-head">
              <h2>Jelentkezések monitor</h2>
              <div className="admin-monitor-filter" role="group" aria-label="Edzések időszak szűrés">
                <button
                  type="button"
                  className={`admin-filter-tab ${registrationPeriod === 'future' ? 'active' : ''}`}
                  onClick={() => setRegistrationPeriod('future')}
                >
                  Jövőbeli edzések
                </button>
                <button
                  type="button"
                  className={`admin-filter-tab ${registrationPeriod === 'past' ? 'active' : ''}`}
                  onClick={() => setRegistrationPeriod('past')}
                >
                  Múltbeli edzések
                </button>
              </div>
            </div>
            {registrationsLoading || waitlistLoading ? (
              <p>Jelentkezések betöltése...</p>
            ) : groupedRegistrations.length === 0 ? (
              <p>
                {registrationPeriod === 'future'
                  ? 'Nincs jövőbeli jelentkezési adat.'
                  : 'Nincs múltbeli jelentkezési adat.'}
              </p>
            ) : (
              <div className="admin-registrations">
                {registrationActionNotice && (
                  <p className={`registration-notice ${registrationActionNotice.startsWith('Sikeres') ? 'ok' : 'error'}`}>
                    {registrationActionNotice}
                  </p>
                )}
                {groupedRegistrations.map(({ trainingDate, items, regularCount, lateCancelledCount, waitlistCount, capacity, cancelledByAdmin }) => {
                  const capacityDraft = capacityDrafts[trainingDate] ?? String(capacity);
                  const isCapacityBusy = capacityBusyDate === trainingDate;
                  const training = trainingWeeks[trainingDate] || {};
                  const trainingEndAt = getTrainingEndForWeek(trainingDate, training);
                  const hasTrainingEnded = !trainingEndAt || currentTime.getTime() >= trainingEndAt.getTime();
                  const occupiedUserIds = new Set(items.map((item) => item.userId).filter(Boolean));
                  const availableUsers = users.filter((user) => !occupiedUserIds.has(user.uid));
                  const selectedAddUserId = registrationAddUserDrafts[trainingDate] || '';
                  const isRegistrationAddBusy = registrationAddBusyDate === trainingDate;
                  const occupiedCapacity = regularCount + (training.waitlistReservedCount || 0);
                  const isRegistrationAddDisabled = cancelledByAdmin
                    || hasTrainingEnded
                    || occupiedCapacity >= capacity
                    || usersLoading
                    || isRegistrationAddBusy;
                  const billingPreview = buildTrainingBillingPreview(trainingDate, items);
                  const existingBillingRun = billingRuns[trainingDate];
                  const existingBillingItems = billingItemsByTrainingDate[trainingDate] || [];
                  const displayedClubCourtFeePerPersonHuf = existingBillingRun?.clubCourtFeePerPersonHuf
                    ?? billingPreview.clubCourtFeePerPersonHuf;
                  const displayedClubCourtFeeParticipantCount = existingBillingRun?.clubCourtFeeParticipantCount
                    ?? billingPreview.clubCourtFeeParticipantCount;
                  const displayedClubCourtCostHuf = existingBillingRun?.clubCourtCostHuf
                    ?? billingPreview.clubCourtCostHuf;
                  const billingCategoryLabel = billingPreview.category
                    ? `${billingPreview.category.minParticipants}-${billingPreview.category.maxParticipants}${billingPreview.participantCount > billingPreview.category.maxParticipants ? '+' : ''} fő`
                    : '-';
                  const billingRowByRegistrationId = Object.fromEntries(
                    billingPreview.rows.map((row) => [row.id, row]),
                  );
                  const isBillingRunProcessing = existingBillingRun?.status === 'billingo_processing';
                  const isBillingRunFinished = existingBillingRun?.status === 'billingo_finished';
                  const billingButtonLabel = isBillingRunProcessing
                    ? 'Küldés...'
                    : existingBillingRun
                      ? existingBillingRun.status === 'billingo_finished_with_errors' || existingBillingRun.status === 'billingo_failed'
                        ? 'Billingo újrapróbálás'
                        : 'Billingo küldés indítása'
                      : 'Számlázás indítása';

                  return (
                  <div className="admin-registration-group" key={trainingDate}>
                    <h3>
                      <span className="admin-registration-heading">
                        {formatTrainingDate(trainingDate)}
                        <label className="admin-capacity-control">
                          <span>Maximális férőhely:</span>
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={capacityDraft}
                            onChange={(event) => handleCapacityDraftChange(trainingDate, event.target.value)}
                            disabled={isCapacityBusy}
                          />
                          <button
                            type="button"
                            className="button-soft admin-capacity-save"
                            onClick={() => handleSaveTrainingCapacity(trainingDate)}
                            disabled={isCapacityBusy || Number.parseInt(capacityDraft, 10) === capacity}
                          >
                            {isCapacityBusy ? 'Mentés...' : 'Mentés'}
                          </button>
                        </label>
                        <span>
                          ({regularCount} aktív fő{lateCancelledCount > 0 ? ` + ${lateCancelledCount} késői lemondó` : ''}{waitlistCount > 0 ? ` + ${waitlistCount} várólistás` : ''})
                        </span>
                        {cancelledByAdmin && (
                          <span className="admin-training-status">Lemondva a szervező által</span>
                        )}
                      </span>
                    </h3>
                    <div className="admin-registration-add-control">
                      <div>
                        <strong>Felhasználó hozzáadása</strong>
                        <span>
                          {cancelledByAdmin
                            ? 'A szervező által lemondott edzéshez nem adható hozzá felhasználó.'
                            : hasTrainingEnded
                              ? 'Az edzés befejeződött.'
                              : occupiedCapacity >= capacity
                                ? 'Az edzés betelt. A hozzáadáshoz előbb emeld meg a maximális férőhelyet.'
                                : `Hozzáadás az edzés végéig (${formatDateTimeInBudapest(trainingEndAt, { hour: '2-digit', minute: '2-digit' })}).`}
                        </span>
                      </div>
                      <div className="admin-registration-add-fields">
                        <select
                          className="admin-inline-select"
                          value={selectedAddUserId}
                          onChange={(event) => {
                            setRegistrationAddUserDrafts((current) => ({
                              ...current,
                              [trainingDate]: event.target.value,
                            }));
                            setRegistrationActionNotice('');
                          }}
                          disabled={isRegistrationAddDisabled}
                          aria-label={`Felhasználó kiválasztása: ${formatTrainingDate(trainingDate)}`}
                        >
                          <option value="">{usersLoading ? 'Felhasználók betöltése...' : 'Válassz felhasználót'}</option>
                          {availableUsers.map((user) => (
                            <option
                              key={user.uid}
                              value={user.uid}
                              disabled={user.trainingRegistrationDisabledByAdmin}
                            >
                              {user.displayName || user.email || user.uid}
                              {user.email && user.displayName ? ` (${user.email})` : ''}
                              {user.trainingRegistrationDisabledByAdmin ? ' - letiltva' : ''}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="button-primary admin-registration-add-button"
                          onClick={() => handleAddRegistration(trainingDate)}
                          disabled={isRegistrationAddDisabled || !selectedAddUserId}
                        >
                          <Plus size={16} aria-hidden="true" />
                          {isRegistrationAddBusy ? 'Hozzáadás...' : 'Hozzáadás'}
                        </button>
                      </div>
                    </div>
                    <div className="admin-billing-monitor-summary">
                      <div>
                        <strong>Számlázási létszám: {billingPreview.participantCount} fő</strong>
                        <span>
                          Kategória: {billingCategoryLabel}; számlára előkészítve: {formatCurrency(billingPreview.invoiceTotalHuf)};
                          egyenlegből rendezendő: {formatCurrency(billingPreview.balanceDeductionTotalHuf)}
                        </span>
                        <span className="admin-club-court-cost">
                          <strong>Klubnak fizetendő pályaköltség: {formatCurrency(displayedClubCourtCostHuf)}</strong>
                          <span className="admin-club-court-cost-detail">
                            Pályaköltség fejenként: <strong>{formatCurrency(displayedClubCourtFeePerPersonHuf)}/fő</strong>
                            {' '}({displayedClubCourtFeeParticipantCount} teljes díjas fő)
                          </span>
                        </span>
                        {existingBillingRun && (
                          <span>
                            Futás rögzítve: {existingBillingRun.invoiceCount} számla,
                            {' '}{existingBillingRun.balanceCoveredCount} egyenlegből,
                            {' '}{existingBillingRun.failedCount} hibás tétel
                            {existingBillingRun.billingoSentCount > 0 && `; Billingo kiküldve: ${existingBillingRun.billingoSentCount}`}
                            {existingBillingRun.billingoFailedCount > 0 && `; Billingo hiba: ${existingBillingRun.billingoFailedCount}`}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        className="button-primary admin-start-billing-button"
                        onClick={() => handleStartTrainingBilling(trainingDate, items)}
                        disabled={
                          cancelledByAdmin
                          || isBillingRunProcessing
                          || isBillingRunFinished
                          || billingPreview.participantCount < 2
                          || billingStartBusyDate === trainingDate
                        }
                      >
                        {billingStartBusyDate === trainingDate ? 'Számlázás...' : billingButtonLabel}
                      </button>
                    </div>
                    {items.length === 0 ? (
                      <p className="admin-registration-empty">Még nincs jelentkező erre az alkalomra.</p>
                    ) : (
                      <div className="admin-table-wrap">
                        <table className="admin-table">
                          <colgroup>
                            <col className="admin-table-col-name" />
                            <col className="admin-table-col-email" />
                            <col className="admin-table-col-time" />
                            <col className="admin-table-col-late-cancel" />
                            <col className="admin-table-col-waitlist" />
                            <col className="admin-table-col-payment" />
                            <col className="admin-table-col-billing" />
                            <col className="admin-table-col-actions" />
                          </colgroup>
                          <thead>
                            <tr>
                              <th>Jelentkező</th>
                              <th>Email</th>
                              <th>Jelentkezés ideje</th>
                              <th>Késői lemondás</th>
                              <th>Várólista</th>
                              <th>Fizetés</th>
                              <th>Számlázás</th>
                              <th>Műveletek</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((item) => {
                              const billingRow = billingRowByRegistrationId[item.id];
                              const persistedBillingItem = existingBillingItems.find((billingItem) => billingItem.id.endsWith(`_${item.userId || item.id}`));
                              const displayedBillingStatus = persistedBillingItem?.status || billingRow?.status || '';
                              const displayedBillingAmount = persistedBillingItem?.amountHuf ?? billingRow?.amountHuf ?? 0;

                              return (
                              <tr
                                key={`${item.waitlisted ? 'waitlist' : 'registration'}-${item.id}`}
                                className={item.waitlisted ? 'admin-registration-row waitlisted' : 'admin-registration-row'}
                              >
                                <td>{item.userDisplayName || item.userEmail || item.userId || 'Ismeretlen'}</td>
                                <td>{item.userEmail || '-'}</td>
                                <td>{formatRegistrationTimestamp(item.createdAt)}</td>
                                <td>{item.lateCancelled ? 'X' : ''}</td>
                                <td>{item.waitlisted ? 'X' : ''}</td>
                                <td>
                                  {item.waitlisted ? '-' : (
                                    <select
                                      className="admin-inline-select admin-payment-method-select"
                                      value={item.billingPaymentMethod}
                                      onChange={(event) => handleRegistrationPaymentMethodChange(item, event.target.value)}
                                      disabled={paymentMethodBusyId === item.id || Boolean(existingBillingRun)}
                                    >
                                      {paymentMethodOptions.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                      ))}
                                    </select>
                                  )}
                                </td>
                                <td>
                                  {item.waitlisted ? '-' : (
                                    <div className="admin-billing-cell">
                                      <strong>{formatCurrency(displayedBillingAmount)}</strong>
                                      <span>{displayedBillingStatus === 'ready_for_billingo'
                                        ? 'Számlázandó'
                                        : displayedBillingStatus === 'billingo_processing'
                                          ? 'Billingo feldolgozás'
                                          : displayedBillingStatus === 'billingo_created'
                                            ? 'Számla létrehozva'
                                            : displayedBillingStatus === 'billingo_sent'
                                              ? 'Kiküldve'
                                              : displayedBillingStatus === 'billingo_failed'
                                                ? 'Billingo hiba'
                                                : displayedBillingStatus === 'billingo_created_send_failed'
                                                  ? 'Küldési hiba'
                                        : displayedBillingStatus === 'charged_to_balance'
                                          ? 'Egyenlegre terhelve'
                                          : displayedBillingStatus === 'paid_from_balance'
                                            ? 'Egyenlegből'
                                            : displayedBillingStatus === 'needs_billing_data'
                                              ? 'Hiányos adat'
                                              : displayedBillingStatus === 'skipped_no_invoice'
                                                ? 'Nem számlázandó'
                                                : '-'}</span>
                                    </div>
                                  )}
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="button-soft admin-row-action-button"
                                    onClick={() => handleRemoveRegistration(item)}
                                    disabled={registrationRemoveBusyId === item.id}
                                  >
                                    {registrationRemoveBusyId === item.id ? 'Törlés...' : 'Törlés'}
                                  </button>
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {currentUser && !claimsLoading && hasOwnerAccess && activeView === 'trainings' && (
          <section className="card card-large admin-panel admin-trainings-panel">
            <div className="admin-monitor-head">
              <div>
                <h2>Edzések</h2>
                <p className="admin-panel-subtitle">
                  Itt hozhatsz létre ad-hoc edzéseket és módosíthatod az alkalmak maximális létszámát.
                </p>
              </div>
              <button type="button" className="button-primary" onClick={handleOpenTrainingCreate}>
                Edzés létrehozása
              </button>
            </div>

            {trainingCreateNotice && (
              <p className={`registration-notice ${trainingCreateNotice.startsWith('Sikeres') ? 'ok' : 'error'}`}>
                {trainingCreateNotice}
              </p>
            )}

            {trainingCancelNotice && (
              <p className={`registration-notice ${trainingCancelNotice.startsWith('Sikeres') ? 'ok' : 'error'}`}>
                {trainingCancelNotice}
              </p>
            )}

            {trainingListNotice && (
              <p className="registration-notice error">{trainingListNotice}</p>
            )}

            {trainingListLoading && trainingRows.length === 0 ? (
              <p className="admin-registration-empty">Edzések betöltése...</p>
            ) : trainingRows.length === 0 ? (
              <p className="admin-registration-empty">Még nincs létrehozott edzésalkalom.</p>
            ) : (
              <>
                <div className="admin-table-wrap">
                  <table className="admin-table admin-trainings-table">
                    <colgroup>
                      <col className="admin-trainings-col-date" />
                      <col className="admin-trainings-col-time" />
                      <col className="admin-trainings-col-capacity" />
                      <col className="admin-trainings-col-created" />
                      <col className="admin-trainings-col-status" />
                      <col className="admin-trainings-col-actions" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>Dátum</th>
                        <th>Időpont</th>
                        <th>Maximális létszám</th>
                        <th>Létrehozás ideje</th>
                        <th>Állapot</th>
                        <th>Műveletek</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trainingRows.map((training) => {
                        const capacityDraft = capacityDrafts[training.trainingDate] ?? String(training.capacity);
                        const isCapacityBusy = capacityBusyDate === training.trainingDate;
                        const isCancelBusy = trainingCancelBusy === training.trainingDate;
                        const isRestoreBusy = trainingRestoreBusy === training.trainingDate;
                        const isDeleteBusy = trainingDeleteBusy === training.trainingDate;
                        const matchingRegistrations = registrations.filter((item) => item.trainingDate === training.trainingDate);
                        const liveRegularCount = matchingRegistrations.filter((item) => !item.lateCancelled).length;
                        const liveLateCancelledCount = matchingRegistrations.filter((item) => item.lateCancelled).length;
                        const liveWaitlistCount = waitlistEntries.filter((item) => item.trainingDate === training.trainingDate).length;
                        const deleteRegularCount = registrationsLoading ? (training.count || 0) : liveRegularCount;
                        const deleteLateCancelledCount = registrationsLoading ? 0 : liveLateCancelledCount;
                        const deleteWaitlistCount = waitlistLoading ? (training.waitlistCount || 0) : liveWaitlistCount;
                        const deleteBlockedMessage = getTrainingDeleteBlockedMessage({
                          regularCount: deleteRegularCount,
                          lateCancelledCount: deleteLateCancelledCount,
                          waitlistCount: deleteWaitlistCount,
                          loading: registrationsLoading || waitlistLoading,
                        });
                        const canDeleteTraining = !deleteBlockedMessage;

                        return (
                          <tr key={training.id || training.trainingDate}>
                            <td>{formatTrainingDate(training.trainingDate)}</td>
                            <td>{training.timeLabel}</td>
                            <td>
                              <div className="admin-table-capacity-edit">
                                <input
                                  type="number"
                                  min="1"
                                  step="1"
                                  value={capacityDraft}
                                  onChange={(event) => handleCapacityDraftChange(training.trainingDate, event.target.value)}
                                  disabled={isCapacityBusy}
                                />
                                <button
                                  type="button"
                                  className="button-soft admin-capacity-save"
                                  onClick={() => handleSaveTrainingCapacity(training.trainingDate)}
                                  disabled={isCapacityBusy || Number.parseInt(capacityDraft, 10) === training.capacity}
                                >
                                  {isCapacityBusy ? 'Mentés...' : 'Mentés'}
                                </button>
                              </div>
                            </td>
                            <td>{formatRegistrationTimestamp(training.createdAt)}</td>
                            <td>{training.statusLabel}</td>
                            <td>
                              <div className="admin-table-actions">
                                {training.cancelledByAdmin ? (
                                  <button
                                    type="button"
                                    className="button-soft admin-cancel-training-button"
                                    onClick={() => handleRestoreTraining(training.trainingDate)}
                                    disabled={isRestoreBusy}
                                  >
                                    {isRestoreBusy ? 'Visszavonás...' : 'Lemondás visszavonása'}
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="button-soft admin-cancel-training-button"
                                    onClick={() => setTrainingCancelDate(training.trainingDate)}
                                    disabled={isCancelBusy}
                                  >
                                    {isCancelBusy ? 'Lemondás...' : 'Lemondás'}
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="button-soft admin-cancel-training-button"
                                  onClick={() => {
                                    if (!canDeleteTraining) {
                                      setTrainingCancelNotice(deleteBlockedMessage);
                                      return;
                                    }
                                    setTrainingDeleteDate(training.trainingDate);
                                  }}
                                  disabled={isDeleteBusy}
                                  title={canDeleteTraining ? 'Edzés törlése' : deleteBlockedMessage}
                                >
                                  {isDeleteBusy ? 'Törlés...' : 'Törlés'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {hasMoreTrainingRows && (
                  <div className="admin-load-more-row">
                    <button
                      type="button"
                      className="button-soft"
                      onClick={handleLoadMoreTrainings}
                      disabled={trainingListLoading}
                    >
                      {trainingListLoading ? 'Betöltés...' : 'Továbbiak betöltése'}
                    </button>
                  </div>
                )}
              </>
            )}
          </section>
        )}

        {currentUser && !claimsLoading && hasOwnerAccess && activeView === 'tasks' && (
          <section className="card card-large admin-panel admin-tasks-panel">
            <div className="admin-monitor-head">
              <div>
                <h2>Feladatok</h2>
                <p className="admin-panel-subtitle">
                  Az edzésekhez használható feladatok kezelése, eredményrögzítés nélkül.
                </p>
              </div>
            </div>

            <div className="admin-results-grid admin-tasks-grid">
              <section className="admin-content-section">
                <div className="admin-content-section-head">
                  <h3>Új feladat</h3>
                  <p>Azonos név megadásakor a már létező feladat adatai frissülnek.</p>
                </div>
                <form
                  className="admin-form admin-form-compact"
                  onSubmit={(event) => {
                    event.preventDefault();
                    handleCreateTask();
                  }}
                >
                  <label className="admin-field">
                    <span>Név</span>
                    <input
                      type="text"
                      value={taskForm.name}
                      onChange={(event) => handleTaskFormChange('name', event.target.value)}
                    />
                  </label>
                  <label className="admin-field">
                    <span>Kategória</span>
                    <input
                      type="text"
                      value={taskForm.category}
                      onChange={(event) => handleTaskFormChange('category', event.target.value)}
                    />
                  </label>
                  <div className="admin-results-form-row two">
                    <label className="admin-field">
                      <span>Mennyiség</span>
                      <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={taskForm.quantity}
                        onChange={(event) => handleTaskFormChange('quantity', event.target.value)}
                      />
                    </label>
                    <label className="admin-field">
                      <span>Mértékegység</span>
                      <input
                        type="text"
                        list="task-unit-options"
                        value={taskForm.unit}
                        onChange={(event) => handleTaskFormChange('unit', event.target.value)}
                      />
                      <datalist id="task-unit-options">
                        {exerciseUnitOptions.map((unit) => (
                          <option key={unit} value={unit} />
                        ))}
                      </datalist>
                    </label>
                  </div>
                  <label className="admin-field">
                    <span>Leírás</span>
                    <textarea
                      rows={4}
                      value={taskForm.description}
                      onChange={(event) => handleTaskFormChange('description', event.target.value)}
                    />
                  </label>
                  <button type="submit" className="button-primary" disabled={taskSaveBusy}>
                    {taskSaveBusy ? 'Mentés...' : 'Feladat mentése'}
                  </button>
                  {taskNotice && (
                    <p className={`registration-notice ${taskNotice.startsWith('Feladat létrehozva') || taskNotice.startsWith('Feladat frissítve') ? 'ok' : 'error'}`}>
                      {taskNotice}
                    </p>
                  )}
                </form>

                <div className="exercise-import-panel">
                  <div className="admin-content-section-head">
                    <h3>Feladatok importálása táblázatból</h3>
                    <p>Excel (.xlsx), CSV vagy TSV fájl tölthető fel. Az oszlopok: Név, Kategória, Mennyiség, Mértékegység és Leírás.</p>
                  </div>
                  <a className="button-soft task-template-link" href="/feladatok-import-minta.csv" download>
                    <Download size={16} />
                    Importminta letöltése
                  </a>
                  <input
                    type="file"
                    accept=".xlsx,.csv,.tsv,.txt,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/tab-separated-values"
                    onChange={(event) => handleTaskImportFileChange(event.target.files?.[0] || null)}
                    disabled={taskImportBusy}
                  />
                  {taskImportFile && (
                    <p className="admin-background-file">
                      Kiválasztott fájl: <strong>{taskImportFile.name}</strong>
                    </p>
                  )}
                  <button
                    type="button"
                    className="button-soft exercise-import-button"
                    onClick={handleImportTasks}
                    disabled={taskImportBusy}
                  >
                    <Upload size={16} />
                    {taskImportBusy ? 'Importálás...' : 'Feladatok importálása'}
                  </button>
                  {taskImportNotice && (
                    <p className={`registration-notice ${taskImportNotice.includes('létrehozva') || taskImportNotice.includes('frissítve') ? 'ok' : 'error'}`}>
                      {taskImportNotice}
                    </p>
                  )}
                  {taskImportErrors.length > 0 && (
                    <ul className="exercise-import-errors">
                      {taskImportErrors.slice(0, 8).map((error) => (
                        <li key={error}>{error}</li>
                      ))}
                      {taskImportErrors.length > 8 && (
                        <li>...és még {taskImportErrors.length - 8} hiba.</li>
                      )}
                    </ul>
                  )}
                </div>
              </section>

              <section className="admin-content-section admin-task-list-section">
                <div className="admin-content-section-head">
                  <h3>Feladatlista</h3>
                  <p>{tasks.length} feladat van az adatbázisban.</p>
                </div>
                {tasksLoading ? (
                  <p className="admin-registration-empty">Feladatok betöltése...</p>
                ) : tasks.length === 0 ? (
                  <p className="admin-registration-empty">Még nincs létrehozott feladat.</p>
                ) : (
                  <div className="admin-table-wrap">
                    <table className="admin-table admin-tasks-table">
                      <thead>
                        <tr>
                          <th>Név</th>
                          <th>Kategória</th>
                          <th>Mennyiség</th>
                          <th>Leírás</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tasks.map((task) => (
                          <tr key={task.id}>
                            <td><strong>{task.name}</strong></td>
                            <td>{task.category}</td>
                            <td>{task.quantity} {task.unit}</td>
                            <td>{task.description || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </div>

            <section className="admin-content-section admin-training-tasks-section">
              <div className="admin-content-section-head">
                <h3>Edzés feladatlistája</h3>
                <p>Ugyanaz a feladat többször is hozzáadható. A nyilakkal állíthatod be a végrehajtási sorrendet.</p>
              </div>

              <div className="admin-training-task-controls">
                <label className="admin-field">
                  <span>Edzés</span>
                  <select
                    value={selectedTaskTrainingDate}
                    onChange={(event) => {
                      setSelectedTaskTrainingDate(event.target.value);
                      setTrainingTaskAssignmentNotice('');
                    }}
                    disabled={upcomingTaskTrainings.length === 0}
                  >
                    {upcomingTaskTrainings.length === 0 ? (
                      <option value="">Nincs aktuális vagy jövőbeli edzés</option>
                    ) : (
                      upcomingTaskTrainings.map((training) => (
                        <option key={training.id} value={training.trainingDate}>
                          {formatTrainingDate(training.trainingDate)} - {formatTrainingTime(training)}
                        </option>
                      ))
                    )}
                  </select>
                </label>

                <label className="admin-field">
                  <span>Hozzáadandó feladat</span>
                  <select
                    value={selectedTrainingTaskId}
                    onChange={(event) => {
                      setSelectedTrainingTaskId(event.target.value);
                      setTrainingTaskAssignmentNotice('');
                    }}
                    disabled={tasksLoading || tasks.length === 0}
                  >
                    {tasks.length === 0 ? (
                      <option value="">Nincs létrehozott feladat</option>
                    ) : (
                      tasks.map((task) => (
                        <option key={task.id} value={task.id}>
                          {task.name} - {task.quantity} {task.unit}
                        </option>
                      ))
                    )}
                  </select>
                </label>

                <button
                  type="button"
                  className="button-primary admin-training-task-add"
                  onClick={handleAddTrainingTask}
                  disabled={
                    trainingTaskAssignmentBusyId === 'add'
                    || !selectedTaskTrainingDate
                    || !selectedTrainingTaskId
                  }
                >
                  <Plus size={17} />
                  {trainingTaskAssignmentBusyId === 'add' ? 'Hozzáadás...' : 'Hozzáadás'}
                </button>
              </div>

              {trainingTaskAssignmentNotice && (
                <p className={`registration-notice ${trainingTaskAssignmentNotice.startsWith('Feladat hozzáadva') || trainingTaskAssignmentNotice.startsWith('Feladat eltávolítva') ? 'ok' : 'error'}`}>
                  {trainingTaskAssignmentNotice}
                </p>
              )}

              {!selectedTaskTrainingDate ? (
                <p className="admin-registration-empty">Nincs kezelhető aktuális vagy jövőbeli edzés.</p>
              ) : trainingTaskAssignmentsLoading ? (
                <p className="admin-registration-empty">Feladatlista betöltése...</p>
              ) : trainingTaskAssignments.length === 0 ? (
                <p className="admin-registration-empty">Ehhez az edzéshez még nincs feladat hozzárendelve.</p>
              ) : (
                <ol className="admin-training-task-list">
                  {trainingTaskAssignments.map((assignment, index) => {
                    const currentTask = taskById[assignment.taskId];
                    const taskName = currentTask?.name || assignment.taskName || 'Ismeretlen feladat';
                    const taskCategory = currentTask?.category || assignment.taskCategory;
                    const taskQuantity = currentTask?.quantity || assignment.quantity;
                    const taskUnit = currentTask?.unit || assignment.unit;
                    const taskDescription = currentTask?.description || assignment.description;
                    const controlsDisabled = Boolean(trainingTaskAssignmentBusyId);

                    return (
                      <li key={assignment.id}>
                        <span className="admin-training-task-number">{index + 1}</span>
                        <div className="admin-training-task-copy">
                          <strong>{taskName}</strong>
                          <small>{taskCategory || 'Kategória nélkül'} · {taskQuantity} {taskUnit}</small>
                          {taskDescription && <p>{taskDescription}</p>}
                        </div>
                        <div className="admin-training-task-actions">
                          <button
                            type="button"
                            className="button-soft admin-icon-button"
                            onClick={() => handleMoveTrainingTask(index, -1)}
                            disabled={controlsDisabled || index === 0}
                            aria-label={`${taskName} mozgatása feljebb`}
                            title="Mozgatás feljebb"
                          >
                            <ArrowUp size={17} />
                          </button>
                          <button
                            type="button"
                            className="button-soft admin-icon-button"
                            onClick={() => handleMoveTrainingTask(index, 1)}
                            disabled={controlsDisabled || index === trainingTaskAssignments.length - 1}
                            aria-label={`${taskName} mozgatása lejjebb`}
                            title="Mozgatás lejjebb"
                          >
                            <ArrowDown size={17} />
                          </button>
                          <button
                            type="button"
                            className="button-soft admin-icon-button danger"
                            onClick={() => handleDeleteTrainingTask(assignment)}
                            disabled={controlsDisabled}
                            aria-label={`${taskName} eltávolítása az edzésből`}
                            title="Eltávolítás az edzésből"
                          >
                            <Trash2 size={17} />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </section>
          </section>
        )}

        {currentUser && !claimsLoading && hasOwnerAccess && activeView === 'billing' && (
          <section className="card card-large admin-panel admin-billing-panel">
            <div className="admin-monitor-head">
              <div>
                <h2>Számlázás</h2>
                <p className="admin-panel-subtitle">
                  Itt állíthatod az edzés árkategóriáit, a felhasználók számlázási módját és az egyenlegeket.
                </p>
              </div>
            </div>

            {billingNotice && (
              <p className={`registration-notice ${billingNotice.includes('hiba') || billingNotice.includes('nem') || billingNotice.includes('Válassz') ? 'error' : 'ok'}`}>
                {billingNotice}
              </p>
            )}

            <div className="admin-billing-grid">
              <section className="admin-content-section">
                <div className="admin-content-section-head">
                  <h3>Árkategóriák</h3>
                  <p>A számlán egy tétel szerepel majd: Csapatedzés.</p>
                </div>
                <div className="admin-form admin-billing-settings-form">
                  <label className="admin-field">
                    <span>Pályadíj / óra / pálya</span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={billingSettingsDraft.courtFeeHuf}
                      onChange={(event) => handleBillingSettingFieldChange('courtFeeHuf', event.target.value)}
                    />
                  </label>
                  <label className="admin-field">
                    <span>Edzés időtartama (óra)</span>
                    <input
                      type="number"
                      min="0.25"
                      step="0.25"
                      value={billingSettingsDraft.trainingDurationHours}
                      onChange={(event) => handleBillingSettingFieldChange('trainingDurationHours', event.target.value)}
                    />
                  </label>
                  <label className="admin-field">
                    <span>Késői lemondás (%)</span>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={billingSettingsDraft.lateCancellationPercent}
                      onChange={(event) => handleBillingSettingFieldChange('lateCancellationPercent', event.target.value)}
                    />
                  </label>
                </div>

                <div className="admin-table-wrap">
                  <table className="admin-table admin-billing-category-table">
                    <thead>
                      <tr>
                        <th>Kategória</th>
                        <th>Tól</th>
                        <th>Ig</th>
                        <th>Fix ár</th>
                        <th>Pályaszám</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billingSettingsDraft.categories.map((category, index) => (
                        <tr key={category.id}>
                          <td>{category.id}</td>
                          <td>
                            <input
                              type="number"
                              min="1"
                              step="1"
                              value={category.minParticipants}
                              onChange={(event) => handleBillingCategoryDraftChange(index, 'minParticipants', event.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              min="1"
                              step="1"
                              value={category.maxParticipants}
                              onChange={(event) => handleBillingCategoryDraftChange(index, 'maxParticipants', event.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={category.totalPriceHuf}
                              onChange={(event) => handleBillingCategoryDraftChange(index, 'totalPriceHuf', event.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              min="0.5"
                              step="0.5"
                              value={category.courtCount}
                              onChange={(event) => handleBillingCategoryDraftChange(index, 'courtCount', event.target.value)}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="admin-save-row">
                  <button className="button-primary" onClick={handleSaveBillingSettings} disabled={billingSettingsSaveBusy}>
                    <Save size={16} />
                    {billingSettingsSaveBusy ? 'Mentés...' : 'Számlázási beállítások mentése'}
                  </button>
                </div>
              </section>

              <section className="admin-content-section">
                <div className="admin-content-section-head">
                  <h3>Egyenleg módosítás</h3>
                  <p>Jóváírás, befizetés, manuális számlázás vagy korrekció rögzítése naplóval.</p>
                </div>
                <div className="admin-form admin-form-compact">
                  <label className="admin-field">
                    <span>Felhasználó</span>
                    <select
                      value={balanceForm.userId}
                      onChange={(event) => handleBalanceFormChange('userId', event.target.value)}
                    >
                      {users.map((userItem) => (
                        <option key={userItem.id} value={userItem.uid || userItem.id}>
                          {userItem.displayName || userItem.email || userItem.id} ({formatCurrency(userItem.billingBalanceHuf)})
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="admin-field">
                    <span>Típus</span>
                    <select
                      value={balanceForm.type}
                      onChange={(event) => handleBalanceFormChange('type', event.target.value)}
                    >
                      {balanceTransactionTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="admin-field">
                    <span>Összeg</span>
                    <input
                      type="number"
                      step="1"
                      value={balanceForm.amount}
                      onChange={(event) => handleBalanceFormChange('amount', event.target.value)}
                    />
                  </label>
                  <label className="admin-field">
                    <span>Megjegyzés</span>
                    <input
                      type="text"
                      value={balanceForm.note}
                      onChange={(event) => handleBalanceFormChange('note', event.target.value)}
                    />
                  </label>
                </div>
                <div className="admin-save-row">
                  <button className="button-primary" onClick={handleAddBalanceTransaction} disabled={balanceSaveBusy || users.length === 0}>
                    {balanceSaveBusy ? 'Mentés...' : 'Egyenleg rögzítése'}
                  </button>
                </div>
              </section>
            </div>

            <section className="admin-content-section admin-billing-users-section">
              <div className="admin-content-section-head">
                <h3>Felhasználói számlázási módok</h3>
                <p>A beállítás minden következő edzés számlázásánál érvényes, amíg át nem állítod.</p>
              </div>
              {usersLoading ? (
                <p>Felhasználók betöltése...</p>
              ) : (
                <div className="admin-table-wrap">
                  <table className="admin-table admin-billing-users-table">
                    <thead>
                      <tr>
                        <th>Név</th>
                        <th>Email</th>
                        <th>Számlázási mód</th>
                        <th>Negatív egyenleg</th>
                        <th>Egyenleg</th>
                        <th>Számlázási adatok</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((userItem) => (
                        <tr key={userItem.id}>
                          <td>{userItem.displayName || userItem.nickname || '-'}</td>
                          <td>{userItem.email || '-'}</td>
                          <td>
                            <select
                              className="admin-inline-select"
                              value={userItem.billingMode}
                              onChange={(event) => handleChangeUserBillingMode(userItem, event.target.value)}
                              disabled={billingUserBusyId === userItem.id}
                            >
                              {billingModeOptions.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <label className="admin-negative-balance-switch">
                              <input
                                type="checkbox"
                                checked={userItem.billingAllowNegativeBalance}
                                onChange={(event) => handleToggleNegativeBalance(userItem, event.target.checked)}
                                disabled={billingUserBusyId === userItem.id}
                                aria-label={`Negatív egyenleg ${userItem.displayName || userItem.email || userItem.id}`}
                              />
                              <span className="admin-negative-balance-switch-track" aria-hidden="true" />
                              <span>{userItem.billingAllowNegativeBalance ? 'Engedélyezve' : 'Kikapcsolva'}</span>
                            </label>
                          </td>
                          <td>{formatCurrency(userItem.billingBalanceHuf)}</td>
                          <td>{hasBillingPartnerData(userItem) ? 'Rendben' : 'Hiányos'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <div className="admin-billing-grid">
              <section className="admin-content-section">
                <div className="admin-content-section-head">
                  <h3>Legutóbbi számlázási tételek</h3>
                  <p>Az itt látható tételek a Billingo küldés előkészített alapjai.</p>
                </div>
                {billingItems.length === 0 ? (
                  <p className="admin-registration-empty">Még nincs számlázási tétel.</p>
                ) : (
                  <div className="admin-table-wrap">
                    <table className="admin-table admin-billing-history-table">
                      <thead>
                        <tr>
                          <th>Edzés</th>
                          <th>Felhasználó</th>
                          <th>Összeg</th>
                          <th>Státusz</th>
                        </tr>
                      </thead>
                      <tbody>
                        {billingItems.slice(0, 20).map((item) => (
                          <tr key={item.id}>
                            <td>{item.trainingDate || '-'}</td>
                            <td>{item.userDisplayName || item.userEmail || item.userId || '-'}</td>
                            <td>{formatCurrency(item.amountHuf)}</td>
                            <td>{item.statusMessage || item.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <section className="admin-content-section">
                <div className="admin-content-section-head">
                  <h3>Egyenlegtörténet</h3>
                  <p>Jóváírások, befizetések, korrekciók és edzéslevonások.</p>
                </div>
                {billingLedgerEntries.length === 0 ? (
                  <p className="admin-registration-empty">Még nincs egyenlegmozgás.</p>
                ) : (
                  <div className="admin-table-wrap">
                    <table className="admin-table admin-billing-history-table">
                      <thead>
                        <tr>
                          <th>Felhasználó</th>
                          <th>Típus</th>
                          <th>Összeg</th>
                          <th>Megjegyzés</th>
                        </tr>
                      </thead>
                      <tbody>
                        {billingLedgerEntries.slice(0, 20).map((entry) => {
                          const typeLabel = balanceTransactionTypeOptions.find((option) => option.value === entry.type)?.label
                            || (entry.type === 'training_deduction'
                              ? 'Edzés levonás'
                              : entry.type === 'training_balance_charge'
                                ? 'Egyenlegre terhelve'
                                : entry.type || '-');
                          return (
                            <tr key={entry.id}>
                              <td>{entry.userDisplayName || entry.userEmail || entry.userId || '-'}</td>
                              <td>{typeLabel}</td>
                              <td>{formatCurrency(entry.amountHuf)}</td>
                              <td>{entry.note || entry.trainingDate || '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </div>
          </section>
        )}

        {currentUser && !claimsLoading && hasOwnerAccess && activeView === 'results' && (
          <section className="card card-large admin-panel admin-results-panel">
            <div className="admin-monitor-head">
              <div>
                <h2>Eredmények</h2>
                <p className="admin-panel-subtitle">
                  Fizikai felmérő gyakorlatok, eredményrögzítés, személyes rekordok és ranglisták.
                </p>
              </div>
              <div className="results-mode-switch" role="group" aria-label="Eredménytípus választó">
                <button
                  type="button"
                  className={adminResultsMode === 'exercises' ? 'active' : ''}
                  onClick={() => handleAdminResultsModeChange('exercises')}
                >
                  Gyakorlatok
                </button>
                <button
                  type="button"
                  className={adminResultsMode === 'sets' ? 'active' : ''}
                  onClick={() => handleAdminResultsModeChange('sets')}
                >
                  Szettek
                </button>
              </div>
            </div>

            {adminResultsMode === 'exercises' ? (
              <>
            <div className="admin-results-grid">
              <section className="admin-content-section">
                <div className="admin-content-section-head">
                  <h3>Új gyakorlat</h3>
                  <p>A gyakorlatok külön dokumentumok, ezért később adatbázis-migráció nélkül bővíthetők.</p>
                </div>
                <form
                  className="admin-form admin-form-compact"
                  onSubmit={(event) => {
                    event.preventDefault();
                    handleCreateExercise();
                  }}
                >
                  <label className="admin-field">
                    <span>Név</span>
                    <input
                      type="text"
                      value={exerciseForm.name}
                      onChange={(event) => handleExerciseFormChange('name', event.target.value)}
                    />
                  </label>
                  <label className="admin-field">
                    <span>Leírás</span>
                    <textarea
                      rows={3}
                      value={exerciseForm.description}
                      onChange={(event) => handleExerciseFormChange('description', event.target.value)}
                    />
                  </label>
                  <div className="admin-results-form-row four">
                    <label className="admin-field">
                      <span>Ismétlésszám</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={exerciseForm.repetitionCount}
                        onChange={(event) => handleExerciseFormChange('repetitionCount', event.target.value)}
                        placeholder="pl. 20"
                      />
                    </label>
                    <label className="admin-field">
                      <span>Mértékegység</span>
                      <select
                        value={exerciseForm.unit}
                        onChange={(event) => handleExerciseFormChange('unit', event.target.value)}
                      >
                        {exerciseUnitOptions.map((unit) => (
                          <option key={unit} value={unit}>{unit}</option>
                        ))}
                      </select>
                    </label>
                    <label className="admin-field">
                      <span>Időtartam perc</span>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={exerciseForm.durationMinutes}
                        onChange={(event) => handleExerciseFormChange('durationMinutes', event.target.value)}
                      />
                    </label>
                    <label className="admin-field">
                      <span>Időtartam mp</span>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        step="1"
                        value={exerciseForm.durationSeconds}
                        onChange={(event) => handleExerciseFormChange('durationSeconds', event.target.value)}
                      />
                    </label>
                  </div>
                  <div className="admin-results-form-row two">
                    <label className="admin-field">
                      <span>Eredmény mértékegysége</span>
                      <select
                        value={exerciseForm.resultUnit}
                        onChange={(event) => handleExerciseFormChange('resultUnit', event.target.value)}
                      >
                        {resultUnitOptions.map((unit) => (
                          <option key={unit} value={unit}>{unit}</option>
                        ))}
                      </select>
                    </label>
                    <label className="admin-field">
                      <span>Rangsorolás</span>
                      <select
                        value={exerciseForm.resultDirection}
                        onChange={(event) => handleExerciseFormChange('resultDirection', event.target.value)}
                      >
                        {resultDirectionOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <button type="submit" className="button-primary" disabled={exerciseSaveBusy}>
                    {exerciseSaveBusy ? 'Mentés...' : 'Gyakorlat mentése'}
                  </button>
                  {exerciseNotice && (
                    <p className={`registration-notice ${exerciseNotice.startsWith('Gyakorlat létrehozva') ? 'ok' : 'error'}`}>
                      {exerciseNotice}
                    </p>
                  )}
                </form>

                <div className="exercise-import-panel">
                  <div className="admin-content-section-head">
                    <h3>Gyakorlatok importálása táblázatból</h3>
                    <p>Excelből mentsd CSV vagy tabulátorral tagolt szövegként, majd töltsd fel itt.</p>
                  </div>
                  <input
                    type="file"
                    accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values"
                    onChange={(event) => handleExerciseImportFileChange(event.target.files?.[0] || null)}
                    disabled={exerciseImportBusy}
                  />
                  {exerciseImportFile && (
                    <p className="admin-background-file">
                      Kiválasztott fájl: <strong>{exerciseImportFile.name}</strong>
                    </p>
                  )}
                  <button
                    type="button"
                    className="button-soft exercise-import-button"
                    onClick={handleImportExercises}
                    disabled={exerciseImportBusy || !exerciseImportFile}
                  >
                    <Upload size={16} />
                    {exerciseImportBusy ? 'Importálás...' : 'Gyakorlatok importálása'}
                  </button>
                  {exerciseImportNotice && (
                    <p className={`registration-notice ${exerciseImportNotice.includes('sikeresen') || exerciseImportNotice.includes('importálva') ? 'ok' : 'error'}`}>
                      {exerciseImportNotice}
                    </p>
                  )}
                  {exerciseImportErrors.length > 0 && (
                    <ul className="exercise-import-errors">
                      {exerciseImportErrors.slice(0, 8).map((error) => (
                        <li key={error}>{error}</li>
                      ))}
                      {exerciseImportErrors.length > 8 && (
                        <li>...és még {exerciseImportErrors.length - 8} hiba.</li>
                      )}
                    </ul>
                  )}
                </div>
              </section>

              <section className="admin-content-section">
                <div className="admin-content-section-head">
                  <h3>Eredmény rögzítése</h3>
                  <p>Admin által felvitt és játékos által felvitt eredmények ugyanabba a kollekcióba kerülnek.</p>
                </div>
                <form
                  className="admin-form admin-form-compact"
                  onSubmit={(event) => {
                    event.preventDefault();
                    handleCreateFitnessResult();
                  }}
                >
                  <label className="admin-field">
                    <span>Játékos</span>
                    <select
                      value={fitnessResultForm.userId}
                      onChange={(event) => handleFitnessResultFormChange('userId', event.target.value)}
                      disabled={usersLoading || users.length === 0}
                    >
                      {users.length === 0 ? (
                        <option value="">Nincs felhasználó</option>
                      ) : (
                        users.map((userItem) => (
                          <option key={userItem.uid || userItem.id} value={userItem.uid || userItem.id}>
                            {userItem.displayName || userItem.email || userItem.uid || userItem.id}
                          </option>
                        ))
                      )}
                    </select>
                  </label>
                  <label className="admin-field">
                    <span>Gyakorlat</span>
                    <select
                      value={fitnessResultForm.exerciseId}
                      onChange={(event) => handleFitnessResultFormChange('exerciseId', event.target.value)}
                      disabled={exercisesLoading || exercises.length === 0}
                    >
                      {exercises.length === 0 ? (
                        <option value="">Nincs gyakorlat</option>
                      ) : (
                        exercises.map((exercise) => (
                          <option key={exercise.id} value={exercise.id}>
                            {formatExerciseTask(exercise) ? `${exercise.name} (${formatExerciseTask(exercise)})` : exercise.name}
                          </option>
                        ))
                      )}
                    </select>
                  </label>
                  {exerciseById[fitnessResultForm.exerciseId] && formatExerciseTask(exerciseById[fitnessResultForm.exerciseId]) && (
                    <p className="profile-muted">Feladat: {formatExerciseTask(exerciseById[fitnessResultForm.exerciseId])}</p>
                  )}
                  <div className="admin-results-form-row two">
                    <label className="admin-field">
                      <span>Edzés dátuma</span>
                      <input
                        type="date"
                        value={fitnessResultForm.trainingDate}
                        onChange={(event) => handleFitnessResultFormChange('trainingDate', event.target.value)}
                      />
                    </label>
                    <label className="admin-field">
                      <span>Eredmény</span>
                      <input
                        type="number"
                        step="0.01"
                        value={fitnessResultForm.value}
                        onChange={(event) => handleFitnessResultFormChange('value', event.target.value)}
                      />
                    </label>
                  </div>
                  <button
                    type="submit"
                    className="button-primary"
                    disabled={fitnessResultSaveBusy || users.length === 0 || exercises.length === 0}
                  >
                    {fitnessResultSaveBusy ? 'Mentés...' : 'Eredmény mentése'}
                  </button>
                  {fitnessResultNotice && (
                    <p className={`registration-notice ${fitnessResultNotice.startsWith('Eredmény rögzítve') || fitnessResultNotice.startsWith('Eredmény frissítve') ? 'ok' : 'error'}`}>
                      {fitnessResultNotice}
                    </p>
                  )}
                </form>
              </section>
            </div>

            <div className="admin-results-filter-row">
              <label className="admin-field">
                <span>Gyakorlat szerinti szűrés</span>
                <select
                  value={selectedResultExerciseId}
                  onChange={(event) => setSelectedResultExerciseId(event.target.value)}
                >
                  <option value="">Összes gyakorlat</option>
                  {exercises.map((exercise) => (
                    <option key={exercise.id} value={exercise.id}>
                      {formatExerciseTask(exercise) ? `${exercise.name} (${formatExerciseTask(exercise)})` : exercise.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="admin-field">
                <span>Játékos szerinti szűrés</span>
                <select
                  value={selectedResultUserId}
                  onChange={(event) => setSelectedResultUserId(event.target.value)}
                >
                  <option value="">Összes játékos</option>
                  {users.map((userItem) => (
                    <option key={userItem.uid || userItem.id} value={userItem.uid || userItem.id}>
                      {userItem.displayName || userItem.email || userItem.uid || userItem.id}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="admin-results-grid lower">
              <section className="admin-content-section">
                <div className="admin-content-section-head">
                  <h3>Gyakorlat rangsor</h3>
                  <p>Az all-time lista a személyes rekord dokumentumokból jön, ezért nem kell minden eredményt újraszámolni.</p>
                </div>
                {!selectedResultExerciseId ? (
                  <p className="admin-registration-empty">Válassz gyakorlatot a ranglistához.</p>
                ) : selectedExerciseLeaderboard.length === 0 ? (
                  <p className="admin-registration-empty">Még nincs személyes rekord ehhez a gyakorlathoz.</p>
                ) : (
                  <ol className="leaderboard-list admin-leaderboard-list">
                    {selectedExerciseLeaderboard.map((record) => (
                      <li key={record.id}>
                        <span>{getPublicPlayerName(record)}</span>
                        <strong>{formatResultValue(record.resultValue, record.resultUnit)}</strong>
                        <small>{formatRegistrationTimestamp(record.achievedAt)}</small>
                      </li>
                    ))}
                  </ol>
                )}
              </section>

              <section className="admin-content-section">
                <div className="admin-content-section-head">
                  <h3>Játékos fejlődése</h3>
                  <p>Válassz játékost a fenti szűrőben, és itt látszanak az időrendi eredményei.</p>
                </div>
                {!selectedResultUserId ? (
                  <p className="admin-registration-empty">Nincs kiválasztott játékos.</p>
                ) : selectedUserProgress.length === 0 ? (
                  <p className="admin-registration-empty">Ehhez a játékoshoz még nincs eredmény.</p>
                ) : (
                  <ul className="admin-progress-list">
                    {selectedUserProgress.map((item) => (
                      <li key={item.id}>
                        <span>
                          {formatExerciseTask(exerciseById[item.exerciseId])
                            ? `${getExerciseName(exerciseById, item.exerciseId, item.exerciseName)} (${formatExerciseTask(exerciseById[item.exerciseId])})`
                            : getExerciseName(exerciseById, item.exerciseId, item.exerciseName)}
                        </span>
                        <strong>{formatResultValue(item.resultValue, item.resultUnit)}</strong>
                        <small>{formatTrainingDate(item.trainingDate)}</small>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>

            <section className="admin-content-section admin-results-table-section">
              <div className="admin-content-section-head">
                <h3>Összes eredmény</h3>
                <p>Az érték mezőben javíthatod a hibásan felvitt eredményeket.</p>
              </div>
              {fitnessResultsLoading ? (
                <p className="admin-registration-empty">Eredmények betöltése...</p>
              ) : filteredFitnessResults.length === 0 ? (
                <p className="admin-registration-empty">Nincs a szűrésnek megfelelő eredmény.</p>
              ) : (
                <div className="admin-table-wrap">
                  <table className="admin-table admin-results-table">
                    <colgroup>
                      <col className="admin-results-col-player" />
                      <col className="admin-results-col-exercise" />
                      <col className="admin-results-col-training" />
                      <col className="admin-results-col-value" />
                      <col className="admin-results-col-created" />
                      <col className="admin-results-col-actions" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>Játékos</th>
                        <th>Gyakorlat</th>
                        <th>Edzés</th>
                        <th>Eredmény</th>
                        <th>Rögzítve</th>
                        <th>Művelet</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredFitnessResults.map((item) => {
                        const draftValue = resultDrafts[item.id] ?? String(item.resultValue);
                        const exercise = exerciseById[item.exerciseId];
                        const unit = exercise?.resultUnit || item.resultUnit || '';

                        return (
                          <tr key={item.id}>
                            <td>{item.userDisplayName || item.userEmail || item.userId || '-'}</td>
                            <td>
                              {formatExerciseTask(exerciseById[item.exerciseId])
                                ? `${getExerciseName(exerciseById, item.exerciseId, item.exerciseName)} (${formatExerciseTask(exerciseById[item.exerciseId])})`
                                : getExerciseName(exerciseById, item.exerciseId, item.exerciseName)}
                            </td>
                            <td>{formatTrainingDate(item.trainingDate)}</td>
                            <td>
                              <div className="admin-result-value-edit">
                                <input
                                  type="number"
                                  step="0.01"
                                  value={draftValue}
                                  onChange={(event) => handleResultDraftChange(item.id, event.target.value)}
                                />
                                <span>{unit}</span>
                              </div>
                            </td>
                            <td>{formatRegistrationTimestamp(item.recordedAt)}</td>
                            <td>
                              <button
                                type="button"
                                className="button-soft admin-row-action-button"
                                onClick={() => handleUpdateFitnessResult(item)}
                                disabled={resultUpdateBusyId === item.id || Number.parseFloat(String(draftValue).replace(',', '.')) === item.resultValue}
                              >
                                {resultUpdateBusyId === item.id ? 'Mentés...' : 'Mentés'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
              </>
            ) : (
              <section className="admin-content-section admin-squash-sets-section">
                <div className="admin-content-section-head">
                  <h3>Összes szett eredmény</h3>
                  <p>A legutóbbi 300 rögzített szett látható.</p>
                </div>

                {adminSquashSetNotice && (
                  <p className={`registration-notice ${adminSquashSetNotice.startsWith('Szett eredménye frissítve') || adminSquashSetNotice.startsWith('Szett eredménye törölve') ? 'ok' : 'error'}`}>
                    {adminSquashSetNotice}
                  </p>
                )}

                {adminSquashSetsLoading ? (
                  <p className="admin-registration-empty">Szett eredmények betöltése...</p>
                ) : adminSquashSets.length === 0 ? (
                  <p className="admin-registration-empty">Még nincs rögzített szett eredmény.</p>
                ) : (
                  <div className="admin-squash-set-list">
                    {adminSquashSets.map((set) => {
                      const playerAName = getSquashSetPlayerName(set, set.playerAId);
                      const playerBName = getSquashSetPlayerName(set, set.playerBId);
                      const editDraft = adminSquashSetDrafts[set.id];

                      return (
                        <article className="squash-set-history-item" key={set.id}>
                          <div className="squash-set-history-head">
                            <div>
                              <strong>{playerAName} {set.playerAScore}–{set.playerBScore} {playerBName}</strong>
                              <small>
                                {formatTrainingDate(set.trainingDate)} · {formatRegistrationTimestamp(set.recordedAt)}
                              </small>
                            </div>
                            <div className="squash-set-history-actions">
                              {!editDraft && (
                                <button
                                  type="button"
                                  className="button-soft admin-icon-button"
                                  onClick={() => handleStartAdminSquashSetEdit(set)}
                                  disabled={Boolean(adminSquashSetBusyId)}
                                  aria-label="Szett szerkesztése"
                                  title="Szett szerkesztése"
                                >
                                  <Pencil size={16} />
                                </button>
                              )}
                              <button
                                type="button"
                                className="button-soft admin-icon-button danger"
                                onClick={() => handleDeleteAdminSquashSet(set)}
                                disabled={Boolean(adminSquashSetBusyId)}
                                aria-label="Szett törlése"
                                title="Szett törlése"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>

                          {editDraft && (
                            <div className="squash-set-inline-edit">
                              <SquashScoreEditor
                                playerAId={set.playerAId}
                                playerAName={playerAName}
                                playerBId={set.playerBId}
                                playerBName={playerBName}
                                winnerId={editDraft.winnerId}
                                loserScore={editDraft.loserScore}
                                onWinnerChange={(winnerId) => handleAdminSquashSetDraftChange(set.id, 'winnerId', winnerId)}
                                onLoserScoreChange={(score) => handleAdminSquashSetDraftChange(set.id, 'loserScore', score)}
                                disabled={adminSquashSetBusyId === set.id}
                              />
                              <div className="squash-set-inline-actions">
                                <button type="button" className="button-soft" onClick={() => handleCancelAdminSquashSetEdit(set.id)}>
                                  Mégse
                                </button>
                                <button
                                  type="button"
                                  className="button-primary"
                                  onClick={() => handleSaveAdminSquashSet(set)}
                                  disabled={adminSquashSetBusyId === set.id}
                                >
                                  {adminSquashSetBusyId === set.id ? 'Mentés...' : 'Mentés'}
                                </button>
                              </div>
                            </div>
                          )}
                        </article>
                      );
                    })}
                  </div>
                )}
              </section>
            )}
          </section>
        )}

        {currentUser && !claimsLoading && hasOwnerAccess && activeView === 'content' && (
          <section className="card card-large admin-panel">
            <h2>Weboldal szövegek</h2>
            <p className="admin-panel-subtitle">
              A mentés után a főoldal a Firestore-ból ezeket a szövegeket tölti.
            </p>
            <div className="admin-backgrounds">
              <h3>Háttérképek</h3>
              <p className="admin-panel-subtitle">
                Itt tudod feltölteni a főoldali diabemutató képeit és a szekciók háttérképeit.
              </p>
              <div className="admin-background-item">
                <div className="admin-background-head">
                  <h4>Főoldali diabemutató</h4>
                  <p>A feltöltött képek automatikusan váltakoznak a főoldal tetején.</p>
                </div>

                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) => handleBackgroundFileSelect('heroBackgroundUrls', event.target.files?.[0] || null)}
                  disabled={uploadBusyKey === 'heroBackgroundUrls'}
                />
                {backgroundFiles.heroBackgroundUrls && (
                  <p className="admin-background-file">
                    Kiválasztott fájl: <strong>{backgroundFiles.heroBackgroundUrls.name}</strong>
                  </p>
                )}

                <div className="admin-background-actions">
                  <button
                    type="button"
                    className="button-primary"
                    onClick={handleHeroSlideUpload}
                    disabled={uploadBusyKey === 'heroBackgroundUrls' || !backgroundFiles.heroBackgroundUrls}
                  >
                    <Upload size={16} />
                    {uploadBusyKey === 'heroBackgroundUrls' ? 'Feltöltés...' : 'Kép hozzáadása'}
                  </button>
                </div>

                {heroSlideshowUrls.length > 0 ? (
                  <div className="admin-slideshow-list">
                    {heroSlideshowUrls.map((url, index) => {
                      const position = normalizeImagePosition(siteContentForm.heroBackgroundPositions?.[index]);

                      return (
                        <div className="admin-slideshow-item" key={url}>
                          <div className="admin-slideshow-preview">
                            <img
                              src={url}
                              alt={`Főoldali diabemutató ${index + 1}. kép`}
                              style={{ objectPosition: position }}
                            />
                            <span>{index + 1}. kép</span>
                          </div>
                          {renderImagePositionControls({
                            position,
                            busyKey: 'heroBackgroundPositions',
                            onChange: (axis, value) => handleImagePositionChange('heroBackgroundPositions', axis, value, index),
                            onSave: () => handleImagePositionSave('heroBackgroundPositions'),
                          })}
                          <button
                            type="button"
                            className="button-soft"
                            onClick={() => handleHeroSlideRemove(url)}
                            disabled={uploadBusyKey === url}
                          >
                            <Trash2 size={16} />
                            Törlés
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="admin-background-empty">Nincs beállított főoldali kép.</p>
                )}
              </div>
              <div className="admin-background-grid">
                {backgroundImageFields.map((field) => {
                  const selectedFile = backgroundFiles[field.key];
                  const currentUrl = siteContentForm[field.key];
                  const isBusy = uploadBusyKey === field.key;
                  const previewClassName = field.previewClassName || 'admin-background-preview';
                  const clearLabel = field.clearLabel || 'Háttér törlése';
                  const emptyText = field.emptyText || 'Nincs beállított háttérkép.';
                  const positionKey = `${field.key.replace(/Url$/, '')}Position`;
                  const position = normalizeImagePosition(siteContentForm[positionKey]);

                  return (
                    <div className="admin-background-item" key={field.key}>
                      <div className="admin-background-head">
                        <h4>{field.title}</h4>
                        <p>{field.helper}</p>
                      </div>

                      <input
                        type="file"
                        accept="image/*"
                        onChange={(event) => handleBackgroundFileSelect(field.key, event.target.files?.[0] || null)}
                        disabled={isBusy}
                      />
                      {selectedFile && (
                        <p className="admin-background-file">
                          Kiválasztott fájl: <strong>{selectedFile.name}</strong>
                        </p>
                      )}

                      <div className="admin-background-actions">
                        <button
                          type="button"
                          className="button-primary"
                          onClick={() => handleBackgroundUpload(field.key)}
                          disabled={isBusy || !selectedFile}
                        >
                          <Upload size={16} />
                          {isBusy ? 'Feltöltés...' : 'Kép feltöltése'}
                        </button>
                        <button
                          type="button"
                          className="button-soft"
                          onClick={() => handleBackgroundClear(field.key)}
                          disabled={isBusy || !currentUrl}
                        >
                          <Trash2 size={16} />
                          {clearLabel}
                        </button>
                      </div>

                      {currentUrl ? (
                        <>
                          <div className={previewClassName}>
                            <img
                              src={currentUrl}
                              alt={`${field.title} előnézet`}
                              style={{ objectPosition: position }}
                            />
                          </div>
                          {renderImagePositionControls({
                            position,
                            busyKey: positionKey,
                            disabled: isBusy,
                            onChange: (axis, value) => handleImagePositionChange(positionKey, axis, value),
                            onSave: () => handleImagePositionSave(positionKey),
                          })}
                        </>
                      ) : (
                        <p className="admin-background-empty">{emptyText}</p>
                      )}
                    </div>
                  );
                })}
              </div>
              {uploadNotice && (
                <p className={`registration-notice ${uploadNotice.startsWith('Háttérkép') || uploadNotice.startsWith('Kép pozíciója') ? 'ok' : 'error'}`}>
                  {uploadNotice}
                </p>
              )}
            </div>
            <div className="admin-form">
              {siteContentFields.map((field) => (
                <label className="admin-field" key={field.key}>
                  <span>{field.label}</span>
                  {field.multiline ? (
                    <textarea
                      rows={field.rows || 4}
                      value={siteContentForm[field.key]}
                      onChange={(event) => handleFieldChange(field.key, event.target.value)}
                    />
                  ) : (
                    <input
                      type="text"
                      value={siteContentForm[field.key]}
                      onChange={(event) => handleFieldChange(field.key, event.target.value)}
                    />
                  )}
                </label>
              ))}
            </div>
            <div className="admin-save-row">
              <button className="button-primary" onClick={handleSaveContent} disabled={saveBusy}>
                <Save size={16} />
                {saveBusy ? 'Mentés...' : 'Szövegek mentése'}
              </button>
              {saveNotice && (
                <p className={`registration-notice ${saveNotice.startsWith('Sikeres') ? 'ok' : 'error'}`}>
                  {saveNotice}
                </p>
              )}
            </div>
          </section>
        )}

        {currentUser && !claimsLoading && hasOwnerAccess && activeView === 'administration' && (
          <section className="card card-large admin-panel">
            <h2>Adminisztráció</h2>
            <p className="admin-panel-subtitle">
              Itt tudod szerkeszteni a láblécben elérhető jogi és szolgáltatási oldalakat.
            </p>

            <div className="admin-section-list">
              {administrationContentSections.map((section) => (
                <section className="admin-content-section" key={section.title}>
                  <div className="admin-content-section-head">
                    <h3>{section.title}</h3>
                    <p>{section.description}</p>
                  </div>
                  <div className="admin-form admin-form-compact">
                    {section.fields.map((field) => (
                      <label className="admin-field" key={field.key}>
                        <span>{field.label}</span>
                        {field.multiline ? (
                          <textarea
                            rows={field.rows || 5}
                            value={siteContentForm[field.key]}
                            onChange={(event) => handleFieldChange(field.key, event.target.value)}
                          />
                        ) : (
                          <input
                            type={field.key === 'contactEmail' ? 'email' : 'text'}
                            value={siteContentForm[field.key]}
                            onChange={(event) => handleFieldChange(field.key, event.target.value)}
                          />
                        )}
                      </label>
                    ))}
                  </div>
                </section>
              ))}
            </div>

            <div className="admin-save-row">
              <button className="button-primary" onClick={handleSaveContent} disabled={saveBusy}>
                <Save size={16} />
                {saveBusy ? 'Mentés...' : 'Adminisztráció mentése'}
              </button>
              {saveNotice && (
                <p className={`registration-notice ${saveNotice.startsWith('Sikeres') ? 'ok' : 'error'}`}>
                  {saveNotice}
                </p>
              )}
            </div>
          </section>
        )}

        {currentUser && !claimsLoading && hasOwnerAccess && activeView === 'emailTemplates' && (
          <section className="card card-large admin-panel admin-email-templates-panel">
            <h2>E-mail sablonok</h2>
            <p className="admin-panel-subtitle">
              Itt tudod szerkeszteni, hogy melyik trigger eseményre milyen tárgyú és szövegű e-mail menjen ki.
            </p>
            <div className="admin-template-placeholders">
              <Mail size={18} />
              <span>Használható változók: {emailTemplatePlaceholders.join(', ')}</span>
            </div>

            <div className="admin-section-list">
              {emailTemplateSections.map((section) => (
                <section className="admin-content-section" key={section.title}>
                  <div className="admin-content-section-head">
                    <h3>{section.title}</h3>
                    <p>{section.description}</p>
                  </div>
                  <div className="admin-form admin-form-compact">
                    {section.fields.map((field) => (
                      <label className="admin-field" key={field.key}>
                        <span>{field.label}</span>
                        {field.multiline ? (
                          <textarea
                            rows={field.rows || 5}
                            value={emailTemplateForm[field.key]}
                            onChange={(event) => handleEmailTemplateFieldChange(field.key, event.target.value)}
                          />
                        ) : (
                          <input
                            type="text"
                            value={emailTemplateForm[field.key]}
                            onChange={(event) => handleEmailTemplateFieldChange(field.key, event.target.value)}
                          />
                        )}
                      </label>
                    ))}
                  </div>
                </section>
              ))}
            </div>

            <div className="admin-save-row">
              <button className="button-primary" onClick={handleSaveEmailTemplates} disabled={emailTemplateSaveBusy}>
                <Save size={16} />
                {emailTemplateSaveBusy ? 'Mentés...' : 'E-mail sablonok mentése'}
              </button>
              {emailTemplateNotice && (
                <p className={`registration-notice ${emailTemplateNotice.startsWith('Sikeres') ? 'ok' : 'error'}`}>
                  {emailTemplateNotice}
                </p>
              )}
            </div>
          </section>
        )}

        {currentUser && !claimsLoading && hasOwnerAccess && activeView === 'users' && (
          <section className="card card-large admin-panel admin-users-panel">
            <h2>Felhasználók</h2>
            <p className="admin-panel-subtitle">
              Itt láthatod a felhasználók címadatait a `users` kollekció alapján.
            </p>
            {userActionNotice && (
              <p className={`registration-notice ${userActionNotice.includes('hiba') ? 'error' : 'ok'}`}>
                {userActionNotice}
              </p>
            )}
            {usersLoading ? (
              <p>Felhasználók betöltése...</p>
            ) : users.length === 0 ? (
              <p>Még nincs felhasználó adat.</p>
            ) : (
              <div className="admin-table-wrap admin-users-table-wrap">
                <table className="admin-table admin-users-table">
                  <colgroup>
                    <col className="admin-users-col-name" />
                    <col className="admin-users-col-nickname" />
                    <col className="admin-users-col-email" />
                    <col className="admin-users-col-uid" />
                    <col className="admin-users-col-country" />
                    <col className="admin-users-col-zip" />
                    <col className="admin-users-col-city" />
                    <col className="admin-users-col-street" />
                    <col className="admin-users-col-billing" />
                    <col className="admin-users-col-late-count" />
                    <col className="admin-users-col-block" />
                    <col className="admin-users-col-delete" />
                    <col className="admin-users-col-role" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Név</th>
                      <th>Becenév</th>
                      <th>Email</th>
                      <th>UID</th>
                      <th>Ország</th>
                      <th>Irányítószám</th>
                      <th>Város</th>
                      <th>Utca, házszám</th>
                      <th>Számlázási adatok</th>
                      <th>Késői lemondás (4 hónap)</th>
                      <th>Jelentkezés</th>
                      <th>Törlés</th>
                      <th>Jogosultság</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((userItem) => (
                      <tr key={userItem.id}>
                        <td>{userItem.displayName || '-'}</td>
                        <td>{userItem.nickname || '-'}</td>
                        <td>{userItem.email || '-'}</td>
                        <td>
                          <button
                            type="button"
                            className="button-soft admin-row-action-button"
                            onClick={() => setUserUidPreview(userItem)}
                          >
                            Megjelenítés
                          </button>
                        </td>
                        <td>{userItem.country || '-'}</td>
                        <td>{userItem.zip || '-'}</td>
                        <td>{userItem.city || '-'}</td>
                        <td>{userItem.street || '-'}</td>
                        <td>
                          <button
                            type="button"
                            className="button-soft admin-row-action-button"
                            onClick={() => handleOpenUserBillingEdit(userItem)}
                          >
                            <Pencil size={14} />
                            Számlázási adatok módosítása
                          </button>
                        </td>
                        <td>{lateCancellationCounts[userItem.uid || userItem.id] || 0}</td>
                        <td>
                          <button
                            type="button"
                            className="button-soft admin-row-action-button"
                            onClick={() => handleToggleUserTrainingRegistration(userItem)}
                            disabled={userBlockBusyId === userItem.id}
                          >
                            {userBlockBusyId === userItem.id
                              ? 'Mentés...'
                              : userItem.trainingRegistrationDisabledByAdmin
                                ? 'Tiltás feloldása'
                                : 'Jelentkezés tiltása'}
                          </button>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="button-soft admin-row-action-button danger"
                            onClick={() => setUserDeleteConfirm(userItem)}
                            disabled={
                              userDeleteBusyId === userItem.id
                              || (userItem.role === 'admin' && (userItem.uid || userItem.id) !== currentUser.uid)
                            }
                            title={
                              userItem.role === 'admin' && (userItem.uid || userItem.id) !== currentUser.uid
                                ? 'Admin jogosultságú felhasználót nem törölhetsz.'
                                : 'Felhasználó törlése'
                            }
                          >
                            <Trash2 size={14} />
                            {userDeleteBusyId === userItem.id ? 'Törlés...' : 'Törlés'}
                          </button>
                        </td>
                        <td>
                          <select
                            className="admin-role-select"
                            value={userItem.role}
                            onChange={(event) => handleChangeUserRole(userItem, event.target.value)}
                            disabled={
                              userRoleBusyId === userItem.id
                              || ((userItem.uid || userItem.id) === currentUser.uid && userItem.role === 'admin')
                            }
                            title={
                              (userItem.uid || userItem.id) === currentUser.uid && userItem.role === 'admin'
                                ? 'A saját admin jogosultságodat nem tudod levenni.'
                                : 'Jogosultság módosítása'
                            }
                          >
                            <option value="user">Felhasználó</option>
                            <option value="admin">Admin</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </main>

      {userBillingEdit && (
        <div
          className="auth-overlay"
          onClick={() => !userBillingSaveBusy && setUserBillingEdit(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="admin-user-billing-title"
        >
          <div className="auth-modal card admin-user-billing-modal" onClick={(event) => event.stopPropagation()}>
            <button
              className="auth-close"
              onClick={() => setUserBillingEdit(null)}
              aria-label="Bezárás"
              disabled={userBillingSaveBusy}
            >
              <X size={18} />
            </button>
            <div className="auth-header">
              <h2 id="admin-user-billing-title">Számlázási adatok módosítása</h2>
              <p>{userBillingEdit.displayName || userBillingEdit.email || 'Felhasználó'}</p>
            </div>
            <div className="admin-form admin-user-billing-form">
              <label className="admin-field admin-user-billing-wide">
                <span>Név</span>
                <input
                  type="text"
                  value={userBillingForm.name}
                  onChange={(event) => handleUserBillingFormChange('name', event.target.value)}
                  autoComplete="name"
                  disabled={userBillingSaveBusy}
                  required
                />
              </label>
              <label className="admin-field">
                <span>Ország</span>
                <input
                  type="text"
                  value={userBillingForm.country}
                  onChange={(event) => handleUserBillingFormChange('country', event.target.value)}
                  autoComplete="country-name"
                  disabled={userBillingSaveBusy}
                  required
                />
              </label>
              <label className="admin-field">
                <span>Irányítószám</span>
                <input
                  type="text"
                  value={userBillingForm.zip}
                  onChange={(event) => handleUserBillingFormChange('zip', event.target.value)}
                  autoComplete="postal-code"
                  disabled={userBillingSaveBusy}
                  required
                />
              </label>
              <label className="admin-field">
                <span>Város</span>
                <input
                  type="text"
                  value={userBillingForm.city}
                  onChange={(event) => handleUserBillingFormChange('city', event.target.value)}
                  autoComplete="address-level2"
                  disabled={userBillingSaveBusy}
                  required
                />
              </label>
              <label className="admin-field admin-user-billing-wide">
                <span>Utca, házszám</span>
                <input
                  type="text"
                  value={userBillingForm.street}
                  onChange={(event) => handleUserBillingFormChange('street', event.target.value)}
                  autoComplete="street-address"
                  disabled={userBillingSaveBusy}
                  required
                />
              </label>
            </div>
            {userBillingSaveNotice && (
              <p className="registration-notice error">{userBillingSaveNotice}</p>
            )}
            <div className="admin-confirm-actions">
              <button
                type="button"
                className="button-soft"
                onClick={() => setUserBillingEdit(null)}
                disabled={userBillingSaveBusy}
              >
                Mégsem
              </button>
              <button
                type="button"
                className="button-primary"
                onClick={handleSaveUserBillingData}
                disabled={userBillingSaveBusy}
              >
                <Save size={16} />
                {userBillingSaveBusy ? 'Mentés...' : 'Mentés'}
              </button>
            </div>
          </div>
        </div>
      )}

      {userUidPreview && (
        <div
          className="auth-overlay"
          onClick={() => setUserUidPreview(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="admin-user-uid-title"
        >
          <div className="late-cancel-modal card admin-confirm-modal admin-uid-modal" onClick={(event) => event.stopPropagation()}>
            <button
              className="auth-close"
              onClick={() => setUserUidPreview(null)}
              aria-label="Bezárás"
            >
              <X size={18} />
            </button>
            <h2 id="admin-user-uid-title">Felhasználó UID</h2>
            <p>{userUidPreview.displayName || userUidPreview.email || 'Felhasználó'}</p>
            <div className="admin-uid-value">{userUidPreview.uid || userUidPreview.id}</div>
            <div className="admin-confirm-actions single">
              <button className="button-primary" type="button" onClick={() => setUserUidPreview(null)}>
                Bezárás
              </button>
            </div>
          </div>
        </div>
      )}

      {userDeleteConfirm && (
        <div
          className="auth-overlay"
          onClick={() => !userDeleteBusyId && setUserDeleteConfirm(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="admin-delete-user-title"
        >
          <div className="late-cancel-modal card admin-confirm-modal" onClick={(event) => event.stopPropagation()}>
            <button
              className="auth-close"
              onClick={() => setUserDeleteConfirm(null)}
              aria-label="Bezárás"
              disabled={Boolean(userDeleteBusyId)}
            >
              <X size={18} />
            </button>
            <h2 id="admin-delete-user-title">Felhasználó törlése</h2>
            <p>
              Biztosan törlöd ezt a felhasználót? A jövőbeni edzésjelentkezései és várólistás helyei törlődnek,
              a profilhoz kötött egyéb adatai kikerülnek az adatbázisból, de a múltbeli edzésnévsorok és rekordok megmaradnak.
            </p>
            <div className="admin-confirm-training-date">
              {userDeleteConfirm.displayName || userDeleteConfirm.email || userDeleteConfirm.id}
            </div>
            <div className="admin-confirm-actions">
              <button className="button-soft" type="button" onClick={() => setUserDeleteConfirm(null)} disabled={Boolean(userDeleteBusyId)}>
                Mégsem
              </button>
              <button className="button-primary danger" type="button" onClick={handleDeleteUser} disabled={Boolean(userDeleteBusyId)}>
                {userDeleteBusyId ? 'Törlés...' : 'Felhasználó törlése'}
              </button>
            </div>
          </div>
        </div>
      )}

      {trainingCreateOpen && (
        <div className="auth-overlay" onClick={() => !trainingCreateBusy && setTrainingCreateOpen(false)} role="dialog" aria-modal="true">
          <div className="auth-modal card admin-training-create-modal" onClick={(event) => event.stopPropagation()}>
            <button
              className="auth-close"
              onClick={() => setTrainingCreateOpen(false)}
              aria-label="Bezárás"
              disabled={trainingCreateBusy}
            >
              <X size={18} />
            </button>
            <div className="auth-header">
              <h2>Edzés létrehozása</h2>
            </div>
            <div className="admin-form">
              <label className="admin-field">
                <span>Dátum</span>
                <input
                  type="date"
                  value={trainingCreateForm.date}
                  onChange={(event) => handleTrainingCreateFieldChange('date', event.target.value)}
                  disabled={trainingCreateBusy}
                />
              </label>
              <label className="admin-field">
                <span>Időpont</span>
                <input
                  type="time"
                  value={trainingCreateForm.time}
                  onChange={(event) => handleTrainingCreateFieldChange('time', event.target.value)}
                  disabled={trainingCreateBusy}
                />
              </label>
              <label className="admin-field">
                <span>Maximális létszám</span>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={trainingCreateForm.capacity}
                  onChange={(event) => handleTrainingCreateFieldChange('capacity', event.target.value)}
                  disabled={trainingCreateBusy}
                />
              </label>
            </div>
            {trainingCreateNotice && (
              <p className={`registration-notice ${trainingCreateNotice.startsWith('Sikeres') ? 'ok' : 'error'}`}>
                {trainingCreateNotice}
              </p>
            )}
            <div className="admin-confirm-actions">
              <button type="button" className="button-soft" onClick={() => setTrainingCreateOpen(false)} disabled={trainingCreateBusy}>
                Mégsem
              </button>
              <button type="button" className="button-primary" onClick={handleCreateTraining} disabled={trainingCreateBusy}>
                {trainingCreateBusy ? 'Létrehozás...' : 'Létrehozás'}
              </button>
            </div>
          </div>
        </div>
      )}

      {trainingCancelDate && (
        <div
          className="auth-overlay"
          onClick={closeTrainingCancelConfirm}
          role="dialog"
          aria-modal="true"
          aria-labelledby="admin-cancel-training-title"
        >
          <div className="late-cancel-modal card admin-confirm-modal" onClick={(event) => event.stopPropagation()}>
            <button
              className="auth-close"
              onClick={closeTrainingCancelConfirm}
              aria-label="Bezárás"
              disabled={Boolean(trainingCancelBusy)}
            >
              <X size={18} />
            </button>
            <h2 id="admin-cancel-training-title">Edzés lemondása</h2>
            <p>Biztosan lemondod ezt az edzést? A résztvevők értesítést kapnak a szervezői lemondásról.</p>
            <div className="admin-confirm-training-date">{formatTrainingDate(trainingCancelDate)}</div>
            <div className="admin-confirm-actions">
              <button
                type="button"
                className="button-soft"
                onClick={closeTrainingCancelConfirm}
                disabled={Boolean(trainingCancelBusy)}
              >
                Mégsem
              </button>
              <button
                type="button"
                className="button-primary"
                onClick={handleCancelTraining}
                disabled={trainingCancelBusy === trainingCancelDate}
              >
                {trainingCancelBusy === trainingCancelDate ? 'Lemondás...' : 'Lemondás'}
              </button>
            </div>
          </div>
        </div>
      )}

      {trainingDeleteDate && (
        <div
          className="auth-overlay"
          onClick={closeTrainingDeleteConfirm}
          role="dialog"
          aria-modal="true"
          aria-labelledby="admin-delete-training-title"
        >
          <div className="late-cancel-modal card admin-confirm-modal" onClick={(event) => event.stopPropagation()}>
            <button
              className="auth-close"
              onClick={closeTrainingDeleteConfirm}
              aria-label="Bezárás"
              disabled={Boolean(trainingDeleteBusy)}
            >
              <X size={18} />
            </button>
            <h2 id="admin-delete-training-title">Edzés törlése</h2>
            <p>Biztosan teljesen törlöd ezt az edzést? Törlés után eltűnik az admin felületről és a nyilvános oldalról is.</p>
            <div className="admin-confirm-training-date">{formatTrainingDate(trainingDeleteDate)}</div>
            <div className="admin-confirm-actions">
              <button
                type="button"
                className="button-soft"
                onClick={closeTrainingDeleteConfirm}
                disabled={Boolean(trainingDeleteBusy)}
              >
                Mégsem
              </button>
              <button
                type="button"
                className="button-primary"
                onClick={handleDeleteTraining}
                disabled={trainingDeleteBusy === trainingDeleteDate}
              >
                {trainingDeleteBusy === trainingDeleteDate ? 'Törlés...' : 'Törlés'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPage;
